import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import mammoth from 'mammoth';
import {
  AlertTriangle,
  Bot,
  BookOpen,
  ChevronDown,
  Download,
  FileText,
  FolderOpen,
  Info,
  Lock,
  Mail,
  MessageSquareText,
  Rocket,
  Send,
  Settings2,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { STATUS } from './design-tokens/semanticColors.js';
import './App.css';
import { buildUserProfile, fetchWithMicrosoftAuth, getActiveAccount } from './auth.js';
import { readStoredObject } from './storage.js';
import logo from './assets/msr_logo.webp';
import Login from './components/Login';
import MultiMonthSelector from './components/MultiMonthSelector';

// MultiSelectDropdown Component
function MultiSelectDropdown({ options, selectedValues, onChange, placeholder = 'Select options...', selectionLabel = 'selected' }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false); 
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    } else if (selectedValues.length === 1) {
      const selected = options.find(opt => opt.value === selectedValues[0]);
      return selected ? selected.label : '';
    } else {
      return `${selectedValues.length} ${selectionLabel}`;
    }
  };

  return (
    <div className="multi-select-dropdown" ref={dropdownRef}>
      <div 
        className="multi-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="multi-select-display">{getDisplayText()}</span>
        <span className="multi-select-arrow" aria-hidden="true"><ChevronDown size={16} /></span>
      </div>
      
      {isOpen && (
        <div className="multi-select-options">
          {options.length === 0 ? (
            <div className="multi-select-empty">No options available</div>
          ) : (
            options.map(option => (
              <label key={option.value} className="multi-select-option">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                  className="multi-select-checkbox"
                />
                <span className="multi-select-label">{option.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Simple markdown parser function
function parseMarkdown(text) {
  let html = text || '';
  const expandableBlocks = [];
  
  // Escape HTML to prevent XSS attacks
  html = html.replace(/&/g, '&amp;');
  html = html.replace(/</g, '&lt;');
  html = html.replace(/>/g, '&gt;');

  // Stash expandable details blocks before the markdown transforms so the
  // excerpt body stays readable and doesn't get mangled by the simple parser.
  html = html.replace(/\[\[expand:(.*?)\]\]([\s\S]*?)\[\[\/expand\]\]/g, (_, label, body) => {
    const token = `@@EXPANDABLE_${expandableBlocks.length}@@`;
    expandableBlocks.push({
      token,
      label: (label || '').trim() || 'View details',
      body: (body || '').trim()
    });
    return token;
  });
  
  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  
  // Line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br/>');
  
  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
  
  // Inline code
  html = html.replace(/`(.+?)`/g, '<code>$1</code>');
  
  // Lists
  html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
  html = html.replace(/^- (.+)$/gim, '<li>$1</li>');
  html = html.replace(/^(\d+)\. (.+)$/gim, '<li>$2</li>');
  
  // Wrap lists
  html = html.replace(/(<li>.*<\/li>)/s, function(match) {
    return '<ul>' + match + '</ul>';
  });
  
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Restore expandable details blocks after the markdown transforms.
  expandableBlocks.forEach(({ token, label, body }) => {
    const safeBody = body.replace(/\n/g, '<br/>');
    html = html.replace(
      token,
      `<details class="chat-expandable"><summary>${label}</summary><div class="chat-expandable-body">${safeBody}</div></details>`
    );
  });
  
  // Wrap in paragraphs if needed
  if (!html.includes('<p>') && !html.includes('<h') && !html.includes('<ul') && !html.includes('<details')) {
    html = '<p>' + html + '</p>';
  }
  
  return html;
}

const MAX_CHAT_MESSAGES_STORED = 200;
const MAX_SAVED_CHATS = 24;
const MONTH_NAMES_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const CONTRACT_OPTIONS = [
  { value: 'GS00Q14OADS112, Delivery Order FA8622-20-F-8145', label: 'GS00Q14OADS112 / DO FA8622-20-F-8145 (AFIT)' },
  { value: '47QRAD20D1042, Task Order FA8622-22-F-0001', label: '47QRAD20D1042 / TO FA8622-22-F-0001 (AFMC HQ)' },
  { value: 'GS00Q14OADS112, Delivery Order FA8675-20-F-2045', label: 'GS00Q14OADS112 / DO FA8675-20-F-2045 (F-35)' },
  { value: 'FA8501-19-D-0010, Delivery Order FA8501-21-F-0001', label: 'FA8501-19-D-0010 / DO FA8501-21-F-0001 (Robins)' },
  { value: '47QRAD20D3009, Delivery Order FA8721-24-F-B005', label: '47QRAD20D3009 / DO FA8721-24-F-B005 (EPASS Task 74 / C3BM-C3I)' },
];

const CDRL_OPTIONS = [
  { value: 'A001/ Monthly Status Report/DI-MGMT-80368A/T', label: 'A001 / Monthly Status Report / DI-MGMT-80368A/T' },
  { value: 'A002/ Monthly Status Report/DI-MGMT-80368A', label: 'A002 / Monthly Status Report / DI-MGMT-80368A' },
  { value: 'A003/ Program Status Report/DI-MGMT-80227A', label: 'A003 / Program Status Report / DI-MGMT-80227A' },
  { value: 'A001/ Contract Status Report/DI-MGMT-80368B', label: 'A001 / Contract Status Report / DI-MGMT-80368B' },
];

const BASE_OPTIONS = [
  { value: 'Air Force Institute of Technology\n2950 Hobson Way\nWright-Patterson AFB, OH 45433-7765', label: 'AFIT — Wright-Patterson AFB, OH' },
  { value: 'Headquarters Air Force Materiel Command (HQ AFMC)\n4225 Logistics Ave # S205', label: 'HQ AFMC — 4225 Logistics Ave # S205' },
  { value: 'Robins Air Force Base\n215 Page Road\nRobins AFB, GA 31098', label: 'Robins AFB, GA' },
  { value: 'DAF C3BM/C3I, C2ISR Division\n750 Third Street, Bldg 350\nRobins AFB, GA 31098-2122', label: 'DAF C3BM/C3I C2ISR Division — Robins AFB, GA (EPASS Task 74)' },
  { value: 'Tinker Air Force Base\n3001 Staff Drive\nTinker AFB, OK 73145', label: 'Tinker AFB, OK' },
  { value: 'Hill Air Force Base\n7278 4th Street\nHill AFB, UT 84056', label: 'Hill AFB, UT' },
  { value: 'Eglin Air Force Base\n101 W D Avenue\nEglin AFB, FL 32542', label: 'Eglin AFB, FL' },
  { value: 'Hanscom Air Force Base\n20 Schilling Circle\nHanscom AFB, MA 01731', label: 'Hanscom AFB, MA' },
  { value: 'Maxwell Air Force Base\n50 South Turner Boulevard\nMaxwell AFB, AL 36112', label: 'Maxwell AFB, AL' },
  { value: 'Joint Base San Antonio\n2230 Hughes Avenue\nJBSA-Lackland, TX 78236', label: 'JBSA-Lackland, TX' },
  { value: 'Arnold Air Force Base\n100 Kindel Drive\nArnold AFB, TN 37389', label: 'Arnold AFB, TN' },
  { value: 'Edwards Air Force Base\n305 E Popson Avenue\nEdwards AFB, CA 93524', label: 'Edwards AFB, CA' },
  { value: 'F-35 Joint Program Office\n200 12th Street South\nArlington, VA 22202', label: 'F-35 JPO — Arlington, VA' },
];

const PRESET_CUSTOM = '__custom__';

/**
 * Single-control preset picker with a free-text escape hatch.
 *
 * A <datalist> is the wrong tool here: it filters its options against whatever
 * is already in the input, and these fields arrive pre-populated from
 * PROJECT_DEFAULTS — so the list opens showing only the value you already have
 * and reads as broken. A <select> always offers the full list and shows the
 * friendly label rather than the raw stored value.
 *
 * The text field appears only when it is needed: the user picked "Custom", or
 * the incoming value isn't one of the presets (F-16's contract, for instance).
 * In the common case this is one control, not two.
 */
function PresetCombo({ id, ariaLabel, value, onChange, options, placeholder, emptyLabel = '-- Select --', multiline = false, blankHint }) {
  const [forceCustom, setForceCustom] = useState(false);
  const customRef = useRef(null);
  // Tracks the value this component last handed to onChange, so the effect
  // below can tell "the user typed/picked something here" apart from "the form
  // replaced this field from outside".
  const ownValueRef = useRef(value);
  const isPreset = options.some(o => o.value === value);
  const showCustom = forceCustom || (!!value && !isPreset);

  const emit = next => {
    ownValueRef.current = next;
    onChange(next);
  };

  // Snap back to the preset list whenever the value is replaced from outside —
  // confirming or switching a project repopulates every transmittal field, and
  // without this the control stays stuck on "Custom…" showing a project default
  // in the free-text box as though the user had typed it.
  useEffect(() => {
    if (value === ownValueRef.current) return;
    ownValueRef.current = value;
    setForceCustom(false);
  }, [value]);

  // Focus the field when the user picks "Custom…", so they get a cursor and can
  // type straight away. Keyed on forceCustom rather than showCustom on purpose:
  // showCustom is also true when the incoming value simply isn't a preset, and
  // focusing in that case would steal focus and scroll the form on first render.
  useEffect(() => {
    if (forceCustom) {
      customRef.current?.focus();
    }
  }, [forceCustom]);

  return (
    <>
      <select
        id={id}
        aria-label={ariaLabel}
        className="project-select"
        value={showCustom ? PRESET_CUSTOM : value}
        onChange={e => {
          const next = e.target.value;
          if (next === PRESET_CUSTOM) {
            // Clear as well as reveal. Without this the field renders holding
            // the preset the user just switched away from, so "Custom" looks
            // pre-filled with an unrelated option instead of showing its
            // placeholder.
            setForceCustom(true);
            emit('');
            return;
          }
          setForceCustom(false);
          emit(next);
        }}
      >
        <option value="">{emptyLabel}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        <option value={PRESET_CUSTOM}>Custom…</option>
      </select>
      {showCustom && (multiline
        ? <textarea ref={customRef} className="project-select field-textarea" value={value} onChange={e => emit(e.target.value)} placeholder={placeholder} />
        : <input ref={customRef} type="text" className="project-select" value={value} onChange={e => emit(e.target.value)} placeholder={placeholder} />
      )}
      {/* Blank Custom isn't blank output — each field falls back differently.
          Say which fallback applies, and only while the field is actually empty. */}
      {showCustom && !value && blankHint && <p className="field-hint">{blankHint}</p>}
    </>
  );
}

function truncateChatTitle(text, limit = 52) {
  const normalized = (text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'Untitled conversation';
  }
  if (normalized.length <= limit) {
    return normalized;
  }
  return `${normalized.slice(0, limit - 1).trimEnd()}…`;
}

function deriveChatTitle(messages) {
  const firstUserMessage = (messages || []).find(message => message?.sender === 'user' && message?.text);
  if (firstUserMessage?.text) {
    return truncateChatTitle(firstUserMessage.text);
  }
  const firstMessage = (messages || []).find(message => message?.text);
  return truncateChatTitle(firstMessage?.text || 'Untitled conversation');
}

function formatChatPeriodLabel(months = [], fallbackYear = '') {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const normalized = (months || [])
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .map(value => {
      if (value.includes('-')) {
        const [yearPart, monthPart] = value.split('-');
        return { year: yearPart, month: monthPart.padStart(2, '0') };
      }
      return { year: fallbackYear, month: value.padStart(2, '0') };
    })
    .filter(item => item.year && item.month);

  if (!normalized.length) {
    return fallbackYear ? `MSR ${fallbackYear}` : '';
  }

  const unique = normalized.filter((item, index, array) => (
    array.findIndex(other => other.year === item.year && other.month === item.month) === index
  ));

  if (unique.length === 1) {
    const item = unique[0];
    return `${monthNames[Math.max(0, parseInt(item.month, 10) - 1)]} ${item.year}`;
  }

  const sameYear = unique.every(item => item.year === unique[0].year);
  if (sameYear) {
    const labels = unique.map(item => monthNames[Math.max(0, parseInt(item.month, 10) - 1)]);
    return `${labels.join(', ')} ${unique[0].year}`;
  }

  return unique
    .map(item => `${monthNames[Math.max(0, parseInt(item.month, 10) - 1)]} ${item.year}`)
    .join(', ');
}

function formatSavedPeriodLabel(period) {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const value = String(period || '').trim();
  if (!value.includes('-')) return value;
  const [yearPart, monthPart] = value.split('-');
  const monthIndex = Number.parseInt(monthPart, 10) - 1;
  if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex >= monthNames.length) {
    return value;
  }
  return `${monthNames[monthIndex]} ${yearPart}`;
}

function normalizeStoredChats(...storageCandidates) {
  for (const candidate of storageCandidates) {
    const storageKey = typeof candidate === 'string' ? candidate : candidate?.key;
    const fallbackYear = typeof candidate === 'string' ? '' : (candidate?.fallbackYear || '');
    if (!storageKey) {
      continue;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        continue;
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        continue;
      }

      const looksLikeLegacyMessages = parsed.every(item => item && typeof item === 'object' && 'sender' in item && 'text' in item);
      if (looksLikeLegacyMessages) {
        const legacyMessages = parsed.slice(-MAX_CHAT_MESSAGES_STORED);
        return [{
          id: `legacy_${Date.now()}`,
          title: deriveChatTitle(legacyMessages),
          updatedAt: legacyMessages[legacyMessages.length - 1]?.timestamp || new Date().toISOString(),
          months: [],
          periodLabel: formatChatPeriodLabel([], fallbackYear),
          messages: legacyMessages,
        }];
      }

      return parsed
        .filter(item => item && typeof item === 'object' && Array.isArray(item.messages))
        .slice(0, MAX_SAVED_CHATS)
        .map(item => ({
          id: item.id || `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          title: truncateChatTitle(item.title || deriveChatTitle(item.messages)),
          updatedAt: item.updatedAt || item.messages[item.messages.length - 1]?.timestamp || new Date().toISOString(),
          months: Array.isArray(item.months) ? item.months : [],
          periodLabel: item.periodLabel || formatChatPeriodLabel(item.months || [], fallbackYear),
          scopeMode: item.scopeMode || 'current',
          selectedProjects: Array.isArray(item.selectedProjects) ? item.selectedProjects : [],
          selectedScopePeriods: Array.isArray(item.selectedScopePeriods) ? item.selectedScopePeriods : [],
          useAllSavedPeriods: item.useAllSavedPeriods !== false,
          messages: (item.messages || []).slice(-MAX_CHAT_MESSAGES_STORED),
        }));
    } catch {
      // Try the next candidate key.
    }
  }
  return [];
}

function formatConversationOption(chat) {
  const label = truncateChatTitle(chat?.title || 'Untitled conversation', 38);
  const periodLabel = truncateChatTitle(chat?.periodLabel || '', 28);
  const updated = chat?.updatedAt ? new Date(chat.updatedAt) : null;
  if (!updated || Number.isNaN(updated.getTime())) {
    return periodLabel ? `${periodLabel} — ${label}` : label;
  }
  const prefix = periodLabel ? `${periodLabel} — ` : '';
  return `${prefix}${label} • ${updated.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${updated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

// Chatbot Component
function Chatbot({ apiBaseUrl, chatUrl, resyncUrl, syncStatusUrl, authenticatedFetch, projectInfo }) {
  const _chatStorageKey = `msr_chat_history_${projectInfo?.projectName || 'default'}_${projectInfo?.year || 'default'}`;
  const _legacyChatStorageKey = `msr_chat_history_${projectInfo?.projectName || 'default'}`;
  // Persist chat filter state (months, scope, projects) across tab switches
  // so navigating chat → knowledge → chat doesn't wipe the user's selection.
  const _chatFilterStorageKey = `msr_chat_filter_${projectInfo?.projectName || 'default'}_${projectInfo?.year || 'default'}`;
  const _initialFilter = readStoredObject(() => localStorage, _chatFilterStorageKey);
  const [savedChats, setSavedChats] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState('new');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [, setSyncStatistics] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(
    Array.isArray(_initialFilter?.selectedMonths) ? _initialFilter.selectedMonths : (projectInfo.months || [])
  );
  const [scopeMode, setScopeMode] = useState(_initialFilter?.scopeMode || 'current');
  const [selectedProjects, setSelectedProjects] = useState(
    Array.isArray(_initialFilter?.selectedProjects) && _initialFilter.selectedProjects.length > 0
      ? _initialFilter.selectedProjects
      : (projectInfo.projectName ? [projectInfo.projectName] : [])
  );
  const [useAllSavedPeriods, setUseAllSavedPeriods] = useState(
    typeof _initialFilter?.useAllSavedPeriods === 'boolean' ? _initialFilter.useAllSavedPeriods : true
  );
  const [selectedScopePeriods, setSelectedScopePeriods] = useState(
    Array.isArray(_initialFilter?.selectedScopePeriods) ? _initialFilter.selectedScopePeriods : []
  );
  const [scopePeriodOptions, setScopePeriodOptions] = useState([]);
  const [scopeOptionsLoading, setScopeOptionsLoading] = useState(false);
  const [, setHasInitiatedSync] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const isFirstLoad = useRef(true);
  const syncPollingInterval = useRef(null);
  const hasInitiatedSyncRef = useRef(false);
  const isSyncingRef = useRef(false);
  const activeConversationIdRef = useRef('new');
  const projectInfoRef = useRef(projectInfo);
  const scopeModeRef = useRef(scopeMode);
  const parentMonthsKey = JSON.stringify(projectInfo.months || []);
  const projectOptionsKey = JSON.stringify(projectInfo.projectOptions || []);
  const allProjectOptions = useMemo(() => JSON.parse(projectOptionsKey), [projectOptionsKey]);
  const effectiveScopedProjects = useMemo(() => (scopeMode === 'all'
    ? allProjectOptions
    : scopeMode === 'selected'
      ? selectedProjects.filter(Boolean)
      : [projectInfo.projectName].filter(Boolean)), [
    allProjectOptions,
    projectInfo.projectName,
    scopeMode,
    selectedProjects,
  ]);
  const apiPost = useCallback(async (endpoint, data) => {
    try {
      const response = await authenticatedFetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      if (responseData.statusCode && responseData.body) {
        const bodyData = JSON.parse(responseData.body);
        if (responseData.statusCode >= 400) {
          throw new Error(bodyData.error || bodyData.message || 'Request failed');
        }
        return bodyData;
      }

      return responseData;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }, [apiBaseUrl, authenticatedFetch]);
  const effectiveProjectPeriods = scopeMode === 'current'
    ? {
        [projectInfo.projectName]: (selectedMonths || []).map(value => (
          String(value || '').includes('-') ? String(value) : `${projectInfo.year}-${String(value || '').padStart(2, '0')}`
        )),
      }
    : selectedScopePeriods.reduce((accumulator, entry) => {
        const [projectName, period] = String(entry || '').split('::');
        if (!projectName || !period) return accumulator;
        if (!accumulator[projectName]) accumulator[projectName] = [];
        if (!accumulator[projectName].includes(period)) accumulator[projectName].push(period);
        return accumulator;
      }, {});
  const chatScopeLabel = scopeMode === 'all'
    ? (useAllSavedPeriods ? 'All projects · all saved data' : `All projects · ${selectedScopePeriods.length} selected period${selectedScopePeriods.length === 1 ? '' : 's'}`)
    : scopeMode === 'selected'
      ? (useAllSavedPeriods
          ? `${effectiveScopedProjects.length} selected project${effectiveScopedProjects.length === 1 ? '' : 's'} · all saved periods`
          : `${effectiveScopedProjects.length} selected project${effectiveScopedProjects.length === 1 ? '' : 's'} · ${selectedScopePeriods.length} selected period${selectedScopePeriods.length === 1 ? '' : 's'}`)
      : `${projectInfo.projectName} · ${formatChatPeriodLabel(selectedMonths, projectInfo?.year || '') || 'No months selected'}`;
  // Compact label for the chat-header subtitle. The full chatScopeLabel
  // gets too long ("2 selected projects · all saved periods") and ellipsizes
  // mid-word in the header — show just the project count or name here and
  // leave the full label on the Scope button + tooltip.
  const chatScopeShortLabel = scopeMode === 'all'
    ? 'all projects'
    : scopeMode === 'selected'
      ? `${effectiveScopedProjects.length} project${effectiveScopedProjects.length === 1 ? '' : 's'}`
      : projectInfo.projectName || 'no project';

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    projectInfoRef.current = projectInfo;
  }, [projectInfo]);

  useEffect(() => {
    scopeModeRef.current = scopeMode;
  }, [scopeMode]);

  useEffect(() => {
    const currentProjectInfo = projectInfoRef.current;
    const loadedChats = normalizeStoredChats(
      { key: _chatStorageKey, fallbackYear: currentProjectInfo?.year || '' },
      { key: _legacyChatStorageKey, fallbackYear: currentProjectInfo?.year || '' },
    );
    setSavedChats(loadedChats);
    setActiveConversationId('new');
    setMessages([]);
    setInputMessage('');
    // Restore any previously-persisted chat filter (survives tab switches)
    // so chat → knowledge → chat doesn't clear the month selection.
    const saved = readStoredObject(() => localStorage, _chatFilterStorageKey);
    if (saved) {
      if (Array.isArray(saved.selectedMonths)) setSelectedMonths(saved.selectedMonths);
      if (saved.scopeMode) setScopeMode(saved.scopeMode);
      if (Array.isArray(saved.selectedProjects)) setSelectedProjects(saved.selectedProjects);
      if (typeof saved.useAllSavedPeriods === 'boolean') setUseAllSavedPeriods(saved.useAllSavedPeriods);
      if (Array.isArray(saved.selectedScopePeriods)) setSelectedScopePeriods(saved.selectedScopePeriods);
    } else {
      setSelectedMonths(currentProjectInfo?.months || []);
      setScopeMode('current');
      setSelectedProjects(currentProjectInfo?.projectName ? [currentProjectInfo.projectName] : []);
      setUseAllSavedPeriods(true);
      setSelectedScopePeriods([]);
    }
    setScopePeriodOptions([]);
    isFirstLoad.current = true;
  }, [
    _chatFilterStorageKey,
    _chatStorageKey,
    _legacyChatStorageKey,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(_chatStorageKey, JSON.stringify((savedChats || []).slice(0, MAX_SAVED_CHATS)));
    } catch {
      // Chat history persistence is best-effort.
    }
  }, [savedChats, _chatStorageKey]);

  // Keep chat scope synced with the top-bar project selector when the
  // user is in the default 'current' scope mode. Without this, the chat
  // restores its last-saved months from localStorage and ignores the
  // parent's new month — which led to the bug where switching the top
  // bar to August left the chat scoped to July (still saved from a
  // previous session).
  //
  // We intentionally do NOT clobber 'all-saved' or cross-project scope
  // — those modes are explicit user choices that should survive a top-
  // bar change.
  useEffect(() => {
    if (scopeModeRef.current !== 'current') return;
    const parentMonths = JSON.parse(parentMonthsKey);
    setSelectedMonths(currentMonths => {
      const same = parentMonths.length === currentMonths.length
        && parentMonths.every((month, index) => month === currentMonths[index]);
      return same ? currentMonths : parentMonths;
    });
  }, [parentMonthsKey]);

  useEffect(() => {
    try {
      localStorage.setItem(_chatFilterStorageKey, JSON.stringify({
        selectedMonths, scopeMode, selectedProjects, useAllSavedPeriods, selectedScopePeriods,
      }));
    } catch {
      // Chat filter persistence is best-effort.
    }
  }, [_chatFilterStorageKey, selectedMonths, scopeMode, selectedProjects, useAllSavedPeriods, selectedScopePeriods]);

  useEffect(() => {
    if (!messages.length || !messages.some(message => message?.sender === 'user')) {
      return;
    }
    const trimmedMessages = messages.slice(-MAX_CHAT_MESSAGES_STORED);
    const currentId = activeConversationIdRef.current;
    const nextId = currentId === 'new'
      ? `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      : currentId;
    const updatedAt = trimmedMessages[trimmedMessages.length - 1]?.timestamp || new Date().toISOString();
    const nextRecord = {
      id: nextId,
      title: deriveChatTitle(trimmedMessages),
      updatedAt,
      months: [...selectedMonths],
      periodLabel: scopeMode === 'current'
        ? formatChatPeriodLabel(selectedMonths, projectInfo?.year || '')
        : chatScopeLabel,
      scopeMode,
      selectedProjects: [...selectedProjects],
      selectedScopePeriods: [...selectedScopePeriods],
      useAllSavedPeriods,
      messages: trimmedMessages,
    };
    setSavedChats(prev => [
      nextRecord,
      ...prev.filter(chat => chat.id !== nextId),
    ].slice(0, MAX_SAVED_CHATS));
    if (currentId === 'new') {
      setActiveConversationId(nextId);
    }
  }, [messages, projectInfo?.year, selectedMonths, scopeMode, selectedProjects, selectedScopePeriods, useAllSavedPeriods, chatScopeLabel]);

  useEffect(() => {
    if (scopeMode === 'current') {
      setScopePeriodOptions([]);
      setSelectedScopePeriods([]);
      return;
    }

    if (!effectiveScopedProjects.length) {
      setScopePeriodOptions([]);
      setSelectedScopePeriods([]);
      return;
    }

    let isActive = true;
    const loadScopePeriods = async () => {
      setScopeOptionsLoading(true);
      try {
        const responses = await Promise.all(
          effectiveScopedProjects.map(async (projectName) => {
            const result = await apiPost('/kb-status', { projectName, action: 'status' });
            return { projectName, result };
          })
        );
        if (!isActive) return;
        const nextOptions = responses.flatMap(({ projectName, result }) => (
          (result?.months || []).map(periodMeta => ({
            value: `${projectName}::${periodMeta.period}`,
            label: `${projectName} · ${formatSavedPeriodLabel(periodMeta.period)}`,
            projectName,
            period: periodMeta.period,
            entryCount: periodMeta.entryCount || 0,
          }))
        )).sort((left, right) => right.label.localeCompare(left.label));
        setScopePeriodOptions(nextOptions);
        if (useAllSavedPeriods) {
          setSelectedScopePeriods([]);
        } else {
          setSelectedScopePeriods(currentValues => currentValues.filter(value => nextOptions.some(option => option.value === value)));
        }
      } catch (error) {
        console.error('Failed to load cross-project period options:', error);
        if (isActive) {
          setScopePeriodOptions([]);
          setSelectedScopePeriods([]);
        }
      } finally {
        if (isActive) {
          setScopeOptionsLoading(false);
        }
      }
    };

    loadScopePeriods();
    return () => {
      isActive = false;
    };
  }, [apiPost, effectiveScopedProjects, scopeMode, useAllSavedPeriods]);

  const scrollToBottom = () => {
    if (!isFirstLoad.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
      isFirstLoad.current = false;
    }
  }, [messages]);

  // Check sync status on component mount and when project changes
  useEffect(() => {
    // Cleanup polling on unmount
    return () => {
      if (syncPollingInterval.current) {
        clearInterval(syncPollingInterval.current);
      }
    };
  }, [projectInfo.projectName, projectInfo.year]);

  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
      chatInputRef.current.style.height = Math.min(chatInputRef.current.scrollHeight, 100) + 'px';
    }
  }, [inputMessage]);

  const appendChatMessage = (message) => {
    setMessages(prev => [...prev, message]);
  };

  const openConversation = (conversationId) => {
    if (conversationId === 'new') {
      setActiveConversationId('new');
      setMessages([]);
      setInputMessage('');
      setSelectedMonths(projectInfo.months || []);
      setScopeMode('current');
      setSelectedProjects(projectInfo?.projectName ? [projectInfo.projectName] : []);
      setUseAllSavedPeriods(true);
      setSelectedScopePeriods([]);
      isFirstLoad.current = true;
      return;
    }
    const selectedChat = savedChats.find(chat => chat.id === conversationId);
    setActiveConversationId(conversationId);
    setMessages(selectedChat?.messages || []);
    setSelectedMonths(Array.isArray(selectedChat?.months) && selectedChat.months.length > 0 ? selectedChat.months : (projectInfo.months || []));
    setScopeMode(selectedChat?.scopeMode || 'current');
    setSelectedProjects(selectedChat?.selectedProjects?.length ? selectedChat.selectedProjects : (projectInfo?.projectName ? [projectInfo.projectName] : []));
    setUseAllSavedPeriods(selectedChat?.useAllSavedPeriods !== false);
    setSelectedScopePeriods(Array.isArray(selectedChat?.selectedScopePeriods) ? selectedChat.selectedScopePeriods : []);
    setInputMessage('');
    isFirstLoad.current = true;
  };

  const handleDeleteConversation = () => {
    if (activeConversationId === 'new') {
      setMessages([]);
      return;
    }
    setSavedChats(prev => prev.filter(chat => chat.id !== activeConversationId));
    setActiveConversationId('new');
    setMessages([]);
    setInputMessage('');
    isFirstLoad.current = true;
  };

  // Update your checkSyncStatus function in the Chatbot component:

  const checkSyncStatus = async () => {
    if (!syncStatusUrl) {
      console.log('Sync status URL not configured');
      return;
    }

    try {
      const response = await authenticatedFetch(syncStatusUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectName: projectInfo.projectName,
          year: projectInfo.year
        })
      });
      
      const responseData = await response.json();
      console.log('Raw sync status response:', responseData);
      
      const bodyData = responseData;
      
      if (bodyData.error) {
        console.error('Failed to check sync status:', bodyData.error);
        return;
      }
      
      const previousStatus = syncStatus;
      setSyncStatus(bodyData.status);
      setSyncStatistics(bodyData.statistics);
      
      // FIXED: Only show completion message if we initiated a sync in this session
      // AND we're transitioning from a non-complete state to complete
      console.log("Checking the status stuff")
      console.log("hasInitiatedSyncref:", hasInitiatedSyncRef.current)
      console.log("previousStatus:", previousStatus) 
      console.log("bodyData.status:", bodyData.status)
      console.log("is syncing: ", isSyncing)
      console.log("is syncing ref: ", isSyncingRef.current)

      // Show completion message only when:
      // 1. We have initiated a sync in this session, AND
      // 2. We're transitioning to COMPLETE/FAILED from a different status
      if (hasInitiatedSyncRef.current && !isSyncing && (bodyData.status === 'COMPLETE' || bodyData.status === 'FAILED')) {
        if (bodyData.status === 'COMPLETE') {
          console.log("Bot is printing completion message");
          const completionMessage = {
            id: Date.now(),
            text: '✅ Knowledge base sync completed successfully! The chatbot now has access to the latest documents.',
            sender: 'bot',
            timestamp: new Date().toISOString()
          };
          appendChatMessage(completionMessage);
        } else if (bodyData.status === 'FAILED') {
          const failureMessage = {
            id: Date.now(),
            text: '❌ Knowledge base sync failed. Please try again or check the logs.',
            sender: 'bot',
            timestamp: new Date().toISOString()
          };
          appendChatMessage(failureMessage);
        }
      }
      
      // If sync is in progress, start polling
      if (bodyData.status === 'STARTING' || bodyData.status === 'IN_PROGRESS') {
        startSyncPolling();
      } else {
        stopSyncPolling();
        // Reset syncing state when complete
        if (bodyData.status === 'COMPLETE' || bodyData.status === 'FAILED' || bodyData.status === 'STOPPED') {
          setIsSyncing(false);
          isSyncingRef.current = false; 
        }
      }
      
      return bodyData;
      
    } catch (error) {
      console.error('Failed to check sync status:', error);
    }
  };

  const startSyncPolling = () => {
    // Don't start if already polling
    if (syncPollingInterval.current) return;
    
    syncPollingInterval.current = setInterval(() => {
      checkSyncStatus();
    }, 5000); // Poll every 5 seconds
  };

  const stopSyncPolling = () => {
    if (syncPollingInterval.current) {
      clearInterval(syncPollingInterval.current);
      syncPollingInterval.current = null;
      isSyncingRef.current = false;
    }
  };

  const resyncPost = async (data) => {
    try {
      const response = await authenticatedFetch(resyncUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      const responseData = await response.json();
      
      if (responseData.statusCode && responseData.body) {
        const bodyData = JSON.parse(responseData.body);
        
        if (responseData.statusCode >= 400) {
          throw new Error(bodyData.error || bodyData.message || 'Request failed');
        }
        
        return bodyData;
      }
      
      return responseData;
      
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  const _handleResync = async () => {
    setIsSyncing(true);
    setHasInitiatedSync(true);
    hasInitiatedSyncRef.current = true; 
    isSyncingRef.current = true; 
    console.log("clicked resync, hasinitiated and issyncing should be true. hasinitiated: ", hasInitiatedSyncRef.current, ' issyncing: ', isSyncingRef.current);
    setSyncStatus('STARTING');
    try {
      await resyncPost({
        projectName: projectInfo.projectName,
        year: projectInfo.year,
        months: selectedMonths
      });
      
      // Start polling for status updates
      startSyncPolling();
      const syncMessage = {
        id: Date.now(),
        text: '🔄 Knowledge base sync has been initiated. The sync will continue in the background.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      appendChatMessage(syncMessage);
    } catch (error) {
      console.error('Resync error:', error);
      const errorMessage = {
        id: Date.now(),
        text: '❌ Failed to start knowledge base sync. Please try again.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      appendChatMessage(errorMessage);
      setSyncStatus(null);
    }
  };

  const handleMonthFilterConfirm = async (months) => {
    // Just show confirmation message when filter is applied
    if (months.length === 0) {
      return;
    }

    const confirmationMessage = {
      id: Date.now(),
      text: `✅ Filter applied for ${months.length} month${months.length > 1 ? 's' : ''}`,
      sender: 'system',
      timestamp: new Date().toISOString()
    };
    
    appendChatMessage(confirmationMessage);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    if (inputMessage.length > 4000) {
      appendChatMessage({
        id: Date.now(),
        text: `Your message is too long (${inputMessage.length} characters). Please keep it under 4,000 characters.`,
        sender: 'bot',
        timestamp: new Date().toISOString()
      });
      return;
    }
    if (scopeMode !== 'current' && effectiveScopedProjects.length === 0) {
      appendChatMessage({
        id: Date.now(),
        text: 'Select at least one project, or switch to All Data, before asking a cross-project question.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      });
      return;
    }
    if (scopeMode !== 'current' && !useAllSavedPeriods && selectedScopePeriods.length === 0) {
      appendChatMessage({
        id: Date.now(),
        text: 'Choose one or more saved periods, or turn on "Use all saved periods," before asking a cross-project question.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      });
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    appendChatMessage(userMessage);
    setInputMessage('');
    setIsTyping(true);

    try {
      const currentProjectPeriods = (selectedMonths || []).map(month => (
        String(month || '').includes('-') ? String(month) : `${projectInfo.year}-${String(month || '').padStart(2, '0')}`
      ));
      const chatContext = {
        projectName: projectInfo.projectName,
        year: projectInfo.year,
        months: selectedMonths,
        scopeMode,
        projects: effectiveScopedProjects,
        allProjects: scopeMode === 'all',
        allPeriods: scopeMode === 'current' ? false : useAllSavedPeriods,
        projectPeriods: scopeMode === 'current'
          ? { [projectInfo.projectName]: currentProjectPeriods }
          : effectiveProjectPeriods,
      };
      
      // Use agent URL directly
      const response = await authenticatedFetch(chatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          context: chatContext,
          sessionId: activeConversationIdRef.current === 'new' ? undefined : activeConversationIdRef.current,
          // Send the prior turns so the agent has continuity. The backend
          // accepts either the frontend {sender,text} format or the
          // Anthropic {role,content} format. We send the last 24 messages
          // (~12 user/assistant pairs) — chat_agent.HISTORY_TURN_CAP caps
          // the actual amount used.
          history: messages
            .filter(m => m && m.text && (m.sender === 'user' || m.sender === 'bot'))
            .slice(-24)
            .map(m => ({
              role: m.sender === 'user' ? 'user' : 'assistant',
              content: m.text,
            })),
        })
      });

      let responseData = await response.json();
      // Lambda Function URLs may wrap body as a JSON string
      if (responseData.body && typeof responseData.body === 'string') {
        try { responseData = JSON.parse(responseData.body); } catch { /* use as-is */ }
      }

      const botResponse = {
        id: Date.now() + 1,
        text: responseData.message || responseData.answer || responseData.response || 'No response received',
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      
      appendChatMessage(botResponse);
      
    } catch (error) {
      console.error('Chat error:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        text: error.message?.includes('Failed to fetch')
          ? 'Chat service is unavailable. Please check your connection and try again.'
          : error.message?.includes('not valid JSON')
          ? 'The server returned an unexpected response. Please try again.'
          : `Something went wrong: ${error.message || 'unknown error'}. Please try again.`,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      
      appendChatMessage(errorMessage);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const _handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    chatInputRef.current?.focus();
  };

  const getMonthsDisplay = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (selectedMonths.length === 0) {
      return 'No months selected';
    } else if (selectedMonths.length === 1) {
      const month = selectedMonths[0];
      // Check if month includes year (format: "2025-07")
      if (month.includes('-')) {
        const [yearPart, monthPart] = month.split('-');
        return `${monthNames[parseInt(monthPart) - 1]} ${yearPart}`;
      } else {
        return monthNames[parseInt(month) - 1];
      }
    } else {
      // Check if we have multi-year data
      const hasYears = selectedMonths.some(m => m.includes('-'));
      if (hasYears) {
        // Group by year to show summary
        const yearGroups = {};
        selectedMonths.forEach(month => {
          if (month.includes('-')) {
            const [yearPart] = month.split('-');
            yearGroups[yearPart] = (yearGroups[yearPart] || 0) + 1;
          }
        });
        const years = Object.keys(yearGroups);
        if (years.length === 1) {
          return `${selectedMonths.length} months from ${years[0]}`;
        } else {
          return `${selectedMonths.length} months from ${years.length} years`;
        }
      } else {
        return `${selectedMonths.length} months`;
      }
    }
  };

  return (
    <div className="chat-page">
      <div className="chat-toolbar">
        <div className="toolbar-right" style={{marginLeft:'auto', display:'flex', gap:'0.55rem', alignItems:'flex-end', flexWrap:'wrap', justifyContent:'flex-end'}}>
          <div style={{display:'flex', flexDirection:'column', gap:'0.18rem', minWidth:'210px'}}>
            <label className="month-selector-label">Scope</label>
            <div style={{display:'flex', gap:'0.3rem', flexWrap:'wrap'}}>
              {[
                { value: 'current', label: 'Current Project' },
                { value: 'selected', label: 'Selected Projects' },
                { value: 'all', label: 'All Data' },
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setScopeMode(option.value)}
                  className={`btn btn-action ${scopeMode === option.value ? 'btn-primary' : 'btn-secondary'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {scopeMode === 'current' ? (
            <div style={{display:'flex', flexDirection:'column', gap:'0.18rem'}}>
              <label className="month-selector-label">Filter by months:</label>
              <MultiMonthSelector
                selectedMonths={selectedMonths}
                onMonthsChange={setSelectedMonths}
                year={projectInfo.year}
                availableMonths={projectInfo.availableMonths}
                onConfirm={handleMonthFilterConfirm}
              />
            </div>
          ) : (
            <>
              {scopeMode === 'selected' && (
                <div style={{display:'flex', flexDirection:'column', gap:'0.18rem', minWidth:'240px'}}>
                  <label className="month-selector-label">Projects</label>
                  <MultiSelectDropdown
                    options={(projectInfo.projectOptions || []).map(projectName => ({ value: projectName, label: projectName }))}
                    selectedValues={selectedProjects}
                    onChange={setSelectedProjects}
                    placeholder="Select projects..."
                    selectionLabel="projects selected"
                  />
                </div>
              )}
              <div style={{display:'flex', flexDirection:'column', gap:'0.3rem', minWidth:'280px'}}>
                <label className="month-selector-label">Saved periods</label>
                <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap'}}>
                  <label style={{display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', color:STATUS.neutral.text}}>
                    <input
                      type="checkbox"
                      checked={useAllSavedPeriods}
                      onChange={(event) => setUseAllSavedPeriods(event.target.checked)}
                    />
                    Use all saved periods
                  </label>
                  {scopeOptionsLoading && <span style={{fontSize:'0.76rem', color:STATUS.neutral.text}}>Loading periods...</span>}
                </div>
                {!useAllSavedPeriods && (
                  <MultiSelectDropdown
                    options={scopePeriodOptions}
                    selectedValues={selectedScopePeriods}
                    onChange={setSelectedScopePeriods}
                    placeholder="Select saved periods..."
                    selectionLabel="periods selected"
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="chat-container">
        <div className="chat-header">
          <div className="chat-header-icon" aria-hidden="true"><Bot size={22} /></div>
          <div className="chat-header-text">
            <h2>MSR Assistant</h2>
            <p title={chatScopeLabel}>
              {scopeMode === 'current'
                ? `Ask questions about ${projectInfo.projectName} — ${getMonthsDisplay()}`
                : `Ask questions across ${chatScopeShortLabel}`}
            </p>
          </div>
          <div className="chat-header-actions">
            <div className="chat-history-controls">
              <label htmlFor="chat-history-select" className="chat-history-label">Conversation</label>
              <select
                id="chat-history-select"
                className="chat-history-select"
                value={activeConversationId}
                onChange={(event) => openConversation(event.target.value)}
              >
                <option value="new">New chat</option>
                {savedChats.map(chat => (
                  <option key={chat.id} value={chat.id}>
                    {formatConversationOption(chat)}
                  </option>
                ))}
              </select>
            </div>
            {activeConversationId !== 'new' && (
              <button
                className="btn btn-secondary btn-action"
                onClick={handleDeleteConversation}
                title="Delete this saved conversation"
              >
                Delete Chat
              </button>
            )}
            <div
              className="chat-scope-badge"
              title="This shows the current chat scope for the conversation you are about to ask."
            >
              <span className="chat-scope-badge-label">SCOPE</span>
              <span>{scopeMode === 'current' ? projectInfo.projectName : chatScopeLabel}</span>
            </div>
          </div>
        </div>
        
        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="chat-empty">
              <div className="chat-empty-icon" aria-hidden="true"><Bot size={26} /></div>
              <p className="chat-empty-title">Welcome to MSR Assistant</p>
              <p className="chat-empty-subtitle">
                {scopeMode === 'current'
                  ? `I'm ready to help with ${projectInfo.projectName} documents`
                  : `I'm ready to help across ${chatScopeLabel}`}
              </p>
              {savedChats.length > 0 && (
                <p className="chat-history-hint">
                  Start a new chat, or open one of your {savedChats.length} saved conversation{savedChats.length === 1 ? '' : 's'} from the picker above.
                </p>
              )}
              {/* <div className="chat-suggestions">
                <p>Try asking:</p>
                <button 
                  className="suggestion-chip" 
                  onClick={() => handleSuggestionClick("What's the summary of the latest status report?")}
                >
                  What's the summary of the latest status report?
                </button>
                <button 
                  className="suggestion-chip" 
                  onClick={() => handleSuggestionClick("What are the key accomplishments this month?")}
                >
                  What are the key accomplishments this month?
                </button>
                <button 
                  className="suggestion-chip" 
                  onClick={() => handleSuggestionClick("Are there any risks or issues reported?")}
                >
                  Are there any risks or issues reported?
                </button>
              </div> */}
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className={`message ${message.sender}`}>
                  <div className="message-avatar">
                    {message.sender === 'user' ? <UserRound size={16} /> : message.sender === 'system' ? <ShieldCheck size={16} /> : <Bot size={16} />}
                  </div>
                  <div className="message-content">
                    <div className="message-bubble">
                      {message.sender === 'bot' ? (
                        <div 
                          className="message-text markdown-content"
                          dangerouslySetInnerHTML={{ __html: parseMarkdown(message.text) }}
                        />
                      ) : (
                        <p className="message-text">{message.text}</p>
                      )}
                    </div>
                    <p className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="message bot">
                  <div className="message-avatar"><Bot size={16} /></div>
                  <div className="message-content">
                    <div className="message-bubble">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        
        <div className="chat-input-container">
          <textarea
            ref={chatInputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your documents..."
            className="chat-input"
            rows="1"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            className="btn btn-send"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function getValidationReadiness(validationResult) {
  const summary = validationResult?.summary || {};
  const missing = Number(summary.missing || 0);
  const duplicates = Number(summary.duplicates || 0);
  const parseErrors = Number(summary.parseErrors || 0);

  if (validationResult?.blocking) {
    return {
      label: 'Blocked',
      tone: {
        background: STATUS.danger.bg,
        border: STATUS.danger.border,
        text: STATUS.danger.text,
      },
      message: validationResult.blockingReason || 'Resolve duplicate-content conflicts or override them before generating.',
    };
  }

  if (parseErrors > 0) {
    return {
      label: 'Needs Attention',
      tone: {
        background: STATUS.warning.bg,
        border: STATUS.warning.border,
        text: STATUS.warning.text,
      },
      message: 'One or more files could not be parsed. Replace those files and validate again before generating.',
    };
  }

  if (missing > 0 || duplicates > 0) {
    return {
      label: 'Review Recommended',
      tone: {
        background: STATUS.warning.bgStrong,
        border: STATUS.warning.border,
        text: STATUS.warning.text,
      },
      message: 'Generation is available, but there are missing positions or possible carry-over submissions worth reviewing.',
    };
  }

  return {
    label: 'Ready',
    tone: {
      background: STATUS.success.bgStrong,
      border: STATUS.success.border,
      text: STATUS.success.text,
    },
    message: 'Template, inputs, and validation checks look clean for generation.',
  };
}

function collectInputConfirmedContributors(validationResult) {
  const byKey = new Map();

  for (const file of validationResult?.perFile || []) {
    const contributors = Array.isArray(file?.contributorsFound) ? file.contributorsFound : [];
    for (const contributor of contributors) {
      const pid = String(contributor?.pid || '').trim();
      const personName = String(contributor?.personName || '').trim();
      const header = String(contributor?.header || '').trim();
      const source = String(file?.filename || '').trim();
      const key = pid || personName || header || source;
      if (!key || byKey.has(key)) continue;
      byKey.set(key, {
        pid,
        personName,
        header,
        wordCount: Number(contributor?.wordCount || 0),
        source,
      });
    }
  }

  return Array.from(byKey.values()).sort((left, right) => {
    const pidCompare = String(left.pid || '').localeCompare(String(right.pid || ''), undefined, { numeric: true });
    if (pidCompare !== 0) return pidCompare;
    const personCompare = String(left.personName || '').localeCompare(String(right.personName || ''));
    if (personCompare !== 0) return personCompare;
    return String(left.source || '').localeCompare(String(right.source || ''));
  });
}

function getValidationPresentation(validationResult) {
  const summary = validationResult?.summary || {};
  const rosterRows = Array.isArray(validationResult?.roster) ? validationResult.roster : [];
  const inputFiles = Array.isArray(summary.inputFiles) ? summary.inputFiles : [];
  const inputConfirmedContributors = collectInputConfirmedContributors(validationResult);
  const isHeadingMode = summary.mode === 'heading';
  const templateHasRoster = rosterRows.length > 0;
  const usesInputConfirmedRoster = isHeadingMode && !templateHasRoster && inputConfirmedContributors.length > 0;
  const inputConfirmedCount = inputConfirmedContributors.length;
  const submittedLabel = usesInputConfirmedRoster ? 'Input documents' : 'Submitted';
  const submittedCount = usesInputConfirmedRoster ? inputFiles.length : Number(summary.submitted || 0);
  const hasExplicitPeople = inputConfirmedContributors.some(entry => entry.personName);

  let guidanceTitle = '';
  let guidanceMessage = '';
  if (usesInputConfirmedRoster) {
    guidanceTitle = 'Template roster not present';
    guidanceMessage = hasExplicitPeople
      ? `This template does not include a contributor roster. ${inputConfirmedCount} contributor${inputConfirmedCount === 1 ? '' : 's'} were confirmed from the uploaded input documents.`
      : `This template does not include a contributor roster. ${submittedCount} uploaded header submission${submittedCount === 1 ? '' : 's'} were confirmed from the input documents.`;
  }

  return {
    templateHasRoster,
    usesInputConfirmedRoster,
    inputConfirmedContributors,
    inputConfirmedCount,
    submittedLabel,
    submittedCount,
    guidanceTitle,
    guidanceMessage,
  };
}

function collectValidationDuplicateEntries(validationResult) {
  const byKey = new Map();

  for (const row of validationResult?.roster || []) {
    if (!row?.duplicate) continue;
    const duplicate = row.duplicate || {};
    const key = [row.pid, duplicate.previousMonth, duplicate.source || row.source].filter(Boolean).join('|');
    if (!key || byKey.has(key)) continue;
    byKey.set(key, {
      pid: row.pid || '',
      personName: row.personName || '',
      source: duplicate.source || row.source || '',
      previousMonth: duplicate.previousMonth || '',
      similarity: duplicate.similarity || '',
      currentPreview: duplicate.currentPreview || '',
      previousPreview: duplicate.previousPreview || '',
    });
  }

  for (const file of validationResult?.perFile || []) {
    for (const duplicate of file?.duplicateMatches || []) {
      const key = [duplicate.pid, duplicate.previousMonth, duplicate.source || file.filename].filter(Boolean).join('|');
      if (!key || byKey.has(key)) continue;
      byKey.set(key, {
        pid: duplicate.pid || '',
        personName: duplicate.personName || '',
        source: duplicate.source || file.filename || '',
        previousMonth: duplicate.previousMonth || '',
        similarity: duplicate.similarity || '',
        currentPreview: duplicate.currentPreview || file.contentPreview || '',
        previousPreview: duplicate.previousPreview || '',
      });
    }
  }

  return Array.from(byKey.values()).sort((left, right) => Number(right.similarity || 0) - Number(left.similarity || 0));
}

function csvCell(value) {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function buildValidationExportPayload({ projectName, year, month, validationResult }) {
  const summary = validationResult?.summary || {};
  const readiness = getValidationReadiness(validationResult);
  const presentation = getValidationPresentation(validationResult);
  const duplicateEntries = collectValidationDuplicateEntries(validationResult);
  const rosterRows = (validationResult?.roster || []).map(row => ({
    pid: row.pid || '',
    personName: row.personName || '',
    header: row.header || '',
    status: row.vacant ? 'Vacant' : row.submitted ? 'Submitted' : 'Missing',
    vacant: Boolean(row.vacant),
    submitted: Boolean(row.submitted),
    source: row.source || '',
    wordCount: row.wordCount || 0,
    duplicateSimilarity: row.duplicate?.similarity || null,
    duplicateComparedTo: row.duplicate?.previousMonth || '',
    contributorSlot: Boolean(row.isContributorSlot),
    styleName: row.styleName || '',
  }));
  const perFileSummary = (validationResult?.perFile || []).map(file => ({
    filename: file.filename || '',
    paragraphCount: file.paragraphCount || 0,
    duplicate: Boolean(file.duplicate),
    duplicateCount: (file.duplicateMatches || []).length,
    duplicatePids: file.duplicatePids || [],
    duplicatePeople: (file.duplicateMatches || []).map(item => item.personName || item.pid || '').filter(Boolean),
    rosterMatch: file.rosterMatch || '',
    extractedNames: file.extractedNames || [],
    headingsFoundCount: (file.headingsFound || []).length,
    pidsFoundCount: (file.pidsFound || []).length,
    pidsFound: file.pidsFound || [],
    contributorsFoundCount: (file.contributorsFound || []).length,
    error: file.error || '',
  }));

  return {
    exportedAt: new Date().toISOString(),
    projectName,
    year,
    month,
    readiness: {
      label: readiness.label,
      message: readiness.message,
      blocking: Boolean(validationResult?.blocking),
      blockingReason: validationResult?.blockingReason || '',
    },
    summary: {
      mode: summary.mode || 'unknown',
      totalPositions: summary.totalPositions || 0,
      submitted: summary.submitted || 0,
      missing: summary.missing || 0,
      vacant: summary.vacant || 0,
      potentialDuplicates: summary.duplicates || 0,
      parseErrors: summary.parseErrors || 0,
      contributorPositions: summary.contributorPositions || 0,
      contributorMissing: summary.contributorMissing || 0,
      templateInternalPositions: summary.templateInternalPositions || 0,
      templateInternalUnfilled: summary.templateInternalUnfilled || 0,
      inputFiles: summary.inputFiles || [],
      templateHasRoster: presentation.templateHasRoster,
      usesInputConfirmedRoster: presentation.usesInputConfirmedRoster,
      inputConfirmedContributors: presentation.inputConfirmedCount,
      submittedLabel: presentation.submittedLabel,
      submittedDisplayCount: presentation.submittedCount,
    },
    missingRoster: rosterRows.filter(row => row.status === 'Missing'),
    potentialDuplicates: duplicateEntries.map(entry => ({
      pid: entry.pid || '',
      personName: entry.personName || '',
      source: entry.source || '',
      previousMonth: entry.previousMonth || '',
      similarity: entry.similarity || 0,
      currentPreview: entry.currentPreview || '',
      previousPreview: entry.previousPreview || '',
    })),
    inputConfirmedContributors: presentation.inputConfirmedContributors.map(row => ({
      pid: row.pid || '',
      personName: row.personName || '',
      header: row.header || '',
      wordCount: row.wordCount || 0,
      source: row.source || '',
    })),
    roster: rosterRows,
    rosterVerification: (validationResult?.rosterStatus || []).map(row => ({
      name: row.name || '',
      status: row.submitted ? 'Submitted' : 'Missing',
      file: row.file || '',
      matchedVia: row.matchedVia || '',
    })),
    perFileSummary,
    parseWarnings: validationResult?.parseWarnings || [],
  };
}

function buildValidationCsv({ projectName, year, month, validationResult }) {
  const exportPayload = buildValidationExportPayload({ projectName, year, month, validationResult });
  const duplicateByPid = new Map(
    (exportPayload.potentialDuplicates || [])
      .filter(row => row.pid)
      .map(row => [row.pid, row])
  );

  const lines = [
    'sep=,',
    [
      'Project',
      'Year',
      'Month',
      'Validation Status',
      'Reason',
      'Record Type',
      'Identifier',
      'Person',
      'Submission Status',
      'Source File',
      'Word Count',
      'Duplicate Similarity',
      'Compared To',
      'Notes',
    ].join(','),
  ];

  if (exportPayload.roster.length > 0) {
    for (const row of exportPayload.roster) {
      const duplicate = duplicateByPid.get(row.pid) || {};
      const notes = [];
      if (row.contributorSlot === false) notes.push('Template internal position');
      if (row.styleName) notes.push(`Style: ${row.styleName}`);
      if (!row.personName && row.header) notes.push(row.header);
      lines.push([
        exportPayload.projectName,
        exportPayload.year,
        exportPayload.month,
        exportPayload.readiness.label,
        exportPayload.readiness.message,
        'Roster Position',
        row.pid || '',
        row.personName || '',
        row.status || '',
        row.source || '',
        row.wordCount || 0,
        duplicate.similarity || row.duplicateSimilarity || '',
        duplicate.previousMonth || row.duplicateComparedTo || '',
        notes.join(' | '),
      ].map(csvCell).join(','));
    }
  } else if (exportPayload.inputConfirmedContributors.length > 0) {
    for (const row of exportPayload.inputConfirmedContributors) {
      const duplicate = duplicateByPid.get(row.pid) || {};
      const notes = [];
      if (row.header && row.header !== row.personName) notes.push(row.header);
      lines.push([
        exportPayload.projectName,
        exportPayload.year,
        exportPayload.month,
        exportPayload.readiness.label,
        exportPayload.readiness.message,
        'Input Confirmed',
        row.pid || '',
        row.personName || '',
        'Confirmed from inputs',
        row.source || '',
        row.wordCount || 0,
        duplicate.similarity || '',
        duplicate.previousMonth || '',
        notes.join(' | '),
      ].map(csvCell).join(','));
    }
  }

  for (const row of exportPayload.rosterVerification || []) {
    lines.push([
      exportPayload.projectName,
      exportPayload.year,
      exportPayload.month,
      exportPayload.readiness.label,
      exportPayload.readiness.message,
      'Roster Verification',
      '',
      row.name || '',
      row.status || '',
      row.file || '',
      '',
      '',
      '',
      row.matchedVia ? `Matched via ${row.matchedVia}` : '',
    ].map(csvCell).join(','));
  }

  for (const warning of exportPayload.parseWarnings || []) {
    lines.push([
      exportPayload.projectName,
      exportPayload.year,
      exportPayload.month,
      exportPayload.readiness.label,
      exportPayload.readiness.message,
      'Warning',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      warning,
    ].map(csvCell).join(','));
  }

  return lines.join('\r\n');
}

function buildValidationSummaryText({ projectName, year, month, validationResult }) {
  const summary = validationResult?.summary || {};
  const readiness = getValidationReadiness(validationResult);
  const presentation = getValidationPresentation(validationResult);
  const rosterRows = Array.isArray(validationResult?.roster) ? validationResult.roster : [];
  const missingRows = rosterRows.filter(row => !row.submitted && !row.vacant);
  const duplicateRows = rosterRows.filter(row => row.duplicate);
  const lines = [
    `${projectName} ${year}-${month} Validation Summary`,
    `Status: ${readiness.label}`,
    `Reason: ${readiness.message}`,
    `Matching mode: ${summary.mode || 'unknown'}`,
    `${presentation.submittedLabel}: ${presentation.submittedCount}`,
    `Missing: ${summary.missing || 0}`,
    `Vacant: ${summary.vacant || 0}`,
    `Potential duplicates: ${summary.duplicates || 0}`,
    `Parse errors: ${summary.parseErrors || 0}`,
    `Contributor positions: ${summary.contributorPositions || 0}`,
    `Template internal positions: ${summary.templateInternalPositions || 0}`,
  ];
  if (presentation.usesInputConfirmedRoster) {
    lines.push('Template roster: Not present');
    lines.push(`Input-confirmed contributors: ${presentation.inputConfirmedCount}`);
    lines.push(`Submitted files: ${(summary.inputFiles || []).length}`);
  }
  if (validationResult?.blockingReason) {
    lines.push(`Blocking reason: ${validationResult.blockingReason}`);
  }
  if (missingRows.length > 0) {
    lines.push('', 'Missing roster positions:');
    for (const row of missingRows) {
      const personLabel = row.personName || row.header || row.pid || 'Unknown position';
      lines.push(`- ${row.pid}: ${personLabel}`);
    }
  }
  if (duplicateRows.length > 0) {
    lines.push('', 'Potential duplicate positions:');
    for (const row of duplicateRows) {
      const duplicate = row.duplicate || {};
      const personLabel = row.personName || row.header || row.pid || 'Unknown position';
      const comparedTo = duplicate.previousMonth ? ` compared with ${duplicate.previousMonth}` : '';
      lines.push(`- ${row.pid}: ${personLabel} (${duplicate.similarity || 0}% match${comparedTo})`);
    }
  }
  if (presentation.inputConfirmedContributors.length > 0) {
    lines.push('', 'Input-confirmed contributors:');
    for (const row of presentation.inputConfirmedContributors) {
      const descriptor = row.personName || row.header || row.source || 'Unknown contributor';
      const detail = [row.pid, row.source].filter(Boolean).join(' | ');
      lines.push(`- ${descriptor}${detail ? ` (${detail})` : ''}`);
    }
  }
  if (rosterRows.length > 0) {
    lines.push('', 'Full roster status:');
    for (const row of rosterRows) {
      const status = row.vacant ? 'Vacant' : row.submitted ? 'Submitted' : 'Missing';
      const descriptor = row.personName || row.header || row.pid || 'Unknown position';
      const details = [];
      if (row.source) details.push(row.source);
      if (row.wordCount) details.push(`${row.wordCount} words`);
      if (row.duplicate?.similarity) details.push(`${row.duplicate.similarity}% duplicate`);
      lines.push(`- ${row.pid}: ${descriptor} | ${status}${details.length ? ` | ${details.join(' | ')}` : ''}`);
    }
  }
  if ((validationResult?.rosterStatus || []).length > 0) {
    lines.push('', 'Expected roster verification:');
    for (const row of validationResult.rosterStatus) {
      const status = row.submitted ? 'Submitted' : 'Missing';
      const details = [row.file, row.matchedVia].filter(Boolean).join(' | ');
      lines.push(`- ${row.name}: ${status}${details ? ` | ${details}` : ''}`);
    }
  }
  if ((validationResult?.parseWarnings || []).length > 0) {
    lines.push('', 'Parse warnings:');
    for (const warning of validationResult.parseWarnings) {
      lines.push(`- ${warning}`);
    }
  }
  return lines.join('\n');
}

function downloadTextFile(filename, content, mimeType = 'text/plain;charset=utf-8', options = {}) {
  const prefix = options.utf8Bom ? '\uFEFF' : '';
  const blob = new Blob([prefix, content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function sortPidValues(values = []) {
  return [...values].sort((left, right) => {
    const tokenize = (value) => String(value || '').match(/[A-Za-z]+|\d+/g) || [];
    const a = tokenize(left);
    const b = tokenize(right);
    const len = Math.max(a.length, b.length);
    for (let index = 0; index < len; index += 1) {
      const av = a[index] ?? '';
      const bv = b[index] ?? '';
      const aNum = Number.parseInt(av, 10);
      const bNum = Number.parseInt(bv, 10);
      const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
      if (bothNumeric && aNum !== bNum) return aNum - bNum;
      const textCompare = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      if (textCompare !== 0) return textCompare;
    }
    return String(left || '').localeCompare(String(right || ''));
  });
}

function readStoredPlaceholderAuth() {
  try {
    const storedAuth = localStorage.getItem('msr-auth') || sessionStorage.getItem('msr-auth');
    if (!storedAuth) {
      return null;
    }
    return JSON.parse(storedAuth);
  } catch {
    return null;
  }
}

function AppShell({ config, auth }) {
  const {
    apiEndpoint: API_ENDPOINT,
    listMonthsUrl,
    resyncUrl,
    syncStatusUrl
  } = config;
  const { authenticatedFetch, isAuthenticated, loginView, onLogout, user } = auth;
  
  const [activeTab, setActiveTab] = useState('upload');
  
  // Project selection state
  // Persist project/year selection across reloads
  const [projectName, setProjectName] = useState(() => {
    try { return localStorage.getItem('msr_projectName') || ''; } catch { return ''; }
  });
  const [year, setYear] = useState(() => {
    try { return localStorage.getItem('msr_year') || ''; } catch { return ''; }
  });
  // Save on change
  useEffect(() => { try { if (projectName) localStorage.setItem('msr_projectName', projectName); } catch { /* Persistence is best-effort. */ } }, [projectName]);
  useEffect(() => { try { if (year) localStorage.setItem('msr_year', year); } catch { /* Persistence is best-effort. */ } }, [year]);
  const [selectedMonth, setSelectedMonth] = useState(''); 
  const [selectedMonths, setSelectedMonths] = useState([]); 
  const [projectConfirmed, setProjectConfirmed] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [kbSelectedPeriod, setKbSelectedPeriod] = useState('');
  const [kbComparePeriods, setKbComparePeriods] = useState([]);
  const selectedMonthRef = useRef('');
  const lastUserSelectedMonthRef = useRef('');
  
  // Template state
  const [templateFile, setTemplateFile] = useState(null);
  const [templateUploaded, setTemplateUploaded] = useState(false);
  const [, setTemplateKey] = useState('');
  const [templateUploading, setTemplateUploading] = useState(false);

  // Input documents state
  const [inputFiles, setInputFiles] = useState([]);
  const [inputsUploaded, setInputsUploaded] = useState(false);
  const [inputsUploading, setInputsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [inputUploadMode, setInputUploadMode] = useState('replace');
  const [isDraggingOverTemplate, setIsDraggingOverTemplate] = useState(false);
  const [isDraggingOverInputs, setIsDraggingOverInputs] = useState(false);
  const inputFilePickerRef = useRef(null);
  const [completedMsrFiles, setCompletedMsrFiles] = useState([]);
  const [completedMsrUploading, setCompletedMsrUploading] = useState(false);
  const completedMsrPickerRef = useRef(null);
  
  // Transmittal letter fields (optional — toggle off to skip substitution
  // AND strip cover pages from the generated report)
  const [includeTransmittal, setIncludeTransmittal] = useState(true);
  const [transmittalExpanded, setTransmittalExpanded] = useState(false);
  const [letterDate, setLetterDate] = useState('');
  const [baseOfOperation, setBaseOfOperation] = useState('');
  const [reportSubject, setReportSubject] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [cdrlTitleDid, setCdrlTitleDid] = useState('');

  // Cover letter generator fields (optional — for standalone transmittal letter)
  const [showCoverLetterFields, setShowCoverLetterFields] = useState(false);
  const [pmName, setPmName] = useState('');
  const [pmTitle, setPmTitle] = useState('');
  const [pmPhone, setPmPhone] = useState('');
  const [pmEmail, setPmEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [referenceLine, setReferenceLine] = useState('');
  const [programDescription, setProgramDescription] = useState('');
  const [contactLine, setContactLine] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const [preparedBy, setPreparedBy] = useState('');
  const [transmittalGenerating, setTransmittalGenerating] = useState(false);
  const [transmittalDownloadUrl, setTransmittalDownloadUrl] = useState('');

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [, setExecutionArn] = useState('');
  const [executionStatus, setExecutionStatus] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const [processingWarnings, setProcessingWarnings] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [duplicateOverride, setDuplicateOverride] = useState(false);
  const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);
  // Template-mode override slider — default 'auto' lets the backend decide,
  // 'pid' forces PID matching, 'exact_header_match' forces heading-text matching.
  const [templateModeOverride, setTemplateModeOverride] = useState(() => {
    try { return localStorage.getItem('msr_template_mode') || 'auto'; } catch { return 'auto'; }
  });
  const [rosterText, setRosterText] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [kbStatus, setKbStatus] = useState(null);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbQueryResult, setKbQueryResult] = useState(null);
  const [kbQueryLoading, setKbQueryLoading] = useState(false);
  const [kbShowPidInventory, setKbShowPidInventory] = useState(false);
  const [kbShowSubmittedRoster, setKbShowSubmittedRoster] = useState(false);
  const [kbHideTrivialOverlaps, setKbHideTrivialOverlaps] = useState(true);
  const [kbSelectedPid, setKbSelectedPid] = useState('');
  const [kbPidDetail, setKbPidDetail] = useState(null);
  const [kbPidDetailLoading, setKbPidDetailLoading] = useState(false);
  const [kbNotice, setKbNotice] = useState(null);
  const [kbClearing, setKbClearing] = useState(false);
  const rosterStorageKey = projectName && year && (selectedMonth || selectedMonths[0])
    ? `msr_roster_text_${projectName}_${year}_${selectedMonth || selectedMonths[0]}`
    : '';
  const matchingModeOptions = [
    {
      value: 'auto',
      label: 'Auto-detect',
      tooltip: 'Let the system inspect the template and pick PID or Exact Heading automatically. Recommended unless auto picks the wrong pipeline.',
    },
    {
      value: 'pid',
      label: 'PID',
      tooltip: 'Force PID matching. The template must contain markers of the form "PID xxxx" where x is any combination of digits and periods (no spaces, letters, or other characters). Example: "PID 1.1.42".',
    },
    {
      value: 'exact_header_match',
      label: 'Exact Heading',
      tooltip: 'Force exact heading matching. The template must use Heading 1/2/3 styles, and each input doc must use the same heading text. The system inserts content from each input doc under the matching heading in the template.',
    },
  ];
  // Project-specific defaults for transmittal fields + cover letter details
  const PROJECT_DEFAULTS = {
    'AFIT': {
      base: "Air Force Institute of Technology\n2950 Hobson Way\nWright-Patterson AFB, OH 45433-7765",
      contract: "GS00Q14OADS112, Delivery Order FA8622-20-F-8145",
      cdrl: "A001/ Monthly Status Report/DI-MGMT-80368A/T",
      pmName: "Jane Doe",
      pmTitle: "Program Manager",
      pmPhone: "(555) 555-0100",
      pmEmail: "pm@example.com",
      companyName: "Example Company",
      recipientName: "Ms. Recipient One / Ms. Recipient Two",
      referenceLine: "Air Force Institute of Technology (AFIT) Program",
      programDescription: "the Air Force Institute of Technology (AFIT) Programs Civil Engineer School (CE), Civilian Institution Programs (CI), School of Engineering and Management (EN), School of Strategic Force Studies (EX), and the School of Systems and Logistics (LS)",
    },
    'AFMC HQ': {
      // Matches the template letter exactly (2 lines, no city line) so the
      // default run leaves the letter address untouched.
      base: "Headquarters Air Force Materiel Command (HQ AFMC)\n4225 Logistics Ave # S205",
      contract: "47QRAD20D1042, Task Order FA8622-22-F-0001",
      cdrl: "A001/ Monthly Status Report/DI-MGMT-80368A/T",
    },
    'F-35': {
      base: "F-35 Joint Program Office\n200 12th Street South\nArlington, VA 22202",
      contract: "GS00Q14OADS112, Delivery Order FA8675-20-F-2045",
      cdrl: "A001/ Monthly Status Report/DI-MGMT-80368A/T",
    },
    'F-16': {
      base: "Air Force Life Cycle Management Center\nAFLCMC/WAM (F-16 Division)\nWright-Patterson AFB, OH 45433",
      contract: "FA8622-26-F-B002",
      cdrl: "A001/ Monthly Status Report/DI-MGMT-80368A/T",
      companyName: "Credence Management Solutions, LLC",
    },
    'EPASS Task 74': {
      base: "DAF C3BM/C3I, C2ISR Division\n750 Third Street, Bldg 350\nRobins AFB, GA 31098-2122",
      contract: "47QRAD20D3009, Delivery Order FA8721-24-F-B005",
      cdrl: "A001/ Monthly Status Report/DI-MGMT-80368A/T",
      pmName: "John Smith",
      pmTitle: "SPM",
      pmPhone: "(555) 555-0101",
      pmEmail: "spm@example.com",
      companyName: "Example Subsidiary, LLC",
      recipientName: "",
      recipientAddress: "DAF C3BM/C3I, C2ISR Division,\n750 Third Street, Bldg 350\nRobins AFB, GA 31098-2122",
      referenceLine: "Department of the Air Force C3BM/C3I",
      programDescription: "the Department of the Air Force (DAF) Command, Control, Communication Battle Management (C3BM) Directorate Command, Control, Communication & Intelligence (C3I) Directorate, Command, Control, Intelligence, Surveillance and Reconnaissance (C2ISR) Division",
      contactLine: "If you have technical questions or need additional information, please contact John Smith, SPM at (555) 555-0101, spm@example.com, or Alex Johnson, Director at (555) 555-0102, director@example.com.",
      submittedTo: "Ms. Recipient One\nMr. Recipient Two\nCapt. Recipient Three\nMr. Recipient Four\nDAF PEO C3BM/C3I\n750 Third Street, Bldg 350\nRobins AFB, GA 31098-2122",
      preparedBy: "Mr. Preparer One (555) 555-0103\nMs. Preparer Two (555) 555-0101\nMr. Preparer Three (555) 555-0102\nExample Subsidiary, LLC\n1775 Tysons Blvd, Suite 800\nVienna, VA 22102",
    },
    'EPASS Task 79': {
      // EPASS Task 79 (AFLCMC EB EPASS) does NOT use a transmittal letter.
      // Its cover places every field in <w:txbxContent> text boxes on the
      // first page. We auto-populate only the contract-level constants
      // here; PM-specific fields (name, phone, email, etc.) are left
      // blank for each user to enter, and transmittal-only fields
      // (recipientName, referenceLine, programDescription, contactLine,
      // submittedTo, preparedBy) are intentionally omitted because there
      // is no transmittal letter to render.
      base: "AFLCMC AZS- Strategic Services Division\n20 Schilling Circle, Bldg 1305\nHanscom AFB, MA 01731",
      contract: "FA8721-26-F-B001",
      cdrl: "CDRL A001",
      companyName: "Credence Management Solutions LLC",
    },
    'DAF C3BM/C3I': {
      base: "DAF C3BM/C3I, C2ISR Division\n750 Third Street, Bldg 350\nRobins AFB, GA 31098-2122",
      contract: "47QRAD20D3009, Delivery Order FA8721-24-F-B005",
      cdrl: "A001 Monthly Status Report",
      pmName: "John Smith",
      pmTitle: "SPM",
      pmPhone: "(555) 555-0101",
      pmEmail: "spm@example.com",
      companyName: "Example Subsidiary, LLC",
      recipientName: "",
      recipientAddress: "DAF C3BM/C3I, C2ISR Division,\n750 Third Street, Bldg 350\nRobins AFB, GA 31098-2122",
      referenceLine: "Department of the Air Force C3BM/C3I",
      programDescription: "the Department of the Air Force (DAF) Command, Control, Communication Battle Management (C3BM) Directorate Command, Control, Communication & Intelligence (C3I) Directorate, Command, Control, Intelligence, Surveillance and Reconnaissance (C2ISR) Division",
      contactLine: "If you have technical questions or need additional information, please contact John Smith, SPM at (555) 555-0101, spm@example.com, or Alex Johnson, Director at (555) 555-0102, director@example.com.",
    },
  };

  // Generate year and month options
  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 5}, (_, i) => currentYear - 2 + i);
  const months = MONTH_OPTIONS;
  const realProjectOptions = ['AFIT', 'AFIT Bridge', 'AFMC HQ', 'F-16', 'F-35', 'DAF C3BM/C3I', 'EPASS Task 74', 'EPASS Task 79'];

  const _normalizeSubmissionMonthOptions = (rawOptions, targetYear) => {
    const seen = new Map();
    (rawOptions || []).forEach(option => {
      if (!option) return;
      const rawValue = String(option.value || '').trim();
      const optionYear = String(option.year || (rawValue.includes('-') ? rawValue.split('-', 1)[0] : targetYear || '')).trim();
      const optionMonth = String(option.month || (rawValue.includes('-') ? rawValue.split('-').slice(-1)[0] : rawValue)).padStart(2, '0');
      if (!/^\d{2}$/.test(optionMonth)) return;
      if (targetYear && optionYear && optionYear !== String(targetYear)) return;
      if (!seen.has(optionMonth)) {
        seen.set(optionMonth, {
          value: optionMonth,
          label: months.find(m => m.value === optionMonth)?.label || option.label || optionMonth,
        });
      }
    });
    return Array.from(seen.values()).sort((left, right) => left.value.localeCompare(right.value));
  };

  const kbPeriodOptions = useMemo(() => ((kbStatus?.months || []).map(option => {
    const period = String(option.period || '').trim();
    const [periodYear = '', periodMonth = ''] = period.split('-');
    const monthLabel = MONTH_OPTIONS.find(m => m.value === periodMonth)?.label || periodMonth;
    return {
      value: period,
      year: periodYear,
      month: periodMonth,
      label: periodYear && periodMonth ? `${monthLabel} ${periodYear}` : period,
      entryCount: option.entryCount || 0,
    };
  })).sort((left, right) => right.value.localeCompare(left.value)), [kbStatus]);
  const kbPeriodLabelByValue = Object.fromEntries(kbPeriodOptions.map(option => [option.value, option.label]));

  // Transmittal subject presets, keyed off the selected month so every option
  // names the period the report actually covers.
  const subjectMonthLabel = months.find(m => m.value === selectedMonth)?.label || '';
  const subjectOptions = subjectMonthLabel
    ? [
        `Monthly Status Report for ${subjectMonthLabel} ${year}`,
        `Monthly Status Report for ${subjectMonthLabel} 1 - ${new Date(year, parseInt(selectedMonth, 10), 0).getDate()}, ${year}`,
        `Program Status Report for ${subjectMonthLabel} ${year}`,
        `Contract Status Report for ${subjectMonthLabel} ${year}`,
      ].map(s => ({ value: s, label: s }))
    : [];

  // --- Mismatch detection helpers ---
  // Compare on meaning, not on bytes. A strict !== flagged a mismatch the user
  // could not see or clear: pasting an address out of Word brings CRLF line
  // endings, and typing one leaves a trailing newline or space. Both render
  // identically to the default and both failed the comparison. Normalise line
  // endings, strip trailing space on each line, and trim the whole value on
  // both sides before comparing. Interior blank lines still count as a
  // difference — they change the rendered letter.
  const normalizeFieldValue = (value) => String(value ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => line.trimEnd())
    .join('\n')
    .trim();
  const fieldsDiffer = (a, b) => normalizeFieldValue(a) !== normalizeFieldValue(b);

  const projectDefaults = PROJECT_DEFAULTS[projectName] || {};
  const fieldMismatches = {};
  if (projectConfirmed && projectDefaults.base && baseOfOperation && fieldsDiffer(baseOfOperation, projectDefaults.base)) {
    // First line only — the warning sits in a narrow field, and a three-line
    // address wraps into it. '\n' here, not '\\n': the defaults hold real
    // newlines, so splitting on a literal backslash-n never matched and the
    // whole address was pasted into the sentence.
    fieldMismatches.base = `Default base for ${projectName} is "${projectDefaults.base.split('\n')[0]}"`;
  }
  if (projectConfirmed && projectDefaults.contract && contractNumber && fieldsDiffer(contractNumber, projectDefaults.contract)) {
    fieldMismatches.contract = `Default contract for ${projectName} is "${projectDefaults.contract}"`;
  }
  if (projectConfirmed && projectDefaults.cdrl && cdrlTitleDid && fieldsDiffer(cdrlTitleDid, projectDefaults.cdrl)) {
    fieldMismatches.cdrl = `Default CDRL for ${projectName} is "${projectDefaults.cdrl}"`;
  }

  // Detect date mismatches from input filenames
  const detectInputDateMismatch = (files) => {
    if (!files || files.length === 0 || !selectedMonth || !year) return null;
    const monthNum = parseInt(selectedMonth, 10);
    const selectedMonthName = MONTH_NAMES_FULL[monthNum - 1]?.toLowerCase() || '';
    const detectedDates = [];
    for (const file of files) {
      const name = file.name.toLowerCase();
      // Look for month names: "july", "august", etc.
      for (let mi = 0; mi < 12; mi++) {
        const mName = MONTH_NAMES_FULL[mi].toLowerCase();
        const mAbbr = mName.substring(0, 3);
        if (name.includes(mName) || name.includes(`_${mAbbr}_`) || name.includes(`-${mAbbr}-`)) {
          detectedDates.push({ file: file.name, month: MONTH_NAMES_FULL[mi], monthNum: mi + 1 });
          break;
        }
      }
      // Look for numeric month patterns: "07_2025", "07-2025", "08_2025"
      const numMatch = name.match(/(\d{2})[_-](\d{4})/);
      if (numMatch) {
        const fMonth = parseInt(numMatch[1], 10);
        const fYear = parseInt(numMatch[2], 10);
        if (fMonth >= 1 && fMonth <= 12) {
          detectedDates.push({ file: file.name, month: MONTH_NAMES_FULL[fMonth - 1], monthNum: fMonth, year: fYear });
        }
      }
    }
    // Check if any detected dates don't match the selected month/year
    const mismatched = detectedDates.filter(d => {
      if (d.monthNum !== monthNum) return true;
      if (d.year && d.year.toString() !== year) return true;
      return false;
    });
    if (mismatched.length > 0) {
      const examples = mismatched.slice(0, 3).map(d => `"${d.file}" appears to be from ${d.month}${d.year ? ' ' + d.year : ''}`);
      return `Input documents may not match selected period (${selectedMonthName} ${year}): ${examples.join('; ')}. Please verify the correct month/year is selected.`;
    }
    return null;
  };

  const inputDateWarning = inputFiles.length > 0 && selectedMonth && year ? detectInputDateMismatch(inputFiles) : null;

  // Auto-update subject line when month/year changes (only if it follows the default pattern)
  useEffect(() => {
    if (!projectConfirmed || !selectedMonth || !year) return;
    const subjectPattern = /^(Monthly Status Report|Program Status Report|Contract Status Report) for \w+ \d{4}$/;
    if (reportSubject && subjectPattern.test(reportSubject)) {
      const prefix = reportSubject.match(/^(.*) for /)[1];
      const ml = MONTH_NAMES_FULL[parseInt(selectedMonth, 10) - 1] || '';
      setReportSubject(`${prefix} for ${ml} ${year}`);
    }
  }, [projectConfirmed, reportSubject, selectedMonth, year]);

  // Set default values on mount
  useEffect(() => {
    const now = new Date();
    setYear(now.getFullYear().toString());
    // Set default selected month to current month
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    selectedMonthRef.current = currentMonth;
    lastUserSelectedMonthRef.current = currentMonth;
    setSelectedMonth(currentMonth);
    setSelectedMonths([currentMonth]); 
  }, []);

  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);

  useEffect(() => {
    if (activeTab !== 'upload') {
      setPreviewFullscreen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!previewFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewFullscreen]);

  useEffect(() => {
    if (!rosterStorageKey) {
      setRosterText('');
      return;
    }
    try {
      setRosterText(localStorage.getItem(rosterStorageKey) || '');
    } catch {
      setRosterText('');
    }
  }, [rosterStorageKey]);

  useEffect(() => {
    if (activeTab !== 'kb') return;
    const optionValues = kbPeriodOptions.map(option => option.value);
    const requestedPeriod = year && selectedMonth ? `${year}-${selectedMonth}` : '';
    setKbSelectedPeriod(currentKbPeriod => {
      if (optionValues.length === 0) {
        return '';
      }
      if (optionValues.includes(currentKbPeriod)) {
        return currentKbPeriod;
      }
      if (requestedPeriod && optionValues.includes(requestedPeriod)) {
        return requestedPeriod;
      }
      return optionValues[0];
    });
  }, [activeTab, kbPeriodOptions, selectedMonth, year]);

  useEffect(() => {
    if (activeTab !== 'kb') return;
    const optionValues = kbPeriodOptions.map(option => option.value);
    setKbComparePeriods(currentPeriods => {
      const filtered = currentPeriods.filter(period => optionValues.includes(period));
      if (filtered.length >= 2) {
        return filtered;
      }
      const next = [...filtered];
      if (kbSelectedPeriod && optionValues.includes(kbSelectedPeriod) && !next.includes(kbSelectedPeriod)) {
        next.push(kbSelectedPeriod);
      }
      for (const period of optionValues) {
        if (!next.includes(period)) {
          next.push(period);
        }
        if (next.length >= Math.min(2, optionValues.length)) {
          break;
        }
      }
      return next;
    });
  }, [activeTab, kbPeriodOptions, kbSelectedPeriod]);

  useEffect(() => {
    setKbSelectedPid('');
    setKbPidDetail(null);
    setKbPidDetailLoading(false);
    setKbNotice(null);
    // Also clear the cached KB query card (roster-status / missing / repeat /
    // verify) — otherwise it stays rendered from the previous project or month
    // until the user clicks another KB action.
    setKbQueryResult(null);
    setKbShowPidInventory(false);
    setKbShowSubmittedRoster(false);
  }, [projectName, year, kbSelectedPeriod]);

  // API helper functions
  const postJson = useCallback(async (url, data, requestName) => {
    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const responseText = await response.text();
      let responseData = {};
      if (responseText) {
        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = { message: responseText };
        }
      }

      if (!response.ok) {
        const wrappedBody = responseData && responseData.statusCode && responseData.body
          ? (typeof responseData.body === 'string' ? JSON.parse(responseData.body) : responseData.body)
          : null;
        const errorMessage =
          (wrappedBody && (wrappedBody.error || wrappedBody.message)) ||
          responseData.error ||
          responseData.message ||
          `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      console.log(`Response from ${requestName}:`, responseData);
      
      if (responseData.statusCode && responseData.body) {
        const bodyData = typeof responseData.body === 'string' 
          ? JSON.parse(responseData.body) 
          : responseData.body;
        
        if (responseData.statusCode >= 400) {
          throw new Error(bodyData.error || bodyData.message || 'Request failed');
        }
        
        return bodyData;
      }
      
      return responseData;
      
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }, [authenticatedFetch]);

  const apiPost = useCallback(async (endpoint, data) => {
    return postJson(`${API_ENDPOINT}${endpoint}`, data, endpoint);
  }, [API_ENDPOINT, postJson]);

  const functionUrlPost = useCallback(async (url, data, requestName) => {
    return postJson(url, data, requestName);
  }, [postJson]);

  // Fetch available months from S3
  const fetchAvailableMonths = useCallback(async () => {
    setError('');
    
    try {
      const response = await functionUrlPost(listMonthsUrl, {
        projectName: projectName,
        year: year
      }, 'listMonthsUrl');
      
      if (response.months && Array.isArray(response.months)) {
        setAvailableMonths(response.months);
      } else {
        // Fallback to all months if no months found
        setAvailableMonths(MONTH_OPTIONS);
      }
    } catch (err) {
      console.error('Failed to fetch available months:', err);
      // Use default months on error - no need to show error to user
      setAvailableMonths(MONTH_OPTIONS);
    }
  }, [functionUrlPost, listMonthsUrl, projectName, year]);

  // Knowledge Base helpers (Steps 7 + 8)
  const loadKbStatus = useCallback(async () => {
    if (!projectName) return;
    setKbLoading(true);
    setKbNotice(null);
    try {
      const result = await apiPost('/kb-status', { projectName, action: 'status' });
      setKbStatus(result);
    } catch (err) {
      setError('Failed to load knowledge base: ' + err.message);
    }
    setKbLoading(false);
  }, [apiPost, projectName]);

  // Fetch available months when project or year changes.
  useEffect(() => {
    if (!isAuthenticated) {
      setAvailableMonths(MONTH_OPTIONS);
      return;
    }
    if (projectName && year) {
      fetchAvailableMonths();
      return;
    }
    setAvailableMonths(MONTH_OPTIONS);
  }, [activeTab, fetchAvailableMonths, isAuthenticated, projectName, year]);

  // A query button reads as active only while its own result is the one on
  // screen. runKbQuery clears kbQueryResult before fetching, so during a load
  // nothing is showing and no button should claim the state.
  const isKbQueryShowing = (queryType) =>
    !kbQueryLoading && kbQueryResult?.type === queryType;

  const runKbQuery = async (queryType) => {
    if (!projectName) return;
    setKbQueryLoading(true);
    setKbNotice(null);
    setKbQueryResult(null);
    setKbShowPidInventory(false);
    setKbShowSubmittedRoster(false);
    setKbSelectedPid('');
    setKbPidDetail(null);
    try {
      const period = activeTab === 'kb' ? activeKnowledgePeriod : `${year}-${getActiveUploadMonth()}`;
      if (queryType === 'compare-period-similarity') {
        if (kbComparePeriods.length < 2) {
          throw new Error('Select at least two saved periods to compare');
        }
        const result = await apiPost('/kb-status', {
          projectName,
          action: queryType,
          periods: kbComparePeriods,
        });
        setKbQueryResult({ type: queryType, data: result });
        setKbQueryLoading(false);
        return;
      }
      if (queryType !== 'verify' && !period) {
        throw new Error('No saved knowledge period is available for the selected project');
      }
      const [queryYear = year, queryMonth = ''] = period.split('-');
      if (queryType !== 'verify' && !queryMonth) {
        throw new Error('No saved knowledge period is available for the selected project');
      }
      const rosterList = rosterText.split('\n').map(s => s.trim()).filter(Boolean);
      const result = await apiPost('/kb-status', {
        projectName, year: queryYear, month: queryMonth,
        action: queryType,
        roster: rosterList,
      });
      setKbQueryResult({ type: queryType, data: result });
    } catch (err) {
      setError('Query failed: ' + err.message);
    }
    setKbQueryLoading(false);
  };

  const loadKbPidDetail = async (pid) => {
    if (!projectName || !activeKnowledgeYear || !activeKnowledgeMonth || !pid) return;
    if (kbSelectedPid === pid && kbPidDetail && !kbPidDetailLoading) {
      setKbSelectedPid('');
      setKbPidDetail(null);
      return;
    }
    setKbSelectedPid(pid);
    setKbPidDetailLoading(true);
    try {
      const result = await apiPost('/kb-status', {
        projectName,
        year: activeKnowledgeYear,
        month: activeKnowledgeMonth,
        action: 'pid-detail',
        pid,
      });
      setKbPidDetail(result);
    } catch (err) {
      setError('Failed to load entry detail: ' + err.message);
      setKbPidDetail(null);
    }
    setKbPidDetailLoading(false);
  };

  const handleClearKbMonth = async () => {
    if (!projectName || !activeKnowledgeYear || !activeKnowledgeMonth) return;
    const monthLabel = months.find(m => m.value === activeKnowledgeMonth)?.label || activeKnowledgeMonth;
    const confirmed = window.confirm(
      `Clear saved knowledge for ${projectName} ${monthLabel} ${activeKnowledgeYear}?\n\n` +
      `This removes the saved month manifest used by the Knowledge tab and chat.\n` +
      `It does not delete uploaded input files or generated reports.`
    );
    if (!confirmed) return;

    setKbClearing(true);
    setKbNotice(null);
    setError('');
    try {
      const result = await apiPost('/clear-kb-month', {
        projectName,
        year: activeKnowledgeYear,
        month: activeKnowledgeMonth,
      });
      setKbQueryResult(null);
      setKbSelectedPid('');
      setKbPidDetail(null);
      setKbShowPidInventory(false);
      setKbShowSubmittedRoster(false);
      setKbNotice({
        tone: 'success',
        text: result.bedrockResyncRecommended
          ? `${result.message} Run a resync if you want that deletion reflected in the cloud knowledge base too.`
          : result.message,
      });
      await loadKbStatus();
    } catch (err) {
      setError('Failed to clear saved knowledge: ' + err.message);
    }
    setKbClearing(false);
  };

  const handleCompletedMsrSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => file.name.toLowerCase().endsWith('.docx'));
    const invalidFiles = files.filter(file => !file.name.toLowerCase().endsWith('.docx'));
    setCompletedMsrFiles(validFiles);
    if (invalidFiles.length > 0) {
      setError('Only .docx completed MSR files are supported for Knowledge ingestion.');
    } else {
      setError('');
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleCompletedMsrUpload = async () => {
    const targetMonth = getActiveUploadMonth();
    if (!projectConfirmed || !projectName || !year || !targetMonth) {
      setError('Confirm the project, year, and month before adding completed MSRs to Knowledge.');
      return;
    }
    if (completedMsrFiles.length === 0) {
      setError('Choose at least one completed MSR .docx file to ingest.');
      return;
    }

    setCompletedMsrUploading(true);
    setKbNotice(null);
    setError('');
    try {
      const folderPath = `${projectName}/${year}/${targetMonth}/completed/`;
      for (const file of completedMsrFiles) {
        const presigned = await apiPost('/get-upload-url', {
          fileName: file.name,
          folderPath,
          contentType: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          clearFolder: false,
        });
        const uploadResponse = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
        });
        if (!uploadResponse.ok) {
          const responseText = await uploadResponse.text().catch(() => '');
          throw new Error(
            `Completed report upload failed with status ${uploadResponse.status}${
              responseText ? `: ${responseText.slice(0, 200)}` : ''
            }`
          );
        }
      }

      const ingestResult = await apiPost('/ingest-completed-msr', {
        projectName,
        year,
        month: targetMonth,
      });
      setKbNotice({
        tone: 'success',
        text: `${ingestResult.message} Added ${ingestResult.addedEntries || 0} new entries and supplemented ${ingestResult.supplementedEntries || 0}.`,
      });
      setCompletedMsrFiles([]);
      await loadKbStatus();
      setKbSelectedPeriod(`${year}-${String(targetMonth).padStart(2, '0')}`);
    } catch (error) {
      setError(`Failed to ingest completed MSRs: ${error.message}`);
    } finally {
      setCompletedMsrUploading(false);
    }
  };

  const toggleKbComparePeriod = (period) => {
    setKbComparePeriods(currentPeriods => {
      if (currentPeriods.includes(period)) {
        return currentPeriods.filter(value => value !== period);
      }
      return [...currentPeriods, period].sort((left, right) => left.localeCompare(right));
    });
  };

  useEffect(() => {
    if (activeTab === 'kb' && projectName) {
      loadKbStatus();
    }
  }, [activeTab, loadKbStatus, projectName]);

  // Generate standalone transmittal letter
  const handleGenerateTransmittal = async () => {
    if (!projectConfirmed) {
      setError('Please confirm the project, year, and month before generating a transmittal letter.');
      return;
    }
    setTransmittalGenerating(true);
    setTransmittalDownloadUrl('');
    setError('');
    try {
      const month = getActiveUploadMonth();
      if (!month) {
        throw new Error('Please select a report month before generating a transmittal letter.');
      }
      const result = await apiPost('/generate-transmittal', {
        projectName,
        year,
        month,
        letterDate,
        subject: reportSubject,
        contractNumber,
        cdrlTitleDid,
        baseOfOperation,
        referenceLine,
        transmissionMethod: 'Email',
        pmName,
        pmTitle,
        pmPhone,
        pmEmail,
        companyName: companyName || 'Credence',
        recipientName,
        recipientAddress,
        programDescription,
        contactLine,
        submittedTo,
        preparedBy,
      });
      if (result.downloadUrl) {
        setTransmittalDownloadUrl(result.downloadUrl);
      }
    } catch (err) {
      setError('Transmittal letter generation failed: ' + err.message);
    }
    setTransmittalGenerating(false);
  };

  // Validate project selection
  const isProjectValid = () => {
    return projectName && year && selectedMonth;
  };

  const resetValidationState = () => {
    setValidationResult(null);
    setDuplicateOverride(false);
    setShowDuplicateDetails(false);
    setShowConfirmModal(false);
  };

  const getActiveUploadMonth = () => selectedMonth || selectedMonths[0] || '';

  const handleDownloadValidationCsv = () => {
    if (!validationResult) return;
    const month = getActiveUploadMonth();
    const csv = buildValidationCsv({ projectName, year, month, validationResult });
    downloadTextFile(
      `${projectName || 'project'}_${year || 'year'}_${month || 'month'}_validation.csv`,
      csv,
      'text/csv;charset=utf-8',
      { utf8Bom: true }
    );
  };

  const handleDownloadValidationJson = () => {
    if (!validationResult) return;
    const month = getActiveUploadMonth();
    const payload = buildValidationExportPayload({ projectName, year, month, validationResult });
    downloadTextFile(
      `${projectName || 'project'}_${year || 'year'}_${month || 'month'}_validation.json`,
      JSON.stringify(payload, null, 2),
      'application/json;charset=utf-8'
    );
  };

  const handleCopyValidationSummary = async () => {
    if (!validationResult) return;
    const month = getActiveUploadMonth();
    const text = buildValidationSummaryText({ projectName, year, month, validationResult });
    try {
      await navigator.clipboard.writeText(text);
      setError('');
    } catch (err) {
      setError('Failed to copy validation summary: ' + err.message);
    }
  };

  const mergeInputFiles = (existingFiles, newFiles) => {
    const merged = [...existingFiles];
    for (const file of newFiles) {
      const alreadyPresent = merged.some(
        existing =>
          existing.name === file.name &&
          Number(existing.size || 0) === Number(file.size || 0)
      );
      if (!alreadyPresent) {
        merged.push(file);
      }
    }
    return merged;
  };

  // Handle project confirmation
  const handleConfirmProject = () => {
    if (isProjectValid()) {
      const confirmedMonth = lastUserSelectedMonthRef.current || selectedMonthRef.current || selectedMonth;
      if (confirmedMonth && confirmedMonth !== selectedMonth) {
        setSelectedMonth(confirmedMonth);
      }
      if (confirmedMonth && (selectedMonths.length !== 1 || selectedMonths[0] !== confirmedMonth)) {
        setSelectedMonths([confirmedMonth]);
      }
      setProjectConfirmed(true);
      setError('');
      // Auto-populate transmittal defaults ONLY if fields are empty
      const monthLabel = months.find(m => m.value === confirmedMonth)?.label || '';
      const defaults = PROJECT_DEFAULTS[projectName] || {};
      if (!reportSubject) setReportSubject(`Monthly Status Report for ${monthLabel} ${year}`);
      if (!letterDate) {
        const today = new Date();
        setLetterDate(`${MONTH_NAMES_FULL[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`);
      }
      if (!baseOfOperation && defaults.base) setBaseOfOperation(defaults.base);
      if (!contractNumber && defaults.contract) setContractNumber(defaults.contract);
      if (!cdrlTitleDid && defaults.cdrl) setCdrlTitleDid(defaults.cdrl);
      // Auto-populate cover letter defaults from project
      if (!pmName && defaults.pmName) setPmName(defaults.pmName);
      if (!pmTitle && defaults.pmTitle) setPmTitle(defaults.pmTitle);
      if (!pmPhone && defaults.pmPhone) setPmPhone(defaults.pmPhone);
      if (!pmEmail && defaults.pmEmail) setPmEmail(defaults.pmEmail);
      if (!companyName && defaults.companyName) setCompanyName(defaults.companyName);
      if (!recipientName && defaults.recipientName) setRecipientName(defaults.recipientName);
      if (!recipientAddress && defaults.recipientAddress) setRecipientAddress(defaults.recipientAddress);
      if (!referenceLine && defaults.referenceLine) setReferenceLine(defaults.referenceLine);
      if (!programDescription && defaults.programDescription) setProgramDescription(defaults.programDescription);
      if (!contactLine && defaults.contactLine) setContactLine(defaults.contactLine);
      if (!submittedTo && defaults.submittedTo) setSubmittedTo(defaults.submittedTo);
      if (!preparedBy && defaults.preparedBy) setPreparedBy(defaults.preparedBy);
      setTransmittalDownloadUrl('');
      // Reset upload/processing states (but preserve report details)
      setTemplateFile(null);
      setTemplateUploaded(false);
      setTemplateKey('');
      setInputFiles([]);
      setInputsUploaded(false);
      setInputUploadMode('replace');
      setUploadProgress({});
      setProcessingWarnings([]);
      setExecutionArn('');
      setExecutionStatus('');
      setDownloadUrl('');
      resetValidationState();
    } else {
      setError('Please fill in all project details');
    }
  };

  // Handle project change
  const handleChangeProject = () => {
    setProjectConfirmed(false);
    setError('');
    resetValidationState();
    // Reset transmittal fields so values don't bleed across projects
    setLetterDate('');
    setBaseOfOperation('');
    setReportSubject('');
    setContractNumber('');
    setCdrlTitleDid('');
    setPmName('');
    setPmTitle('');
    setPmPhone('');
    setPmEmail('');
    setCompanyName('');
    setRecipientName('');
    setRecipientAddress('');
    setReferenceLine('');
    setProgramDescription('');
    setContactLine('');
    setSubmittedTo('');
    setPreparedBy('');
    setTransmittalDownloadUrl('');
    setIncludeTransmittal(true);
  };

  // Template upload handlers
  const applyTemplateFileSelection = (file) => {
    resetValidationState();
    if (file && file.name.toLowerCase().endsWith('.docx')) {
      setTemplateFile(file);
      setTemplateUploaded(false);
      setTemplateKey('');
      setError('');
    } else if (file) {
      setError('Please select a valid .docx template file. Older .doc files must be re-saved as .docx first.');
    }
  };

  const handleTemplateSelect = (e) => {
    applyTemplateFileSelection(e.target.files[0]);
    if (e.target) {
      e.target.value = '';
    }
  };

  const clearSelectedTemplate = () => {
    resetValidationState();
    setTemplateFile(null);
    setTemplateUploaded(false);
    setTemplateKey('');
  };

  const handleTemplateUpload = async () => {
    if (!templateFile) {
      setError('Please select a template file');
      return;
    }

    if (!projectConfirmed) {
      setError('Please confirm the project details before uploading the template');
      return;
    }

    setTemplateUploading(true);
    setError('');

    try {
      const month = getActiveUploadMonth();
      const folderPath = `${projectName}/${year}/${month}/template/`;
      
      const presignedResponse = await apiPost('/get-upload-url', {
        fileName: templateFile.name,
        folderPath: folderPath,
        contentType: templateFile.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        clearFolder: true
      });

      const { uploadUrl, key } = presignedResponse;

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: templateFile,
        headers: {
          'Content-Type': templateFile.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      });

      if (!uploadResponse.ok) {
        const responseText = await uploadResponse.text().catch(() => '');
        throw new Error(
          `Upload failed with status ${uploadResponse.status}${
            responseText ? `: ${responseText.slice(0, 200)}` : ''
          }`
        );
      }

      setTemplateKey(key);
      setTemplateUploaded(true);
      setTemplateUploading(false);

    } catch (err) {
      console.error('Template upload error:', err);
      setError(`Template upload failed: ${err.message}`);
      setTemplateUploading(false);
    }
  };

  const createDropZoneHandlers = (setDragState, onDropFiles, disabled = false) => ({
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setDragState(true);
      }
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !e.currentTarget.contains(e.relatedTarget)) {
        setDragState(false);
      }
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragState(false);
      if (!disabled) {
        onDropFiles(e.dataTransfer.files);
      }
    },
  });

  // Input documents handlers
  const handleTemplateDrop = (files) => {
    const [file] = Array.from(files || []);
    applyTemplateFileSelection(file);
  };

  const templateDropHandlers = createDropZoneHandlers(
    setIsDraggingOverTemplate,
    handleTemplateDrop,
    templateUploading || processing
  );

  const clearSelectedInputs = () => {
    resetValidationState();
    setInputFiles([]);
    setInputsUploaded(false);
    setUploadProgress({});
    setInputUploadMode('replace');
  };

  const handleInputFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => file.name.toLowerCase().endsWith('.docx'));
    const invalidFiles = files.filter(file => !file.name.toLowerCase().endsWith('.docx'));
    resetValidationState();
    if (inputsUploaded && validFiles.length > 0) {
      setInputFiles(prev => mergeInputFiles(prev, validFiles));
      setInputsUploaded(false);
      setInputUploadMode('append');
    } else if (!inputsUploaded) {
      setInputFiles(validFiles);
      setInputUploadMode('replace');
      if (validFiles.length > 0) {
        setInputsUploaded(false);
      }
    }
    setUploadProgress({});
    setError(
      invalidFiles.length > 0
        ? 'Only .docx input files are supported. If an older Word file was renamed to .docx, open it in Word and save it as a true .docx first.'
        : ''
    );
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleInputsDrop = (files) => {
    const fileArray = Array.from(files || []);
    const validFiles = fileArray.filter(file => file.name.toLowerCase().endsWith('.docx'));
    const invalidFiles = fileArray.filter(file => !file.name.toLowerCase().endsWith('.docx'));

    resetValidationState();
    if (inputsUploaded && validFiles.length > 0) {
      setInputFiles(prev => mergeInputFiles(prev, validFiles));
      setInputsUploaded(false);
      setInputUploadMode('append');
    } else if (inputFiles.length > 0 && validFiles.length > 0) {
      setInputFiles(prev => mergeInputFiles(prev, validFiles));
    } else if (validFiles.length > 0) {
      setInputFiles(validFiles);
      setInputUploadMode('replace');
    }
    setUploadProgress({});
    setError(
      invalidFiles.length > 0
        ? 'Only .docx input files are supported. If an older Word file was renamed to .docx, open it in Word and save it as a true .docx first.'
        : ''
    );
  };

  const inputsDropHandlers = createDropZoneHandlers(
    setIsDraggingOverInputs,
    handleInputsDrop,
    inputsUploading || processing
  );

  const handleInputsUpload = async () => {
    if (inputFiles.length === 0) {
      setError('Please select at least one input document');
      return;
    }

    if (!projectConfirmed) {
      setError('Please confirm the project details before uploading input documents');
      return;
    }

    setInputsUploading(true);
    setError('');
    setUploadProgress({});

    try {
      const month = getActiveUploadMonth();
      const folderPath = `${projectName}/${year}/${month}/input/`;

      if (inputUploadMode !== 'append') {
        await apiPost('/clear-folder', { folderPath });
      }
      
      for (let i = 0; i < inputFiles.length; i++) {
        const file = inputFiles[i];
        const contentType = file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        const presignedResponse = await apiPost('/get-upload-url', {
          fileName: file.name,
          folderPath: folderPath,
          contentType,
          clearFolder: false
        });

        const { uploadUrl } = presignedResponse;

        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 0
        }));

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': contentType
          }
        });

        if (!uploadResponse.ok) {
          const responseText = await uploadResponse.text().catch(() => '');
          throw new Error(
            `Upload failed with status ${uploadResponse.status}${
              responseText ? `: ${responseText.slice(0, 200)}` : ''
            }`
          );
        }

        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 100
        }));
      }

      setInputsUploaded(true);
      setInputUploadMode('replace');
      setInputsUploading(false);
      
      // Refresh available months after successful upload
      if (projectName && year) {
        fetchAvailableMonths();
      }
      
    } catch (err) {
      console.error('Input upload error:', err);
      setError(`Input upload failed: ${err.message}`);
      setInputsUploading(false);
    }
  };

  // Process MSR
  const handleProcess = async () => {
    if (!templateUploaded) {
      setError('Please upload a template first');
      return;
    }

    if (!inputsUploaded) {
      setError('Please upload input documents first');
      return;
    }

    if (!projectConfirmed) {
      setError('Please select project details first');
      return;
    }

    setProcessing(true);
    setError('');
    setExecutionStatus('STARTING');
    setDownloadUrl('');
    setProcessingWarnings([]);

    try {
      const month = getActiveUploadMonth();
      const payload = {
        projectName: projectName,
        year: year,
        month: month,
        templateModeOverride: templateModeOverride,
        overrideDuplicates: duplicateOverride,
        includeTransmittal: includeTransmittal,
      };
      if (includeTransmittal) {
        payload.transmittalFields = {
          letterDate: letterDate || '',
          subject: reportSubject || '',
          contractNumber: contractNumber || '',
          cdrlTitleDid: cdrlTitleDid || '',
          baseOfOperation: baseOfOperation || '',
          submittedTo: submittedTo || '',
          preparedBy: preparedBy || '',
          reportMonth: month,
          reportYear: year,
        };
      }
      const response = await apiPost('/process-msr', payload);

      const { executionArn } = response;
      console.log('Started execution:', executionArn);
      setExecutionArn(executionArn);
      
      pollExecutionStatus(executionArn);
      
    } catch (err) {
      console.error('Processing error:', err);
      setError(`Processing failed: ${err.message}`);
      setProcessing(false);
      setExecutionStatus('FAILED');
    }
  };

  // Polling function
  const pollExecutionStatus = async (arn) => {
    const maxAttempts = 60;
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await apiPost('/check-status', {
          executionArn: arn
        });

        const { status, output } = response;
        setExecutionStatus(status);

        if (status === 'SUCCEEDED') {
          setProcessing(false);
          
          console.log('Execution succeeded with output:', output);
          
          if (output && output.Payload) {
            let reportData = null;
            
            if (typeof output.Payload === 'string') {
              try {
                reportData = JSON.parse(output.Payload);
              } catch (e) {
                console.error('Failed to parse Payload string:', e);
                reportData = output.Payload;
              }
            } else {
              reportData = output.Payload;
            }
            
            console.log('Parsed report data:', reportData);
            
            // Capture warnings from the pipeline
            const pipelineWarnings = reportData?.warnings || [];
            if (reportData && reportData.insertedCount === 0) {
              pipelineWarnings.unshift('No PID-matched content was inserted. The input documents may not contain recognized PID section markers. The report contains only the template structure — please review manually.');
            }
            if (pipelineWarnings.length > 0) {
              setProcessingWarnings(pipelineWarnings);
            }
            // Capture per-section AI revision results when present.
            if (reportData && reportData.outputKey) {
              await getDownloadUrl(reportData.outputBucket, reportData.outputKey);
            } else if (reportData && reportData.reportUrl) {
              const match = reportData.reportUrl.match(/s3:\/\/([^/]+)\/(.+)/);
              if (match) {
                const [, bucket, key] = match;
                await getDownloadUrl(bucket, key);
              } else {
                console.error('Invalid S3 URL format:', reportData.reportUrl);
                setError('Invalid report URL format');
              }
            } else {
              console.error('Could not find output bucket/key in:', reportData);
              setError('Report generated but download information not found.');
            }
          } else {
            console.error('No Payload in output:', output);
            setError('Report generated but output format unexpected');
          }
          
        } else if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED') {
          setProcessing(false);
          const errorMsg = response.error || response.cause || `Processing ${status.toLowerCase()}`;
          setError(errorMsg);
          console.error('Execution failed:', errorMsg);
          
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 5000);
        } else {
          setProcessing(false);
          setError('Processing timeout - please check AWS Console');
        }
        
      } catch (err) {
        console.error('Status check error:', err);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkStatus, 5000);
        } else {
          setProcessing(false);
          setError(`Failed to check status: ${err.message}`);
        }
      }
    };

    checkStatus();
  };

  // Generate DOCX preview by fetching the file and converting to HTML
  const generatePreview = async (url) => {
    setPreviewLoading(true);
    setPreviewHtml('');
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setPreviewHtml(result.value);
    } catch (err) {
      console.error('Preview generation failed:', err);
      setPreviewHtml('<p style="color:#666;font-style:italic;">Preview could not be generated. The document can still be downloaded.</p>');
    }
    setPreviewLoading(false);
  };

  // Get download URL
  const getDownloadUrl = async (bucket, key) => {
    try {
      console.log('Getting download URL for:', { bucket, key });
      
      const payload = { key };
      if (bucket) {
        payload.bucket = bucket;
      }

      const response = await apiPost('/get-download-url', payload);

      if (response.downloadUrl) {
        setDownloadUrl(response.downloadUrl);
        // Generate preview
        generatePreview(response.downloadUrl);
      } else {
        setError('Failed to get download URL');
      }
      
    } catch (err) {
      console.error('Download URL error:', err);
      setError(`Failed to get download URL: ${err.message}`);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const resetApp = () => {
    setTemplateFile(null);
    setTemplateUploaded(false);
    setTemplateKey('');
    setInputFiles([]);
    setInputsUploaded(false);
    setInputUploadMode('replace');
    setUploadProgress({});
    setExecutionArn('');
    setExecutionStatus('');
    setDownloadUrl('');
    setError('');
    setProcessingWarnings([]);
    setPreviewHtml('');
    setPreviewLoading(false);
    setPreviewFullscreen(false);
    setValidationResult(null);
    setValidating(false);
    setProcessing(false);
  };

  // Handle logout
  const handleLogout = async () => {
    setProjectConfirmed(false);
    setActiveTab('upload');
    localStorage.removeItem('msr-auth');
    sessionStorage.removeItem('msr-auth');
    await onLogout();
  };

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return loginView;
  }

  const validationReadiness = validationResult ? getValidationReadiness(validationResult) : null;
  const validationPresentation = validationResult ? getValidationPresentation(validationResult) : null;
  const validationDuplicateEntries = validationResult ? collectValidationDuplicateEntries(validationResult) : [];
  const activeUploadMonth = getActiveUploadMonth();
  const activeKnowledgePeriod = kbSelectedPeriod || '';
  const [activeKnowledgeYear = '', activeKnowledgeMonth = ''] = activeKnowledgePeriod.split('-');
  const canRunMonthScopedKbQueries = Boolean(activeKnowledgeYear && activeKnowledgeMonth);
  const knowledgeIngestTargetPeriod = year && activeUploadMonth ? `${year}-${activeUploadMonth}` : '';
  const knowledgeIngestTargetLabel = knowledgeIngestTargetPeriod
    ? `${projectName} · ${months.find(m => m.value === activeUploadMonth)?.label || activeUploadMonth} ${year}`
    : '';
  // Hide the Completed MSR Ingest panel when the target period already has
  // saved manifest data — i.e. the normal input/template flow has already run,
  // or a completed MSR has already been ingested. The panel is strictly for
  // back-filling prior months/years the tool never processed. Users who need
  // to re-ingest can "Clear Saved Month Data" first.
  const knowledgeIngestTargetHasData = knowledgeIngestTargetPeriod
    ? kbPeriodOptions.some(option => option.value === knowledgeIngestTargetPeriod && (option.entryCount || 0) > 0)
    : false;
  const showCompletedMsrIngest = false;
  const showRosterQueryDetails = false;
  const requestedKnowledgePeriod = year && selectedMonth ? `${year}-${selectedMonth}` : '';
  const kbCurrentSelectionHasData = requestedKnowledgePeriod
    ? kbPeriodOptions.some(option => option.value === requestedKnowledgePeriod)
    : false;
  const kbRedirectTarget = kbPeriodOptions.find(option => option.value === activeKnowledgePeriod) || kbPeriodOptions[0] || null;
  const kbPeriodWarning = requestedKnowledgePeriod && kbPeriodOptions.length > 0 && !kbCurrentSelectionHasData
    ? {
        requested: requestedKnowledgePeriod,
        redirectedTo: kbRedirectTarget?.label || kbRedirectTarget?.value || '',
      }
    : null;
  const kbRosterStatusData = kbQueryResult?.type === 'roster-status' ? kbQueryResult.data : null;
  const kbSubmittedPidList = kbRosterStatusData ? sortPidValues(kbRosterStatusData.submittedEntries || []) : [];
  const kbRosterRows = kbRosterStatusData?.rosterRows || [];
  const kbIsInputConfirmedRoster = kbRosterStatusData?.rosterSource === 'input-confirmed';
  const kbCanDetermineMissing = kbRosterStatusData ? kbRosterStatusData.canDetermineMissing !== false : true;
  const kbRosterNote = kbRosterStatusData?.note || '';
  const kbMissingRosterRows = kbRosterRows.filter(row => !row.submitted);
  const kbSubmittedRosterRows = kbRosterRows.filter(row => row.submitted);
  const kbInputFileRows = kbRosterStatusData
    ? [...(kbRosterStatusData.inputFiles || [])]
        .sort((left, right) => String(left || '').localeCompare(String(right || '')))
        .map(filename => ({
          filename,
          // Uploads through the app sanitize filenames (spaces -> underscores),
          // so compare manifest sources and raw S3 names in sanitized form.
          includedInManifest: (kbRosterStatusData.submittedSources || []).some(
            source => String(source || '').replace(/ /g, '_') === String(filename || '').replace(/ /g, '_'),
          ),
        }))
    : [];
  const kbComparePeriodChips = kbPeriodOptions.map(option => ({
    ...option,
    selected: kbComparePeriods.includes(option.value),
  }));

  return (
    <div className="App">
      <header className="App-header">
        {/* Main Header Bar */}
        <div className="header-main">
          <div className="header-left">
            <img src={logo} alt="Logo" className="header-logo" />
          </div>
          
          <div className="header-center">
            <div className="header-tabs">
              <button
                className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
                onClick={() => setActiveTab('upload')}
              >
                <span className="tab-icon" aria-hidden="true"><Rocket size={18} /></span>
                <span className="tab-text">Process</span>
              </button>
              <button
                className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('chat');
                  if (projectName && year) fetchAvailableMonths();
                }}
              >
                <span className="tab-icon" aria-hidden="true"><MessageSquareText size={18} /></span>
                <span className="tab-text">Chat</span>
              </button>
              <button
                className={`tab ${activeTab === 'kb' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('kb');
                  if (projectName) loadKbStatus();
                }}
              >
                <span className="tab-icon" aria-hidden="true"><BookOpen size={18} /></span>
                <span className="tab-text">Knowledge</span>
              </button>
            </div>
          </div>
          
          <div className="header-right">
            <div className="header-user">
              <span className="user-name"><span className="user-badge" aria-hidden="true"><UserRound size={15} /></span> {user?.email || user?.name}</span>
              <button
                onClick={handleLogout}
                className="btn btn-logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        
        {/* Project Selection Bar */}
        <div className="project-selection-bar">
          {!projectConfirmed ? (
            <div className="project-controls">
              <div className="control-group">
                <label htmlFor="project-select">Project</label>
                <select
                  id="project-select"
                  aria-label="Project"
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    resetValidationState();
                  }}
                  className="project-select"
                >
                  <option value="">Select project</option>
                  {realProjectOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="control-group">
                <label htmlFor="year-select">Year</label>
                <select
                  id="year-select"
                  aria-label="Year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="project-select"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="control-group">
                <label htmlFor="month-select">Month</label>
                <select
                  id="month-select"
                  aria-label="Month"
                  value={selectedMonth}
                  onChange={(e) => {
                    const nextMonth = e.target.value;
                    selectedMonthRef.current = nextMonth;
                    lastUserSelectedMonthRef.current = nextMonth;
                    setSelectedMonth(nextMonth);
                    setSelectedMonths([nextMonth]); // Keep selectedMonths in sync
                  }}
                  className="project-select"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              <button 
                onClick={handleConfirmProject}
                className="btn btn-confirm"
                disabled={!isProjectValid()}
              >
                Confirm
              </button>
            </div>
          ) : (
            <div className="project-confirmed">
              <div className="project-display">
                <span className="project-label">Current Project:</span>
                <span className="project-value">{projectName}</span>
                <span className="project-separator">/</span>
                <span className="project-value">{activeTab === 'kb' ? (activeKnowledgeYear || year) : year}</span>
                <span className="project-separator">/</span>
                <span className="project-value">
                  {activeTab === 'upload' ? (
                    // On upload tab, show the selected month for processing
                    months.find(m => m.value === selectedMonth)?.label || 'Month'
                  ) : activeTab === 'kb' ? (
                    kbPeriodOptions.length === 0
                      ? 'No saved period'
                      : months.find(m => m.value === activeKnowledgeMonth)?.label || 'Saved period'
                  ) : (
                    // On chat tab, show the selected months within the active year
                    selectedMonths.length === 0 
                      ? 'No months selected'
                      : selectedMonths.length === 1
                      ? months.find(m => m.value === selectedMonths[0])?.label
                      : `${selectedMonths.length} months selected`
                  )}
                </span>
              </div>
              <button 
                onClick={handleChangeProject}
                className="btn btn-change-small"
              >
                Change
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="App-main">
        <div className="content-wrapper">
          {activeTab === 'upload' ? (
            <div className="upload-container">
              {/* Progress Steps */}
              <div className="progress-steps">
                <div className={`step ${inputsUploaded ? 'completed' : 'active'}`}>
                  <span className="step-number">1</span>
                  <span className="step-label">Upload Documents</span>
                </div>
                <div className={`step ${templateUploaded ? 'completed' : 'active'}`}>
                  <span className="step-number">2</span>
                  <span className="step-label">Upload Template</span>
                </div>
                <div className={`step ${(!inputsUploaded || !templateUploaded) ? 'disabled' : downloadUrl ? 'completed' : 'active'}`}>
                  <span className="step-number">3</span>
                  <span className="step-label">Generate Report</span>
                </div>
              </div>

              {/* Report Details Card — transmittal letter is optional */}
              {projectConfirmed && (
                <div className="transmittal-section">
                  <button
                    type="button"
                    className="transmittal-toggle"
                    onClick={() => setTransmittalExpanded(!transmittalExpanded)}
                    aria-expanded={transmittalExpanded}
                  >
                    <span className="transmittal-toggle-icon" aria-hidden="true"><Mail size={16} /></span>
                    <span className="transmittal-toggle-label">Transmittal Letter (Optional)</span>
                    <span className={`transmittal-toggle-chevron ${transmittalExpanded ? 'open' : ''}`} aria-hidden="true"><ChevronDown size={16} /></span>
                  </button>
                  {transmittalExpanded && (
                    <div className="transmittal-panel">
                      <label className="transmittal-include-row">
                        <input
                          type="checkbox"
                          checked={includeTransmittal}
                          onChange={e => setIncludeTransmittal(e.target.checked)}
                        />
                        <span>Include transmittal letter / cover pages in the generated report</span>
                      </label>
                      {includeTransmittal && <div className="transmittal-field-grid">
                    <div className="field">
                      <label className="field-label" htmlFor="tl-letter-date">Letter Date</label>
                      <input id="tl-letter-date" type="text" value={letterDate} onChange={e => setLetterDate(e.target.value)} className="project-select" placeholder="e.g. March 15, 2026" />
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="tl-subject">Subject</label>
                      <PresetCombo
                        id="tl-subject"
                        ariaLabel="Subject"
                        value={reportSubject}
                        onChange={setReportSubject}
                        options={subjectOptions}
                        placeholder="Type a custom subject"
                        blankHint="Leave blank and the standard subject for the selected month is used."
                      />
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="tl-contract">Contract #</label>
                      <PresetCombo
                        id="tl-contract"
                        ariaLabel="Contract number"
                        value={contractNumber}
                        onChange={setContractNumber}
                        options={CONTRACT_OPTIONS}
                        placeholder="Type a custom contract #"
                        blankHint="Leave blank to keep the contract number already in the template."
                      />
                      {fieldMismatches.contract && <div className="field-warning"><AlertTriangle size={12} aria-hidden="true" /> Does not match {projectName} default. {fieldMismatches.contract}</div>}
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="tl-cdrl">CDRL/Title/DID</label>
                      <PresetCombo
                        id="tl-cdrl"
                        ariaLabel="CDRL title DID"
                        value={cdrlTitleDid}
                        onChange={setCdrlTitleDid}
                        options={CDRL_OPTIONS}
                        placeholder="Type a custom CDRL"
                        blankHint="Leave blank to keep the CDRL line already in the template."
                      />
                      {fieldMismatches.cdrl && <div className="field-warning"><AlertTriangle size={12} aria-hidden="true" /> Does not match {projectName} default. {fieldMismatches.cdrl}</div>}
                    </div>
                    <div className="field">
                      <label className="field-label" htmlFor="tl-base">Base of Operation</label>
                      <PresetCombo
                        id="tl-base"
                        ariaLabel="Base of operation"
                        value={baseOfOperation}
                        onChange={setBaseOfOperation}
                        options={BASE_OPTIONS}
                        emptyLabel="-- Select a base --"
                        placeholder={"Organization Name\nStreet Address\nCity, ST ZIP"}
                        blankHint="Leave blank to keep the base of operation already in the template."
                        multiline
                      />
                      {fieldMismatches.base && <div className="field-warning"><AlertTriangle size={12} aria-hidden="true" /> Does not match {projectName} default. {fieldMismatches.base}</div>}
                    </div>
                  </div>}

                  {/* Generate Transmittal Letter — nested inside Transmittal Letter section */}
                  <div className="transmittal-generator">
                    <div className="transmittal-generator-header" style={{cursor:'pointer'}} onClick={() => setShowCoverLetterFields(!showCoverLetterFields)}>
                      <div className="card-header-main">
                        <div className="card-icon card-icon-label" aria-hidden="true"><Mail size={20} /></div>
                        <div className="card-title-section">
                          <h3 style={{margin:0,fontSize:'0.95rem'}}>Generate Transmittal Letter <span style={{fontSize:'0.75rem',fontWeight:400,color:'var(--text-secondary)'}}>(optional)</span></h3>
                          <p className="card-description" style={{margin:'0.2rem 0 0'}}>{showCoverLetterFields ? 'Fill in cover letter details to generate a standalone transmittal letter.' : 'Click to expand and generate a ready-to-send cover letter.'}</p>
                        </div>
                      </div>
                      <span style={{color:'var(--text-primary)',transition:'transform 0.2s',transform: showCoverLetterFields ? 'rotate(180deg)' : 'rotate(0deg)',display:'inline-flex'}} aria-hidden="true"><ChevronDown size={18} /></span>
                  </div>
                  {showCoverLetterFields && (
                    <div className="card-body">
                      <p style={{fontSize:'0.78rem',color:STATUS.neutral.textMuted,margin:'0 0 0.85rem'}}>
                        Enter only the contact and recipient details you want printed in the transmittal letter.
                      </p>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:'0.75rem'}}>
                        <div>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>PM Name (signer)</label>
                          <input type="text" value={pmName} onChange={e => setPmName(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>PM Title</label>
                          <input type="text" value={pmTitle} onChange={e => setPmTitle(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>PM Phone</label>
                          <input type="text" value={pmPhone} onChange={e => setPmPhone(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>PM Email</label>
                          <input type="text" value={pmEmail} onChange={e => setPmEmail(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Company Name</label>
                          <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Recipient (greeting)</label>
                          <input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div style={{gridColumn:'1 / -1'}}>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Recipient Address</label>
                          <textarea value={recipientAddress} onChange={e => setRecipientAddress(e.target.value)} className="project-select" style={{width:'100%',minHeight:'2.5rem',resize:'vertical'}} />
                        </div>
                        <div style={{gridColumn:'1 / -1'}}>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Reference Line</label>
                          <input type="text" value={referenceLine} onChange={e => setReferenceLine(e.target.value)} className="project-select" style={{width:'100%'}} />
                        </div>
                        <div style={{gridColumn:'1 / -1'}}>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Program Description (for body text)</label>
                          <textarea value={programDescription} onChange={e => setProgramDescription(e.target.value)} className="project-select" style={{width:'100%',minHeight:'3rem',resize:'vertical'}} />
                          <p style={{fontSize:'0.72rem',color:STATUS.neutral.textMuted,margin:'0.3rem 0 0'}}>Used in the body: "Please find enclosed the MSR summarizing accomplishments in support of [this text]."</p>
                        </div>
                        <div style={{gridColumn:'1 / -1'}}>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Contact Line (full sentence for body)</label>
                          <textarea value={contactLine} onChange={e => setContactLine(e.target.value)} className="project-select" style={{width:'100%',minHeight:'2.5rem',resize:'vertical'}} />
                          <p style={{fontSize:'0.72rem',color:STATUS.neutral.textMuted,margin:'0.3rem 0 0'}}>Replaces the "If you have technical questions..." paragraph. Include all contacts here.</p>
                        </div>
                        <div style={{gridColumn:'1 / -1'}}>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Submitted To (government recipients)</label>
                          <textarea value={submittedTo} onChange={e => setSubmittedTo(e.target.value)} className="project-select" style={{width:'100%',minHeight:'3rem',resize:'vertical'}} />
                        </div>
                        <div style={{gridColumn:'1 / -1'}}>
                          <label style={{display:'block',fontSize:'0.8rem',fontWeight:600,marginBottom:'0.25rem',color:'var(--text-secondary)'}}>Prepared By (your team)</label>
                          <textarea value={preparedBy} onChange={e => setPreparedBy(e.target.value)} className="project-select" style={{width:'100%',minHeight:'3rem',resize:'vertical'}} />
                        </div>
                      </div>
                      <div style={{marginTop:'1rem',display:'flex',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
                        <button
                          onClick={handleGenerateTransmittal}
                          disabled={transmittalGenerating || !letterDate}
                          className="btn btn-primary"
                        >
                          {transmittalGenerating ? (
                            <><span className="spinner"></span> Generating...</>
                          ) : (
                            <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><Mail size={16} aria-hidden="true" /> Generate Transmittal Letter</span>
                          )}
                        </button>
                        {transmittalDownloadUrl && (
                          <a
                            href={transmittalDownloadUrl}
                            download
                            className="btn btn-confirm"
                            style={{textDecoration:'none'}}
                          >
                            <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><Download size={16} aria-hidden="true" /> Download Transmittal Letter</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                    </div>
                  )}
                </div>
              )}

              {/* Input Document Date Mismatch Warning */}
              {inputDateWarning && projectConfirmed && (
                <div style={{background:STATUS.warning.bgStrong,border:`1px solid ${STATUS.warning.solid}`,borderRadius:'8px',padding:'0.75rem 1rem',marginBottom:'1rem'}}>
                  <strong style={{color:STATUS.warning.text,display:'inline-flex',alignItems:'center',gap:'6px'}}><AlertTriangle size={14} aria-hidden="true" /> Date Mismatch Detected</strong>
                  <p style={{margin:'0.25rem 0 0',fontSize:'0.85rem',color:STATUS.warning.textStrong}}>{inputDateWarning}</p>
                </div>
              )}

              {/* Upload row: Inputs + Template side-by-side */}
              <div className="upload-grid">
              {/* Input Documents Card */}
              <div className={`card ${inputsUploaded ? 'card-success' : ''} ${!projectConfirmed ? 'card-disabled' : ''}`}>
                <div className="card-header">
                  <div className="card-header-main">
                    <div className="card-icon card-icon-label" aria-hidden="true"><FolderOpen size={20} /></div>
                    <div className="card-title-section">
                      <h2>Upload Input Documents</h2>
                      <p className="card-description">Select all team input documents</p>
                    </div>
                  </div>
                  {inputsUploaded && <span className="badge success">Uploaded</span>}
                </div>
                
                <div className="card-body">
                  {inputFiles.length === 0 ? (
                    <label
                      className={`file-drop-zone ${isDraggingOverInputs ? 'drag-over' : ''}`}
                      {...inputsDropHandlers}
                    >
                      <input
                        type="file"
                        multiple
                        accept=".docx"
                        onChange={handleInputFilesSelect}
                        disabled={inputsUploading || processing}
                        className="hidden-input"
                      />
                      <div className="drop-zone-content">
                        <span className="drop-zone-icon" aria-hidden="true"><FolderOpen size={22} /></span>
                        <span className="drop-zone-text">Click or drag to select input documents</span>
                        <span className="drop-zone-hint">Multiple files allowed • .docx only</span>
                      </div>
                    </label>
                  ) : (
                    <div
                      className={`files-section ${isDraggingOverInputs ? 'drag-over' : ''}`}
                      {...inputsDropHandlers}
                    >
                      <div className="files-header">
                        <h3>Selected Documents ({inputFiles.length})</h3>
                      </div>
                      
                      <div className="file-list">
                        {inputFiles.map((file, index) => (
                          <div key={index} className="file-item">
                            <div className="file-item-icon" aria-hidden="true"><FileText size={18} /></div>
                            <div className="file-item-info">
                              <span className="file-item-name">{file.name}</span>
                              <span className="file-item-size">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                            {uploadProgress[file.name] !== undefined && (
                              <div className="file-progress">
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  aria-valuenow={uploadProgress[file.name]}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-label={`Uploading ${file.name}`}
                                >
                                  <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress[file.name]}%` }}
                                  />
                                </div>
                                <span className="progress-text">{uploadProgress[file.name]}%</span>
                              </div>
                            )}
                            {!inputsUploading && !processing && (
                              <button
                                onClick={async () => {
                                  resetValidationState();
                                  // Delete from S3 if already uploaded
                                  if (inputsUploaded) {
                                    try {
                                      const month = getActiveUploadMonth();
                                      await apiPost('/delete-file', {
                                        key: `${projectName}/${year}/${month}/input/${file.name.replace(/ /g, '_')}`,
                                      });
                                    } catch (e) {
                                      console.warn('S3 delete failed:', e);
                                      setError(`Could not remove ${file.name} from uploaded inputs: ${e.message}`);
                                      return;
                                    }
                                  }
                                  const remaining = inputFiles.filter((_, i) => i !== index);
                                  setInputFiles(remaining);
                                  if (remaining.length === 0) {
                                    setInputsUploaded(false);
                                    setUploadProgress({});
                                    setInputUploadMode('replace');
                                  }
                                }}
                                style={{marginLeft:'0.5rem',background:'none',border:'none',cursor:'pointer',fontSize:'1rem',color:'var(--text-secondary)',padding:'0.25rem'}}
                                title="Remove this file"
                              >✕</button>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {inputsUploaded && (
                        <div className="success-message">
                          <div className="success-message-actions success-message-split">
                            <div className="success-message-top">
                              <span className="success-icon" aria-hidden="true"><ShieldCheck size={18} /></span>
                              <span>All documents uploaded successfully!</span>
                            </div>
                            <div className="success-message-actions">
                              <input
                                ref={inputFilePickerRef}
                                type="file"
                                multiple
                                accept=".docx"
                                onChange={handleInputFilesSelect}
                                disabled={inputsUploading || processing}
                                className="hidden-input"
                              />
                              <button
                                className="btn btn-success-compact"
                                onClick={() => inputFilePickerRef.current?.click()}
                                disabled={processing}
                              >
                                Add More Inputs
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {inputFiles.length > 0 && (
                        <div className="card-actions upload-card-actions">

                          {!inputsUploaded && (
                            <button
                              onClick={handleInputsUpload}
                              disabled={inputsUploading}
                              className="btn btn-primary"
                            >
                              {inputsUploading ? (
                                <>
                                  <span className="spinner"></span>
                                  Uploading {inputFiles.length} Files...
                                </>
                              ) : (
                                inputUploadMode === 'append'
                                  ? `Upload All ${inputFiles.length} Selected Document${inputFiles.length > 1 ? 's' : ''}`
                                  : `Upload ${inputFiles.length} Document${inputFiles.length > 1 ? 's' : ''}`
                              )}
                            </button>
                          )}
                          <button
                            className="btn btn-primary"
                            onClick={clearSelectedInputs}
                            disabled={inputsUploading || processing}
                          >
                            Clear All Inputs
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Upload Card */}
              <div className={`card ${templateUploaded ? 'card-success' : ''} ${!projectConfirmed ? 'card-disabled' : ''}`}>
                <div className="card-header">
                  <div className="card-header-main">
                    <div className="card-icon card-icon-label" aria-hidden="true"><FileText size={20} /></div>
                    <div className="card-title-section">
                      <h2>Upload MSR Template</h2>
                      <p className="card-description">Select your MSR template document</p>
                    </div>
                  </div>
                  {templateUploaded && <span className="badge success">Uploaded</span>}
                </div>
                
                <div className="card-body">
                  {!templateFile ? (
                    <label
                      className={`file-drop-zone ${isDraggingOverTemplate ? 'drag-over' : ''}`}
                      {...templateDropHandlers}
                    >
                      <input
                        type="file"
                        accept=".docx"
                        onChange={handleTemplateSelect}
                        disabled={templateUploading || processing}
                        className="hidden-input"
                      />
                      <div className="drop-zone-content">
                        <span className="drop-zone-icon" aria-hidden="true"><FileText size={22} /></span>
                        <span className="drop-zone-text">Click or drag to select template file</span>
                        <span className="drop-zone-hint">.docx only</span>
                      </div>
                    </label>
                  ) : (
                    <div
                      className={`template-card-body ${isDraggingOverTemplate ? 'drag-over' : ''}`}
                      {...templateDropHandlers}
                    >
                      <div className="file-item">
                        <div className="file-item-icon" aria-hidden="true"><FileText size={18} /></div>
                        <div className="file-item-info">
                          <span className="file-item-name">{templateFile.name}</span>
                          <span className="file-item-size">
                            {(templateFile.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                        {!templateUploading && !processing && (
                          <button
                            onClick={clearSelectedTemplate}
                            style={{marginLeft:'0.5rem',background:'none',border:'none',cursor:'pointer',fontSize:'1rem',color:'var(--text-secondary)',padding:'0.25rem'}}
                            title="Remove this file"
                          >✕</button>
                        )}
                      </div>
                      {templateUploading && (
                        <div className="upload-progress">
                          <div
                            className="progress-bar is-indeterminate"
                            role="progressbar"
                            aria-label="Uploading template"
                          >
                            <div className="progress-fill" />
                          </div>
                        </div>
                      )}
                      {templateUploaded && (
                        <div className="success-message">
                          <div className="success-message-actions success-message-split">
                            <div className="success-message-top">
                              <span className="success-icon" aria-hidden="true"><ShieldCheck size={18} /></span>
                              <span>Template uploaded successfully!</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {templateFile && (
                        <div className="card-actions upload-card-actions">
                          {!templateUploaded && (
                            <button
                              onClick={handleTemplateUpload}
                              disabled={templateUploading}
                              className="btn btn-primary"
                            >
                              {templateUploading ? (
                                <>
                                  <span className="spinner"></span>
                                  Uploading...
                                </>
                              ) : (
                                'Upload Template'
                              )}
                            </button>
                          )}
                          <button
                            className="btn btn-primary"
                            onClick={clearSelectedTemplate}
                            disabled={templateUploading || processing}
                          >
                            Clear Template
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </div>

              {/* Process Button Card */}
              <div className={`card ${downloadUrl ? 'card-success' : ''} ${(!inputsUploaded || !templateUploaded) ? 'card-disabled' : ''}`}>
                <div className="card-header">
                  <div className="card-header-main">
                    <div className="card-icon card-icon-label" aria-hidden="true"><Rocket size={20} /></div>
                    <div className="card-title-section">
                      <h2>Generate MSR Report</h2>
                      <p className="card-description">Combine template with input documents</p>
                    </div>
                  </div>
                </div>
                
                <div className="card-body">
                  {!processing && !downloadUrl && (
                    <>
                      <div className="ready-checklist">
                        <div className={`checklist-item ${templateUploaded ? 'completed' : ''}`}>
                          <span className="checklist-icon" aria-hidden="true">{templateUploaded ? <ShieldCheck size={16} /> : <Info size={16} />}</span>
                          <span className="checklist-text">Template uploaded</span>
                        </div>
                        <div className={`checklist-item ${inputsUploaded ? 'completed' : ''}`}>
                          <span className="checklist-icon" aria-hidden="true">{inputsUploaded ? <ShieldCheck size={16} /> : <Info size={16} />}</span>
                          <span className="checklist-text">Input documents uploaded</span>
                        </div>
                      </div>
                      
                      <div className="matching-mode-control">
                        <span className="matching-mode-label">Matching mode</span>
                        <div className="matching-mode-segmented" role="group" aria-label="Matching mode">
                          {matchingModeOptions.map(opt => {
                            const active = templateModeOverride === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                title={opt.tooltip}
                                aria-pressed={active}
                                className={`matching-mode-option${active ? ' active' : ''}`}
                                onClick={() => {
                                  setTemplateModeOverride(opt.value);
                                  try { localStorage.setItem('msr_template_mode', opt.value); } catch { /* Persistence is best-effort. */ }
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="matching-mode-help">
                          Hover each option for details. Most projects work with <strong>Auto-detect</strong>; switch only if the generated report puts content in the wrong place.
                        </p>
                      </div>

                      <div className="field-block">
                        <label className="field-label" htmlFor="expected-roster">
                          Expected Roster (one name per line, optional)
                        </label>
                        <textarea
                          id="expected-roster"
                          value={rosterText}
                          onChange={e => {
                            resetValidationState();
                            setRosterText(e.target.value);
                            if (!rosterStorageKey) return;
                            try {
                              if (e.target.value) {
                                localStorage.setItem(rosterStorageKey, e.target.value);
                              } else {
                                localStorage.removeItem(rosterStorageKey);
                              }
                            } catch {
                              // Roster persistence is best-effort.
                            }
                          }}
                          placeholder="Enter one name per line (e.g. Jane Smith)"
                          rows={4}
                          className="roster-input"
                        />
                        <p className="field-hint">Used to verify everyone in the roster submitted. Names matched against filenames.</p>
                      </div>
                      <button
                        onClick={async () => {
                          setValidating(true);
                          setValidationResult(null);
                          setDuplicateOverride(false);
                          setShowDuplicateDetails(false);
                          try {
                            const month = getActiveUploadMonth();
                            const rosterList = rosterText.split('\n').map(s => s.trim()).filter(Boolean);
                            const result = await apiPost('/validate-submissions', { projectName, year, month, roster: rosterList, rosterMode: 'auto' });
                            setValidationResult(result);
                            if (result && (result.perFile || []).length > 0) {
                              setShowConfirmModal(true);
                            }
                          } catch (err) {
                            setError('Validation failed: ' + err.message);
                          }
                          setValidating(false);
                        }}
                        disabled={!templateUploaded || !inputsUploaded || processing || validating}
                        className="btn btn-confirm btn-large btn-full"
                      >
                        {validating ? 'Validating...' : 'Validate & Confirm Inputs'}
                      </button>

                      {validationResult && (
                        <div style={{marginTop:'0.85rem',marginBottom:'1rem',textAlign:'left'}}>
                          <div
                            style={{
                              background: validationReadiness.tone.background,
                              border: `1px solid ${validationReadiness.tone.border}`,
                              borderRadius: '8px',
                              padding: '0.7rem 0.9rem',
                              marginBottom: '0.75rem',
                              color: validationReadiness.tone.text,
                            }}
                          >
                            <div style={{display:'flex',justifyContent:'space-between',gap:'0.75rem',alignItems:'flex-start',flexWrap:'wrap'}}>
                              <div>
                                <div style={{fontSize:'0.72rem',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',marginBottom:'0.3rem'}}>
                                  Preflight Status
                                </div>
                                <div style={{fontSize:'0.92rem',fontWeight:700,marginBottom:'0.3rem'}}>
                                  {validationReadiness.label}
                                </div>
                                <div style={{fontSize:'0.8rem',lineHeight:1.4,maxWidth:'48rem'}}>
                                  {validationReadiness.message}
                                </div>
                                {validationPresentation?.guidanceMessage && (
                                  <div style={{marginTop:'0.45rem',padding:'0.45rem 0.6rem',borderRadius:'6px',background:'rgba(255,255,255,0.55)',border:`1px solid ${validationReadiness.tone.border}`,fontSize:'0.78rem',lineHeight:1.45,maxWidth:'48rem'}}>
                                    <strong>{validationPresentation.guidanceTitle}.</strong> {validationPresentation.guidanceMessage}
                                  </div>
                                )}
                                <div style={{display:'flex',gap:'0.8rem',flexWrap:'wrap',marginTop:'0.5rem',fontSize:'0.76rem'}}>
                                  <span><strong>Period:</strong> {year}-{activeUploadMonth}</span>
                                  <span><strong>Mode:</strong> {validationResult.summary.mode === 'heading' ? 'Exact Heading' : 'PID Match'}</span>
                                  <span><strong>Input files:</strong> {(validationResult.summary.inputFiles || []).length}</span>
                                  <span><strong>Template roster:</strong> {validationPresentation?.templateHasRoster ? 'Present' : 'Not present'}</span>
                                  {validationPresentation?.usesInputConfirmedRoster ? (
                                    <span><strong>Confirmed from inputs:</strong> {validationPresentation.inputConfirmedCount}</span>
                                  ) : (
                                    <>
                                      <span><strong>Contributor slots:</strong> {validationResult.summary.contributorPositions || 0}</span>
                                      <span><strong>Template internal:</strong> {validationResult.summary.templateInternalPositions || 0}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div style={{display:'flex',gap:'0.35rem',flexWrap:'wrap',alignItems:'center'}}>
                                <button
                                  type="button"
                                  onClick={handleCopyValidationSummary}
                                  title="Copy validation status"
                                  style={{background:'white',color:validationReadiness.tone.text,border:`1px solid ${validationReadiness.tone.border}`,borderRadius:'999px',padding:'0.28rem 0.6rem',fontSize:'0.72rem',fontWeight:700,cursor:'pointer'}}
                                >
                                  Copy status
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDownloadValidationCsv}
                                  title="Export roster status as CSV"
                                  style={{background:'white',color:validationReadiness.tone.text,border:`1px solid ${validationReadiness.tone.border}`,borderRadius:'999px',padding:'0.28rem 0.6rem',fontSize:'0.72rem',fontWeight:700,cursor:'pointer'}}
                                >
                                  CSV
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDownloadValidationJson}
                                  title="Export validation details as JSON"
                                  style={{background:'white',color:validationReadiness.tone.text,border:`1px solid ${validationReadiness.tone.border}`,borderRadius:'999px',padding:'0.28rem 0.6rem',fontSize:'0.72rem',fontWeight:700,cursor:'pointer'}}
                                >
                                  JSON
                                </button>
                              </div>
                            </div>
                          </div>
                          {(validationResult.llmFailureCount || 0) > 0 && (() => {
                            const failed = validationResult.llmFailureCount;
                            const totalLlm = (validationResult.llmPerFile || []).length || failed;
                            const allFailed = failed >= totalLlm;
                            const tone = allFailed
                              ? {bg:STATUS.danger.bg, border:STATUS.danger.border, text:STATUS.danger.text}
                              : {bg:STATUS.warning.bgStrong, border:STATUS.warning.border, text:STATUS.warning.text};
                            const headline = allFailed
                              ? 'Name detection was incomplete for all input file(s).'
                              : `Name detection was incomplete for ${failed} of ${totalLlm} input file(s).`;
                            return (
                              <div style={{background:tone.bg,border:`1px solid ${tone.border}`,borderRadius:'8px',padding:'0.6rem 0.85rem',marginBottom:'0.75rem',color:tone.text,fontSize:'0.82rem'}}>
                                <div style={{fontWeight:700,marginBottom:'0.2rem'}}>{headline}</div>
                                <div>Some contributors may be missing from the table. Re-run validate, or contact support if it persists.</div>
                              </div>
                            );
                          })()}
                          <div style={{display:'flex',gap:'1rem',marginBottom:'0.75rem',flexWrap:'wrap'}}>
                            <span style={{background:STATUS.success.bgStrong,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.success.text}}>{validationPresentation?.submittedCount ?? validationResult.summary.submitted} {validationPresentation?.submittedLabel || 'Submitted'}</span>
                            {validationPresentation?.usesInputConfirmedRoster && <span style={{background:STATUS.info.bgStrong,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.info.text}}>{validationPresentation.inputConfirmedCount} Contributors confirmed</span>}
                            {validationResult.summary.missing > 0 && <span style={{background:STATUS.danger.bg,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.danger.text}}>{validationResult.summary.missing} Missing</span>}
                            {(validationResult.summary.nonSubmission || 0) > 0 && (
                              <span
                                title="People whose section explicitly declares no submission this period (NSTR, on leave, no update, etc.), detected from the input doc text."
                                style={{background:STATUS.warning.bg,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.warning.text,border:`1px solid ${STATUS.warning.border}`}}
                              >
                                {validationResult.summary.nonSubmission} Declared No Submission
                              </span>
                            )}
                            {validationPresentation?.templateHasRoster ? (
                              <span style={{background:STATUS.neutral.border,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.neutral.text}}>{validationResult.summary.vacant} Vacant</span>
                            ) : (
                              <span style={{background:STATUS.info.bg,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.info.text}}>No template roster</span>
                            )}
                            {validationResult.summary.duplicates > 0 && <span style={{background:STATUS.warning.bgStrong,padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,color:STATUS.warning.text}}>{validationResult.summary.duplicates} Potential Duplicates</span>}
                          </div>
                          {validationPresentation?.usesInputConfirmedRoster ? (
                            <div style={{maxHeight:'250px',overflowY:'auto',border:'1px solid var(--border)',borderRadius:'8px',fontSize:'0.8rem'}}>
                              <table style={{width:'100%',borderCollapse:'collapse'}}>
                                <thead><tr style={{background:STATUS.neutral.surface,position:'sticky',top:0}}>
                                  <th style={{padding:'0.4rem 0.6rem',textAlign:'left',borderBottom:`1px solid ${STATUS.neutral.border}`}}>Heading / Entry</th>
                                  <th style={{padding:'0.4rem 0.6rem',textAlign:'left',borderBottom:`1px solid ${STATUS.neutral.border}`}}>Confirmed from inputs</th>
                                  <th style={{padding:'0.4rem 0.6rem',textAlign:'center',borderBottom:`1px solid ${STATUS.neutral.border}`}}>Source</th>
                                </tr></thead>
                                <tbody>
                                  {validationPresentation.inputConfirmedContributors.map(row => (
                                    <tr key={`${row.pid || row.header || row.source}`} style={{background:'white'}}>
                                      <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`}}>
                                        <div style={{fontFamily:'monospace',fontSize:'0.75rem',color:STATUS.neutral.textStrong}}>{row.pid || 'Header match'}</div>
                                        {row.header && <div style={{fontSize:'0.74rem',color:STATUS.neutral.textMuted,marginTop:'0.1rem'}}>{row.header}</div>}
                                      </td>
                                      <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`}}>
                                        {row.personName || <span style={{color:STATUS.neutral.textMuted}}>Confirmed from uploaded header</span>}
                                      </td>
                                      <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`,textAlign:'center'}}>
                                        <span style={{color:STATUS.success.solid,fontWeight:600}}>{row.source || 'Input file'}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div style={{maxHeight:'250px',overflowY:'auto',border:'1px solid var(--border)',borderRadius:'8px',fontSize:'0.8rem'}}>
                              {(() => {
                                // When the dataset has no real per-PID slots
                                // (e.g. F-16, EPASS Task 79), every row's
                                // pid is a synthesized key like
                                // "file:input_doc-6-1" or
                                // "llm:input_doc-6-3.docx:stephen denier".
                                // Showing those in a column labelled "PID"
                                // is misleading and ugly. Detect whether any
                                // row has a real PID and swap the column
                                // header + cell content accordingly.
                                //
                                // F-16 also uses a filename convention like
                                // "001-position-id-41-adam-osting...docx"
                                // where the meaningful ID lives in the
                                // filename. Extract that and show it as the
                                // ID column when no real PIDs exist.
                                // Synthetic-pid detector. The backend uses
                                // pid prefixes like "file:", "llm:", or
                                // "person:" (Task 79 stable_person_key) for
                                // rows that do NOT carry a meaningful real
                                // PID. The ID column should ignore those.
                                const isSyntheticPidString = (pid) => {
                                  const s = String(pid || '');
                                  return s.startsWith('file:') || s.startsWith('llm:') || s.startsWith('person:');
                                };
                                const hasRealPids = (validationResult.roster || []).some(
                                  r => r.pid && !isSyntheticPidString(r.pid)
                                );
                                const extractPositionId = (filename) => {
                                  if (!filename) return '';
                                  const match = String(filename).match(/position[-_\s]?id[-_\s]?(\d+)/i);
                                  return match ? match[1] : '';
                                };
                                // Display priority for the ID column:
                                //   1. r.positionId (PWS / PID / Position ID
                                //      extracted by backend or LLM)
                                //   2. r.pid if it's a real (non-synthetic) PID
                                //   3. position-id-NN from filename
                                //   4. source filename
                                const resolveDisplayId = (r) => {
                                  if (!r) return '';
                                  if (r.positionId) return r.positionId;
                                  if (r.pid && !isSyntheticPidString(r.pid)) return r.pid;
                                  return extractPositionId(r.source);
                                };
                                const anyDisplayIds = (validationResult.roster || []).some(r => resolveDisplayId(r));
                                const firstColLabel = anyDisplayIds ? 'ID' : 'Input File';
                                const filenameIdsAvailable = anyDisplayIds && !hasRealPids;
                                return (
                                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                                    {/* Width hints: keep ID narrow so the
                                        filename-derived numerics don't push
                                        Person off-screen. Word Count and
                                        Match are fixed-width too. */}
                                    <colgroup>
                                      <col style={{width: hasRealPids ? '110px' : (filenameIdsAvailable ? '70px' : '220px')}} />
                                      <col />
                                      <col style={{width:'95px'}} />
                                      <col style={{width:'95px'}} />
                                    </colgroup>
                                    <thead><tr style={{background:STATUS.neutral.surface,position:'sticky',top:0}}>
                                      <th style={{padding:'0.4rem 0.6rem',textAlign:'left',borderBottom:`1px solid ${STATUS.neutral.border}`}}>{firstColLabel}</th>
                                      <th style={{padding:'0.4rem 0.6rem',textAlign:'left',borderBottom:`1px solid ${STATUS.neutral.border}`}}>Person</th>
                                      <th style={{padding:'0.4rem 0.6rem',textAlign:'center',borderBottom:`1px solid ${STATUS.neutral.border}`}}>Word Count</th>
                                      <th style={{padding:'0.4rem 0.6rem',textAlign:'center',borderBottom:`1px solid ${STATUS.neutral.border}`}}>Match</th>
                                    </tr></thead>
                                    <tbody>
                                    {validationResult.roster.map(r => {
                                      const displayId = resolveDisplayId(r);
                                      const firstColValue = displayId || r.source || '';
                                      return (
                                  <tr key={r.pid} style={{background: r.duplicate ? STATUS.warning.bgStrong : r.vacant ? STATUS.neutral.surface : r.nonSubmission ? STATUS.warning.bg : r.nstr ? STATUS.neutral.surface : !r.submitted ? STATUS.danger.bg : 'white'}}>
                                    <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`,fontFamily:'monospace',fontSize:'0.75rem',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{firstColValue}</td>
                                    <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`}}>{r.personName || (r.header || '').split('(PID')[0].split('\t').pop().trim().substring(0, 50)}</td>
                                    <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`,textAlign:'center'}}>
                                      {r.vacant ? <span style={{color:STATUS.neutral.textMuted}}>Vacant</span> :
                                       r.nonSubmission ? <span style={{color:STATUS.warning.text,fontWeight:600}} title="This person's section explicitly declares no submission this period.">No Submission</span> :
                                       r.nstr ? <span style={{color:STATUS.neutral.textMuted,fontStyle:'italic'}}>NTR</span> :
                                       r.submitted ? <span style={{color:STATUS.success.solid}}>{r.wordCount} words</span> :
                                       <span style={{color:STATUS.danger.solid,fontWeight:600}}>MISSING</span>}
                                    </td>
                                    <td style={{padding:'0.3rem 0.6rem',borderBottom:`1px solid ${STATUS.neutral.surfaceAlt}`,textAlign:'center'}}>
                                      {r.duplicate ? (
                                        <span style={{color:STATUS.warning.text,display:'inline-flex',alignItems:'center',gap:'3px'}}>
                                          <AlertTriangle size={12} aria-hidden="true" /> {r.duplicate.similarity}%
                                        </span>
                                      ) : <span style={{color:STATUS.neutral.borderStrong}}>—</span>}
                                    </td>
                                  </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                );
                              })()}
                            </div>
                          )}
                          {validationPresentation?.usesInputConfirmedRoster && (
                            <div style={{background:STATUS.info.bg,borderRadius:'6px',padding:'0.5rem 0.75rem',marginTop:'0.5rem',fontSize:'0.8rem',color:STATUS.info.text,border:`1px solid ${STATUS.info.border}`}}>
                              <strong>Input-confirmed view.</strong> Because this Exact Header template does not contain a formal roster, the table above shows the contributors or headers we could confirm directly from the uploaded input documents.
                            </div>
                          )}
                          {!validationPresentation?.usesInputConfirmedRoster && validationResult.summary.missing > 0 && (
                            <div style={{background:STATUS.danger.bg,borderRadius:'6px',padding:'0.5rem 0.75rem',marginTop:'0.5rem',fontSize:'0.8rem',color:STATUS.danger.text}}>
                              <strong>{validationResult.summary.missing} personnel have no submission.</strong> Report will have empty entries for these positions.
                            </div>
                          )}
                          {(validationResult.parseWarnings || []).length > 0 && (
                            <div style={{background:STATUS.danger.bg,borderRadius:'6px',padding:'0.5rem 0.75rem',marginTop:'0.5rem',fontSize:'0.8rem',color:STATUS.danger.text,border:`1px solid ${STATUS.danger.border}`}}>
                              <strong>{validationResult.parseWarnings.length} input file(s) could not be parsed.</strong> They will not be included in the report. Replace them with valid .docx files and re-validate. If a file was renamed from .doc to .docx, open it in Word and save it as a true .docx first.
                              <ul style={{margin:'0.4rem 0 0 1.2rem',padding:0}}>
                                {validationResult.parseWarnings.map((w, i) => (
                                  <li key={i} style={{fontFamily:'monospace',fontSize:'0.75rem'}}>{w}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {validationResult.summary.duplicates > 0 && (() => {
                            const dupRows = validationDuplicateEntries;
                            return (
                              <div style={{background:STATUS.warning.bgStrong,borderRadius:'6px',padding:'0.5rem 0.75rem',marginTop:'0.5rem',fontSize:'0.8rem',color:STATUS.warning.text}}>
                                <strong>{validationResult.summary.duplicates} entr{validationResult.summary.duplicates===1?'y':'ies'} match previous month &gt;80%.</strong> These may be copy-pasted from last month's report.
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => setShowDuplicateDetails(s => !s)}
                                  style={{marginLeft:'0.5rem',minHeight:'32px',padding:'0.2rem 0.7rem',fontSize:'0.75rem'}}
                                >
                                  {showDuplicateDetails ? 'Hide details' : `Show what's duplicated (${dupRows.length})`}
                                </button>
                                {showDuplicateDetails && (
                                  <div style={{marginTop:'0.6rem',display:'flex',flexDirection:'column',gap:'0.6rem'}}>
                                    {dupRows.map(r => {
                                      const d = r;
                                      return (
                                        <div key={`${r.pid || 'file'}-${d.previousMonth}-${d.source || 'source'}`} style={{background:STATUS.warning.bg,border:`1px solid ${STATUS.warning.border}`,borderRadius:'6px',padding:'0.5rem 0.7rem'}}>
                                          <div style={{fontWeight:700,marginBottom:'0.25rem'}}>
                                            Entry {r.pid} — {r.personName || 'unknown person'} <span style={{color:STATUS.warning.text}}>({d.similarity}% match)</span>
                                          </div>
                                          <div style={{fontSize:'0.72rem',color:STATUS.warning.text,marginBottom:'0.4rem'}}>
                                            From <code>{d.source || r.source || 'unknown file'}</code> — compared with {d.previousMonth}
                                          </div>
                                          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:'0.5rem',fontSize:'0.75rem'}}>
                                            <div>
                                              <div style={{fontWeight:600,color:STATUS.warning.textStrong,marginBottom:'0.15rem'}}>Last month ({d.previousMonth})</div>
                                              <div style={{background:'white',border:`1px solid ${STATUS.warning.border}`,borderRadius:'4px',padding:'0.4rem',whiteSpace:'pre-wrap',fontFamily:'monospace',color:STATUS.neutral.text,maxHeight:'120px',overflowY:'auto'}}>
                                                {d.previousPreview || '(no preview)'}
                                              </div>
                                            </div>
                                            <div>
                                              <div style={{fontWeight:600,color:STATUS.warning.textStrong,marginBottom:'0.15rem'}}>This month</div>
                                              <div style={{background:'white',border:`1px solid ${STATUS.warning.border}`,borderRadius:'4px',padding:'0.4rem',whiteSpace:'pre-wrap',fontFamily:'monospace',color:STATUS.neutral.text,maxHeight:'120px',overflowY:'auto'}}>
                                                {d.currentPreview || '(no preview)'}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {validationResult && validationResult.blocking && (
                        <div style={{background:STATUS.danger.bg,borderRadius:'6px',padding:'0.6rem 0.9rem',marginBottom:'0.5rem',fontSize:'0.85rem',color:STATUS.danger.text,border:`1px solid ${STATUS.danger.border}`}}>
                          <strong>Generate is blocked.</strong> {validationResult.blockingReason || 'One or more files are flagged as duplicates of previous submissions.'} Review the entries above; if they're acceptable, you can override the block.
                          <label style={{display:'flex',alignItems:'center',gap:'0.5rem',marginTop:'0.5rem',fontWeight:600,cursor:'pointer'}}>
                            <input
                              type="checkbox"
                              checked={duplicateOverride}
                              onChange={e => setDuplicateOverride(e.target.checked)}
                            />
                            I've reviewed the duplicate entries and want to proceed anyway
                          </label>
                        </div>
                      )}
                      <button
                        onClick={() => handleProcess()}
                        disabled={!templateUploaded || !inputsUploaded || processing || (validationResult && validationResult.blocking && !duplicateOverride)}
                        className="btn btn-gradient btn-large btn-full"
                        title="Run the consolidation pipeline. Contributor text is placed verbatim under each PID."
                      >
                        <span className="btn-icon" aria-hidden="true"><Rocket size={16} /></span>
                        Generate MSR Report
                      </button>
                    </>
                  )}

                  {processing && (
                    <div className="processing-status">
                      <div className="processing-animation">
                        <div className="spinner-large"></div>
                      </div>
                      <h3>Processing Your Report</h3>
                      <p className="status-description">
                        Analyzing documents and compiling the MSR report...
                      </p>
                      <div className={`status-badge status-${executionStatus.toLowerCase()}`}>
                        {executionStatus}
                      </div>
                      <p className="processing-hint">
                        This typically takes 2-5 minutes.
                      </p>
                    </div>
                  )}

                  {downloadUrl && (
                    <div className="success-content">
                      <div className="success-animation">
                        <span className="success-checkmark" aria-hidden="true"><ShieldCheck size={22} /></span>
                      </div>
                      {(() => {
                        // Split warnings into real warnings vs informational notes
                        const realWarnings = (processingWarnings || []).filter(w =>
                          !w.startsWith('template_mode=') &&
                          !w.startsWith('transmittal_pages_stripped=') &&
                          !w.startsWith('transmittal_strip_skipped=')
                        );
                        const notes = (processingWarnings || []).filter(w =>
                          w.startsWith('template_mode=') ||
                          w.startsWith('transmittal_pages_stripped=')
                        );
                        return <>
                          <h3>{realWarnings.length > 0 ? 'Report Generated with Warnings' : 'Report Generated Successfully!'}</h3>
                          <p>{realWarnings.length > 0 ? 'Your report was generated but may require manual review.' : 'Your Monthly Status Report is ready for download.'}</p>
                          {realWarnings.length > 0 && (
                            <div style={{background:STATUS.warning.bgStrong,border:`1px solid ${STATUS.warning.solid}`,borderRadius:'8px',padding:'0.75rem 1rem',marginBottom:'1rem',textAlign:'left'}}>
                              <strong style={{color:STATUS.warning.text}}>Warnings — manual review recommended:</strong>
                              <ul style={{margin:'0.5rem 0 0 1rem',fontSize:'0.85rem',color:STATUS.warning.textStrong}}>
                                {realWarnings.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          {notes.length > 0 && (
                            <div style={{fontSize:'0.75rem',color:'var(--text-secondary)',marginBottom:'0.5rem'}}>
                              {notes.map((n, i) => <span key={i} style={{marginRight:'1rem',display:'inline-flex',alignItems:'center',gap:'4px'}}><Info size={12} aria-hidden="true" />{n}</span>)}
                            </div>
                          )}
                        </>;
                      })()}
                      {/* Document Preview */}
                      {previewLoading && (
                        <div style={{textAlign:'center',padding:'2rem',color:'var(--text-secondary)'}}>
                          <div className="spinner" style={{margin:'0 auto 0.5rem'}}></div>
                          Loading preview...
                        </div>
                      )}
                      {previewHtml && !previewLoading && (
                        <div className="preview-panel">
                          <div className="preview-toolbar">
                            <h4 className="preview-heading">Document Preview</h4>
                            <div className="preview-actions">
                              <button
                                onClick={() => setPreviewCollapsed(v => !v)}
                                className="btn btn-action btn-secondary"
                              >
                                {previewCollapsed ? 'Show Preview' : 'Hide Preview'}
                              </button>
                              <button
                                onClick={() => setPreviewFullscreen(true)}
                                className="btn btn-action btn-primary"
                              >
                                Expand Full Page
                              </button>
                            </div>
                          </div>
                          {!previewFullscreen && !previewCollapsed && (
                            <>
                              <div
                                className="msr-preview-content"
                                style={{
                                  border:'1px solid var(--border)',
                                  borderRadius:'8px',
                                  padding:'2rem',
                                  maxHeight:'400px',
                                  overflowY:'auto',
                                  background:'white',
                                  textAlign:'left',
                                  boxShadow:'0 2px 8px rgba(0,0,0,0.1)',
                                }}
                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                              />
                              <style>{`
                                .msr-preview-content { font-size: 0.85rem; line-height: 1.6; }
                                .msr-preview-content h1 { font-size: 1.1rem; margin: 1rem 0 0.5rem; }
                                .msr-preview-content h2 { font-size: 1rem; margin: 0.8rem 0 0.4rem; }
                                .msr-preview-content h3 { font-size: 0.95rem; margin: 0.6rem 0 0.3rem; }
                                .msr-preview-content h4, .msr-preview-content h5, .msr-preview-content h6 { font-size: 0.9rem; margin: 0.5rem 0 0.25rem; }
                                .msr-preview-content p { margin: 0.3rem 0; }
                                .msr-preview-content table { border-collapse: collapse; width: 100%; font-size: 0.8rem; margin: 0.5rem 0; }
                                .msr-preview-content td, .msr-preview-content th { border: 1px solid #ddd; padding: 0.3rem 0.5rem; }
                              `}</style>
                            </>
                          )}
                        </div>
                      )}

                      <div className="action-buttons">
                        <button onClick={handleDownload} className="btn btn-download btn-large">
                          <Download size={18} aria-hidden="true" />
                          Download MSR Report
                        </button>
                        <button onClick={resetApp} className="btn btn-secondary">
                          Process Another Report
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Fullscreen Preview Modal — portaled to body so the sticky App-header can't cover the Close button */}
              {previewFullscreen && previewHtml && createPortal(
                <div
                  onClick={(e) => { if (e.target === e.currentTarget) setPreviewFullscreen(false); }}
                  className="preview-fullscreen-overlay"
                >
                  <div className="preview-fullscreen-shell">
                    <div className="preview-fullscreen-header">
                      <div className="preview-fullscreen-title-group">
                        <span className="preview-fullscreen-title">MSR Report Preview</span>
                        <span className="preview-fullscreen-subtitle">Press Esc to close</span>
                      </div>
                      <div className="preview-fullscreen-actions">
                        <button onClick={handleDownload} className="btn btn-download btn-action">
                          <Download size={16} aria-hidden="true" />
                          Download .docx
                        </button>
                        <button onClick={() => setPreviewFullscreen(false)} className="btn btn-secondary btn-action">
                          Close Preview
                        </button>
                      </div>
                    </div>
                    <div className="msr-preview-fullscreen">
                      <div
                        className="msr-preview-content"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    </div>
                  </div>
                </div>,
                document.body
              )}

              {/* Per-File Confirmation Modal (Step 4) */}
              {showConfirmModal && validationResult && (
                <div style={{
                  position:'fixed', top:0, left:0, right:0, bottom:0,
                  background:'rgba(0,0,0,0.7)', zIndex:9998,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  padding:'2rem',
                }}>
                  <div style={{
                    background:'white', borderRadius:'10px', maxWidth:'960px', width:'100%',
                    maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden',
                  }}>
                    <div style={{padding:'1rem 1.5rem', background:STATUS.neutral.textStrong, color:'white', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <span style={{fontWeight:700, fontSize:'1.05rem'}}>Confirm Inputs Before Generating</span>
                      <button onClick={() => setShowConfirmModal(false)} style={{background:'transparent',color:'white',border:'1px solid white',borderRadius:'6px',padding:'0.3rem 0.9rem',cursor:'pointer',fontSize:'0.85rem'}}>Close</button>
                    </div>
                    <div style={{padding:'1rem 1.5rem', overflowY:'auto', flex:1}}>
                      {validationResult.blocking && (
                        <div style={{background:STATUS.danger.bg,borderRadius:'6px',padding:'0.75rem 1rem',marginBottom:'1rem',border:`1px solid ${STATUS.danger.border}`,color:STATUS.danger.text}}>
                          <strong>BLOCKING: </strong>{validationResult.blockingReason}. Generate is disabled until duplicates are resolved.
                        </div>
                      )}
                      {validationResult.rosterStatus && validationResult.rosterStatus.length > 0 && (
                        <div style={{marginBottom:'1rem'}}>
                          <h3 style={{fontSize:'0.95rem',fontWeight:700,margin:'0 0 0.5rem',color:STATUS.neutral.text}}>Roster Status</h3>
                          <div className="chip-row">
                            {validationResult.rosterStatus.map(rs => (
                              <span key={rs.name} style={{
                                padding:'0.3rem 0.7rem',borderRadius:'12px',fontSize:'0.8rem',fontWeight:600,
                                background: rs.submitted ? STATUS.success.bgStrong : STATUS.danger.bg,
                                color: rs.submitted ? STATUS.success.text : STATUS.danger.text,
                              }}>
                                {rs.submitted ? '✓ ' : '✗ '}{rs.name}{rs.file ? ` → ${rs.file}` : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <h3 style={{fontSize:'0.95rem',fontWeight:700,margin:'1rem 0 0.5rem',color:STATUS.neutral.text}}>
                        Per-File Detail ({(validationResult.perFile || []).length} files)
                      </h3>
                      {(validationResult.perFile || []).map((pf, idx) => (
                        <div key={idx} style={{
                          border:`1px solid ${STATUS.neutral.border}`, borderLeft:`4px solid ${pf.duplicate ? STATUS.danger.solid : pf.headingsFound.length > 0 || pf.pidsFound.length > 0 ? STATUS.success.solid : STATUS.warning.solid}`,
                          borderRadius:'6px', padding:'0.75rem 1rem', marginBottom:'0.6rem',
                        }}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.3rem'}}>
                            <strong style={{fontSize:'0.9rem',color:STATUS.neutral.textStrong}}>{pf.filename}</strong>
                            <div style={{display:'flex',gap:'0.4rem',flexWrap:'wrap'}}>
                              {pf.duplicate && <span style={{background:STATUS.danger.bg,color:STATUS.danger.text,padding:'0.15rem 0.5rem',borderRadius:'10px',fontSize:'0.7rem',fontWeight:700}}>DUPLICATE</span>}
                              {pf.rosterMatch && <span style={{background:STATUS.info.bgStrong,color:STATUS.info.text,padding:'0.15rem 0.5rem',borderRadius:'10px',fontSize:'0.7rem',fontWeight:600}}>roster: {pf.rosterMatch}</span>}
                              <span style={{background:STATUS.neutral.surfaceAlt,color:STATUS.neutral.text,padding:'0.15rem 0.5rem',borderRadius:'10px',fontSize:'0.7rem'}}>{pf.paragraphCount} paras</span>
                            </div>
                          </div>
                          {pf.headingsFound.length > 0 && (
                            <div style={{fontSize:'0.78rem',color:STATUS.neutral.text,marginBottom:'0.25rem'}}>
                              <strong>Headings:</strong> {pf.headingsFound.slice(0,5).join(' · ')}{pf.headingsFound.length > 5 ? ` (+${pf.headingsFound.length - 5})` : ''}
                            </div>
                          )}
                          {pf.pidsFound.length > 0 && (
                            <div style={{fontSize:'0.78rem',color:STATUS.neutral.text,marginBottom:'0.25rem'}}>
                              <strong>PIDs:</strong> {pf.pidsFound.slice(0,8).join(', ')}{pf.pidsFound.length > 8 ? ` (+${pf.pidsFound.length - 8})` : ''}
                            </div>
                          )}
                          {pf.contentPreview && (
                            <div style={{fontSize:'0.75rem',color:STATUS.neutral.textMuted,fontStyle:'italic',marginTop:'0.3rem',lineHeight:1.4}}>
                              {pf.contentPreview}{pf.contentPreview.length >= 300 ? '…' : ''}
                            </div>
                          )}
                          {pf.error && (
                            <div style={{fontSize:'0.75rem',color:STATUS.danger.solid,marginTop:'0.3rem'}}>Parse error: {pf.error}</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'0.75rem 1.5rem', background:STATUS.neutral.surface, borderTop:`1px solid ${STATUS.neutral.border}`, display:'flex', justifyContent:'flex-end', gap:'0.6rem'}}>
                      <button
                        className="btn"
                        onClick={() => setShowConfirmModal(false)}
                      >
                        Review Later
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => { setShowConfirmModal(false); }}
                        disabled={validationResult.blocking}
                      >
                        {validationResult.blocking ? 'Resolve Duplicates First' : 'Confirm & Continue'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="error-card">
                  <span className="error-icon" aria-hidden="true"><AlertTriangle size={18} /></span>
                  <span className="error-text">{error}</span>
                </div>
              )}
            </div>
          ) : activeTab === 'kb' ? (
            projectConfirmed ? (
              <div className="upload-container" style={{maxWidth: kbQueryResult && kbQueryResult.type === 'compare-period-similarity' ? 'min(1500px, 97vw)' : '1100px'}}>
                <div className="card">
                  <div className="card-header">
                    <div className="card-header-main">
                      <div className="card-icon card-icon-label" aria-hidden="true"><BookOpen size={20} /></div>
                      <div className="card-title-section">
                        <h2>Knowledge Base</h2>
                        <p className="card-description">Validate the cross-month manifest store and run operator queries</p>
                      </div>
                    </div>
                    {/* Both write/refresh rather than render info, so they live in the
                        header instead of the query row below. */}
                    <div className="card-header-actions">
                      <button
                        onClick={loadKbStatus}
                        disabled={kbLoading}
                        className="btn btn-action btn-confirm"
                        title="Refresh the current knowledge base status"
                      >
                        {kbLoading ? 'Loading...' : 'Refresh KB Status'}
                      </button>
                      <button
                        onClick={handleClearKbMonth}
                        disabled={kbClearing || !canRunMonthScopedKbQueries}
                        className="btn btn-action btn-flat-amber"
                        title={`Clear saved knowledge for ${activeKnowledgePeriod || 'the selected month'} — cannot be undone`}
                      >
                        {kbClearing ? 'Clearing...' : 'Clear Saved Month Data'}
                      </button>
                    </div>
                  </div>
                  <div
                    className="card-body kb-info"
                    style={{
                      '--kb-fill': STATUS.neutral.surface,
                      '--kb-edge': STATUS.info.bgStrong,
                      '--kb-label': STATUS.info.text,
                    }}
                  >
                    <div className="data-panel">
                      <div className="row-between">
                        <div>
                          <div className="micro-label">
                            Active Knowledge Filter
                          </div>
                          <div style={{fontSize:'0.95rem',fontWeight:700,color:STATUS.neutral.textStrong}}>
                            {projectName} · {activeKnowledgePeriod || 'No saved period'}
                          </div>
                          <div className="sub-note">
                            Knowledge uses saved submission periods for this project, even if they live in a different year than the current Process selection.
                          </div>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:'0.3rem',minWidth:'220px'}}>
                          <span className="micro-label">
                            Saved Period
                          </span>
                          <select
                            aria-label="Saved period"
                            value={kbSelectedPeriod}
                            onChange={(e) => setKbSelectedPeriod(e.target.value)}
                            className="project-select"
                            disabled={kbPeriodOptions.length === 0}
                            style={{minWidth:'220px'}}
                          >
                            {kbPeriodOptions.length === 0 ? (
                              <option value="">No saved periods</option>
                            ) : (
                              kbPeriodOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                    {kbPeriodWarning && (
                      <div className="data-panel" style={{background:STATUS.warning.bg,borderColor:STATUS.warning.border,color:STATUS.warning.text,fontSize:'0.8rem'}}>
                        No saved knowledge exists for <strong>{projectName}</strong> in <strong>{requestedKnowledgePeriod}</strong>. Knowledge has been redirected to <strong>{kbPeriodWarning.redirectedTo}</strong>, and you can choose any saved period from the dropdown above.
                      </div>
                    )}
                    {kbPeriodOptions.length === 0 && (
                      <div className="data-panel" style={{background:STATUS.warning.bg,borderColor:STATUS.warning.border,color:STATUS.warning.text,fontSize:'0.8rem'}}>
                        No saved knowledge periods were found for <strong>{projectName}</strong>. Generate or validate a report first, then come back here to inspect the saved data.
                      </div>
                    )}
                    {showCompletedMsrIngest && !knowledgeIngestTargetHasData && (
                    <div className="data-panel">
                      <div style={{display:'flex',justifyContent:'space-between',gap:'0.85rem',alignItems:'center',flexWrap:'wrap'}}>
                        <div>
                          <div className="micro-label">Completed MSR Ingest</div>
                          <div className="sub-note">
                            Add finished MSR documents directly into saved knowledge when a month did not come through the normal input/template flow yet.
                          </div>
                          <div style={{fontSize:'0.78rem',color:STATUS.neutral.text,marginTop:'0.3rem'}}>
                            Target period: <strong>{knowledgeIngestTargetLabel || 'Select a project/year/month first'}</strong>
                          </div>
                        </div>
                        <div style={{display:'flex',gap:'0.55rem',alignItems:'center',flexWrap:'wrap'}}>
                          <input
                            ref={completedMsrPickerRef}
                            type="file"
                            accept=".docx"
                            multiple
                            onChange={handleCompletedMsrSelect}
                            style={{display:'none'}}
                          />
                          <button
                            type="button"
                            onClick={() => completedMsrPickerRef.current?.click()}
                            className="btn btn-confirm"
                            style={{padding:'0.5rem 0.95rem',background:STATUS.info.solid,color:'white'}}
                          >
                            Choose Completed MSRs
                          </button>
                          <button
                            type="button"
                            onClick={handleCompletedMsrUpload}
                            disabled={completedMsrUploading || completedMsrFiles.length === 0 || !knowledgeIngestTargetPeriod}
                            className="btn btn-confirm"
                            style={{padding:'0.5rem 0.95rem',background:STATUS.success.solid,color:'white'}}
                          >
                            {completedMsrUploading ? 'Ingesting...' : `Add to Knowledge${completedMsrFiles.length > 0 ? ` (${completedMsrFiles.length})` : ''}`}
                          </button>
                        </div>
                      </div>
                      {completedMsrFiles.length > 0 && (
                        <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginTop:'0.75rem'}}>
                          {completedMsrFiles.map(file => (
                            <span key={`${file.name}-${file.size}`} className="tag" style={{borderColor:STATUS.info.border,color:STATUS.info.text}}>
                              {file.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    )}
                    {kbPeriodOptions.length > 0 && (
                      <div className="data-panel">
                        <div className="row-between" style={{marginBottom:'0.5rem'}}>
                          <div>
                            <div className="micro-label">Compare Saved Periods</div>
                            <div className="sub-note">Pick two or more saved periods to compare shared entry similarity across months.</div>
                          </div>
                          <button
                            onClick={() => runKbQuery('compare-period-similarity')}
                            disabled={kbQueryLoading || kbComparePeriods.length < 2}
                            className={`btn btn-action btn-kb-purple ${isKbQueryShowing('compare-period-similarity') ? 'is-active' : ''}`}
                            aria-pressed={isKbQueryShowing('compare-period-similarity')}
                            title="Compare shared entry similarity across the selected periods"
                          >
                            Compare Similarity
                          </button>
                        </div>
                        <div className="chip-row">
                          {kbComparePeriodChips.map(option => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => toggleKbComparePeriod(option.value)}
                              style={{
                                padding:'0.32rem 0.68rem',
                                borderRadius:'999px',
                                border: option.selected ? `1px solid ${STATUS.info.solid}` : `1px solid ${STATUS.neutral.borderStrong}`,
                                background: option.selected ? STATUS.info.bg : 'white',
                                color: option.selected ? STATUS.info.text : STATUS.neutral.text,
                                fontSize:'0.76rem',
                                fontWeight:700,
                                cursor:'pointer',
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <span className="micro-label">Operator Queries</span>
                    <div className="data-panel-note" style={{marginBottom:'0.5rem'}}>
                      Read-only. Each renders its result below.
                    </div>
                    <div className="tab-strip">
                      <button onClick={() => runKbQuery('verify')} disabled={kbQueryLoading} className={`btn btn-action btn-confirm ${isKbQueryShowing('verify') ? 'is-active' : ''}`} aria-pressed={isKbQueryShowing('verify')} title="Run an integrity verification on the knowledge base">
                        Verify Integrity
                      </button>
                      <button onClick={() => runKbQuery('roster-status')} disabled={kbQueryLoading || !canRunMonthScopedKbQueries} className={`btn btn-action btn-kb-blue ${isKbQueryShowing('roster-status') ? 'is-active' : ''}`} aria-pressed={isKbQueryShowing('roster-status')} title="Show roster submission status for the selected period">
                        Roster Status
                      </button>
                      <button onClick={() => runKbQuery('missing')} disabled={kbQueryLoading || !canRunMonthScopedKbQueries} className={`btn btn-action btn-kb-amber ${isKbQueryShowing('missing') ? 'is-active' : ''}`} aria-pressed={isKbQueryShowing('missing')} title="Find missing submissions for the selected period">
                        Missing Submissions
                      </button>
                      <button onClick={() => runKbQuery('repeat-content')} disabled={kbQueryLoading || !canRunMonthScopedKbQueries} className={`btn btn-action btn-confirm ${isKbQueryShowing('repeat-content') ? 'is-active' : ''}`} aria-pressed={isKbQueryShowing('repeat-content')} title="Find repeated content across submissions">
                        Repeat Content
                      </button>
                    </div>
                    {kbNotice && (
                      <div className="data-panel" style={{background:STATUS.success.bg,borderColor:STATUS.success.border,color:STATUS.success.text,fontSize:'0.8rem'}}>
                        {kbNotice.text}
                      </div>
                    )}

                    <div className="data-panel">
                      <div className="data-panel-head">
                        <span className="micro-label">Status</span>
                      </div>
                      {kbStatus ? (
                        <>
                          <div style={{display:'flex',gap:'1rem',flexWrap:'wrap',fontSize:'0.85rem',marginTop:'0.6rem'}}>
                            <span><strong>{kbStatus.monthCount}</strong> month(s)</span>
                            <span><strong>{kbStatus.uniquePidCount}</strong> unique entry/entries</span>
                            <span><strong>{kbStatus.uniqueSourceCount}</strong> unique source(s)</span>
                            {kbStatus.lastUpdated && <span><strong>Last updated:</strong> {new Date(kbStatus.lastUpdated).toLocaleString()}</span>}
                          </div>
                          {kbStatus.months && kbStatus.months.length > 0 && (
                            <div style={{marginTop:'0.6rem',display:'flex',flexWrap:'wrap',gap:'0.4rem'}}>
                              {kbStatus.months.map(m => (
                                <span key={m.period} className="tag">
                                  {m.period} · {m.entryCount} entries
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="data-panel-note">
                          {kbLoading ? 'Loading knowledge base status...' : 'Status not loaded.'}
                        </div>
                      )}
                    </div>

                    {kbQueryResult && (
                      <div className="data-panel">
                        <h3 className="panel-title">
                          Query Result: {{
                            'verify': 'Integrity Check',
                            'roster-status': 'Month Inventory',
                            'missing': 'Missing Submissions',
                            'repeat-content': 'Repeat Content',
                            'compare-period-similarity': 'Cross-Period Similarity',
                          }[kbQueryResult.type] || kbQueryResult.type}
                        </h3>
                        {kbQueryResult.type === 'verify' && (
                          <div style={{fontSize:'0.85rem'}}>
                            <div>Total checked: <strong>{kbQueryResult.data.totalChecked}</strong></div>
                            <div>Healthy: <strong style={{color:STATUS.success.solid}}>{kbQueryResult.data.healthy}</strong></div>
                            <div>Broken: <strong style={{color:STATUS.danger.solid}}>{kbQueryResult.data.broken}</strong></div>
                            {kbQueryResult.data.broken > 0 && (
                              <ul style={{marginTop:'0.5rem',fontSize:'0.78rem',color:STATUS.danger.text}}>
                                {(kbQueryResult.data.results || []).filter(r => !r.ok).map((r,i) => (
                                  <li key={i}>{r.key}: {r.error}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        {kbQueryResult.type === 'roster-status' && (
                          <div style={{fontSize:'0.85rem'}}>
                            <div className="stat-grid">
                              <div className="stat-tile">
                                <div className="micro-label">Period</div>
                                <div className="stat-value">{kbQueryResult.data.period}</div>
                              </div>
                              <div className="stat-tile">
                                <div className="micro-label">Input Files</div>
                                <div className="stat-value">{kbInputFileRows.length}</div>
                              </div>
                              <div className="stat-tile">
                                <div className="micro-label">
                                  {kbIsInputConfirmedRoster ? 'Confirmed Contributors' : 'Submitted Entries'}
                                </div>
                                <div className="stat-value">{kbSubmittedPidList.length}</div>
                              </div>
                              <div className="stat-tile">
                                <div className="micro-label">Roster Source</div>
                                <div className="stat-value" style={{textTransform:'capitalize'}}>{kbQueryResult.data.rosterSource || 'template'}</div>
                              </div>
                            </div>
                            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))',gap:'1rem',alignItems:'start'}}>
                              <div className="table-frame">
                                <div className="table-frame-head">
                                  <div style={{fontWeight:700,color:STATUS.neutral.textStrong}}>Month Input Files</div>
                                  <div className="sub-note">Every uploaded `.docx` for the selected month, with manifest coverage shown on the right.</div>
                                </div>
                                <div style={{maxHeight:'320px',overflowY:'auto'}}>
                                  {kbInputFileRows.length > 0 ? kbInputFileRows.map(row => (
                                    <div key={row.filename} className="table-frame-row">
                                      <div style={{minWidth:0}}>
                                        <div style={{fontWeight:600,color:STATUS.neutral.textStrong,wordBreak:'break-word'}}>{row.filename}</div>
                                      </div>
                                      <span style={{
                                        flexShrink:0,
                                        padding:'0.2rem 0.55rem',
                                        borderRadius:'999px',
                                        fontSize:'0.72rem',
                                        fontWeight:700,
                                        background: row.includedInManifest ? STATUS.success.bgStrong : STATUS.warning.bgStrong,
                                        color: row.includedInManifest ? STATUS.success.text : STATUS.warning.text,
                                      }}>
                                        {row.includedInManifest ? 'In manifest' : 'Uploaded only'}
                                      </span>
                                    </div>
                                  )) : (
                                    <div style={{padding:'1rem',fontSize:'0.82rem',color:STATUS.neutral.text}}>No input files found for this month.</div>
                                  )}
                                </div>
                              </div>
                              <div className="table-frame">
                                <div className="table-frame-head">
                                  <div style={{fontWeight:700,color:STATUS.neutral.textStrong}}>
                                    {kbIsInputConfirmedRoster ? 'Confirmed Contributor Inventory' : 'Submitted Entry Inventory'}
                                  </div>
                                  <div className="sub-note">
                                    {kbIsInputConfirmedRoster
                                      ? 'Contributor IDs confirmed directly from saved inputs, with the full month inventory available on demand.'
                                      : 'Entry count by default, with full month entry inventory available on demand.'}
                                  </div>
                                </div>
                                <div className="table-frame-body">
                                  {kbSubmittedPidList.length > 0 ? (
                                    <>
                                      <div className="row-between">
                                        <div style={{color:STATUS.neutral.textStrong}}>
                                          <strong style={{fontSize:'1rem',color:STATUS.neutral.textStrong}}>{kbSubmittedPidList.length}</strong>{' '}
                                          {kbIsInputConfirmedRoster ? 'contributor entries confirmed for this month.' : 'entries recorded for this month.'}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setKbShowPidInventory(show => !show)}
                                          className="btn btn-success-compact" style={{color:STATUS.info.text,borderColor:STATUS.info.border}}
                                        >
                                          {kbShowPidInventory ? 'Hide full entry list' : `View all entries (${kbSubmittedPidList.length})`}
                                        </button>
                                      </div>
                                      {kbShowPidInventory && (
                                        <>
                                          {/* Map raw manifest pid -> roster row so we can render the
                                              friendly "ID — Person" label instead of internal keys
                                              like "llm:input_doc-6-4.docx:sibyl matten". */}
                                          {(() => {
                                            const rowByPid = Object.fromEntries(
                                              (kbRosterRows || []).map(r => [r.pid || r.matchedPerson || r.name, r])
                                            );
                                            const formatEntryLabel = (pid) => {
                                              const row = rowByPid[pid];
                                              if (row) {
                                                const idPart = row.positionId || (
                                                  String(pid).startsWith('llm:') || String(pid).startsWith('file:') ? '' : pid
                                                );
                                                const personPart = row.name || row.matchedPerson || '';
                                                if (idPart && personPart) return `${idPart} — ${personPart}`;
                                                if (personPart) return personPart;
                                                if (idPart) return idPart;
                                              }
                                              return pid;
                                            };
                                            return (
                                              <div style={{display:'flex',flexWrap:'wrap',gap:'0.45rem',maxHeight:'260px',overflowY:'auto',marginTop:'0.8rem',paddingTop:'0.2rem'}}>
                                                {kbSubmittedPidList.map(pid => {
                                                  const isActive = kbSelectedPid === pid;
                                                  return (
                                                    <button
                                                      key={pid}
                                                      type="button"
                                                      title={pid}
                                                      onClick={() => loadKbPidDetail(pid)}
                                                      style={{
                                                        padding:'0.3rem 0.6rem',
                                                        borderRadius:'8px',
                                                        background:isActive ? STATUS.info.text : STATUS.info.bg,
                                                        color:isActive ? 'white' : STATUS.info.text,
                                                        border:isActive ? `1px solid ${STATUS.info.text}` : `1px solid ${STATUS.info.border}`,
                                                        fontFamily:'inherit',
                                                        fontSize:'0.77rem',
                                                        fontWeight:600,
                                                        cursor:'pointer',
                                                        boxShadow:isActive ? '0 4px 10px rgba(29, 78, 216, 0.22)' : 'none',
                                                      }}
                                                    >
                                                      {formatEntryLabel(pid)}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            );
                                          })()}
                                          <div style={{marginTop:'0.8rem',fontSize:'0.77rem',color:STATUS.neutral.text}}>
                                            Click an entry to open the saved submission text for that month.
                                          </div>
                                          {(kbPidDetailLoading || kbPidDetail) && (
                                            <div className="table-frame" style={{marginTop:'0.7rem',borderColor:STATUS.info.bgStrong,background:STATUS.info.bg}}>
                                              <div style={{padding:'0.8rem 1rem',borderBottom:`1px solid ${STATUS.info.bgStrong}`,display:'flex',justifyContent:'space-between',gap:'0.75rem',alignItems:'center',flexWrap:'wrap'}}>
                                                <div>
                                                  <div className="micro-label">Entry Submission Detail</div>
                                                  <div style={{fontSize:'0.98rem',fontWeight:700,color:STATUS.neutral.textStrong,marginTop:'0.1rem'}}>
                                                    {(() => {
                                                      if (kbPidDetailLoading) return `Loading...`;
                                                      const person = kbPidDetail?.personName || 'Unknown submitter';
                                                      // Look up the entry id (PWS / PID / Position) from the
                                                      // roster row that matches the selected key. Falls back
                                                      // to the raw key only when nothing else is available
                                                      // and the key isn't a synthetic prefix.
                                                      const row = (kbRosterRows || []).find(
                                                        r => (r.pid || r.matchedPerson || r.name) === kbSelectedPid,
                                                      );
                                                      const entryId = row?.positionId
                                                        || (kbSelectedPid && !/^(person:|llm:|file:)/i.test(String(kbSelectedPid))
                                                          ? kbSelectedPid : '');
                                                      return entryId ? `${entryId} · ${person}` : person;
                                                    })()}
                                                  </div>
                                                </div>
                                                {!kbPidDetailLoading && kbPidDetail?.found && (
                                                  <span className="tag" style={{background:STATUS.info.bgStrong,borderColor:STATUS.info.border,color:STATUS.info.text,fontWeight:700,textTransform:'capitalize'}}>
                                                    {kbPidDetail.contentSource === 'input' ? 'From input file' : 'From manifest'}
                                                  </span>
                                                )}
                                              </div>
                                              <div className="table-frame-body">
                                                {kbPidDetailLoading ? (
                                                  <div style={{fontSize:'0.84rem',color:STATUS.neutral.text}}>Loading the saved submission text for this entry...</div>
                                                ) : kbPidDetail?.found ? (
                                                  <>
                                                    <div className="stat-grid">
                                                      <div>
                                                        <div className="micro-label">Source file</div>
                                                        <div style={{fontSize:'0.84rem',color:STATUS.neutral.textStrong,marginTop:'0.15rem',wordBreak:'break-word'}}>{kbPidDetail.source || 'Unknown'}</div>
                                                      </div>
                                                      <div>
                                                        <div className="micro-label">Word count</div>
                                                        <div className="sub-note">{kbPidDetail.wordCount || 0}</div>
                                                      </div>
                                                      <div>
                                                        <div className="micro-label">Period</div>
                                                        <div className="sub-note">{kbPidDetail.period}</div>
                                                      </div>
                                                    </div>
                                                    <div style={{fontSize:'0.72rem',textTransform:'uppercase',letterSpacing:'0.04em',color:STATUS.neutral.text,fontWeight:700,marginBottom:'0.35rem'}}>Full saved submission text</div>
                                                    <div style={{
                                                      background:'white',
                                                      border:`1px solid ${STATUS.neutral.border}`,
                                                      borderRadius:'8px',
                                                      padding:'0.85rem 0.95rem',
                                                      color:STATUS.neutral.textStrong,
                                                      fontSize:'0.84rem',
                                                      lineHeight:1.65,
                                                      whiteSpace:'pre-wrap',
                                                      wordBreak:'break-word',
                                                      maxHeight:'320px',
                                                      overflowY:'auto',
                                                    }}>
                                                      {kbPidDetail.content || 'No saved content was found for this entry.'}
                                                    </div>
                                                  </>
                                                ) : (
                                                  <div style={{fontSize:'0.84rem',color:STATUS.warning.text}}>No saved detail was found for entry {kbSelectedPid} in {activeKnowledgePeriod || 'this saved period'}.</div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <div style={{fontSize:'0.82rem',color:STATUS.neutral.text}}>No entries recorded for this month yet.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {kbRosterRows.length > 0 && (
                              <div style={{marginTop:'1rem'}}>
                                <strong style={{display:'block',marginBottom:'0.35rem',color:STATUS.neutral.textStrong}}>Roster Coverage</strong>
                                {kbIsInputConfirmedRoster ? (
                                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:'0.75rem'}}>
                                    <div className="table-frame table-frame-body" style={{borderColor:STATUS.info.border}}>
                                      <div className="row-between" style={{marginBottom:'0.4rem'}}>
                                        <div>
                                          <div className="micro-label">Input-confirmed contributors</div>
                                          <div style={{fontSize:'1rem',fontWeight:700,color:STATUS.info.textStrong,marginTop:'0.1rem'}}>{kbSubmittedRosterRows.length}</div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setKbShowSubmittedRoster(show => !show)}
                                          className="btn btn-success-compact" style={{color:STATUS.info.text,borderColor:STATUS.info.border}}
                                        >
                                          {kbShowSubmittedRoster ? 'Hide confirmed names' : `View confirmed names (${kbSubmittedRosterRows.length})`}
                                        </button>
                                      </div>
                                      <div style={{fontSize:'0.78rem',color:STATUS.neutral.text,marginBottom:'0.55rem'}}>
                                        {kbRosterNote || 'No formal template roster was found, so this month inventory is based on contributors confirmed directly from the uploaded inputs.'}
                                      </div>
                                      {kbShowSubmittedRoster && (
                                        <div className="chip-row">
                                          {kbSubmittedRosterRows.map(row => (
                                            <span key={`${row.name}-${row.pid || row.source || 'confirmed'}`} className="tag" style={{background:STATUS.info.bgStrong,color:STATUS.info.text,borderColor:'transparent'}}>
                                              {row.name}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="stat-grid" style={{marginBottom:0}}>
                                    <div className="table-frame table-frame-body" style={{borderColor:STATUS.danger.bgStrong}}>
                                      <div className="row-between" style={{marginBottom:'0.4rem'}}>
                                        <div>
                                          <div style={{fontSize:'0.72rem',textTransform:'uppercase',letterSpacing:'0.04em',color:STATUS.danger.text,fontWeight:700}}>
                                            {kbCanDetermineMissing ? 'Missing from roster' : 'Missing availability'}
                                          </div>
                                          <div style={{fontSize:'1rem',fontWeight:700,color:STATUS.danger.textStrong,marginTop:'0.1rem'}}>
                                            {kbCanDetermineMissing ? kbMissingRosterRows.length : 'N/A'}
                                          </div>
                                        </div>
                                        <span className="tag" style={{background:STATUS.danger.bg,color:STATUS.danger.text,borderColor:'transparent',fontWeight:700}}>
                                          {kbCanDetermineMissing ? 'Needs follow-up' : 'Template roster required'}
                                        </span>
                                      </div>
                                      {kbCanDetermineMissing ? (
                                        kbMissingRosterRows.length > 0 ? (
                                          <div className="chip-row">
                                            {kbMissingRosterRows.map(row => (
                                              <span key={row.name} className="tag" style={{background:STATUS.danger.bg,color:STATUS.danger.text,borderColor:'transparent'}}>
                                                {row.name}
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          <div style={{fontSize:'0.78rem',color:STATUS.success.text}}>Everyone in the derived roster has a submission this month.</div>
                                        )
                                      ) : (
                                        <div style={{fontSize:'0.78rem',color:STATUS.neutral.text}}>
                                          {kbRosterNote || 'This month does not include a formal roster, so missing contributors cannot be determined automatically.'}
                                        </div>
                                      )}
                                    </div>

                                    <div className="table-frame table-frame-body" style={{borderColor:STATUS.success.bgStrong}}>
                                      <div className="row-between" style={{marginBottom:'0.4rem'}}>
                                        <div>
                                          <div style={{fontSize:'0.72rem',textTransform:'uppercase',letterSpacing:'0.04em',color:STATUS.success.text,fontWeight:700}}>Submitted roster names</div>
                                          <div style={{fontSize:'1rem',fontWeight:700,color:STATUS.success.textStrong,marginTop:'0.1rem'}}>{kbSubmittedRosterRows.length}</div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setKbShowSubmittedRoster(show => !show)}
                                          className="btn btn-success-compact" style={{color:STATUS.success.text,borderColor:STATUS.success.border}}
                                        >
                                          {kbShowSubmittedRoster ? 'Hide submitted names' : `View submitted names (${kbSubmittedRosterRows.length})`}
                                        </button>
                                      </div>
                                      {kbShowSubmittedRoster && (
                                        <div className="chip-row">
                                          {kbSubmittedRosterRows.map(row => (
                                            <span key={row.name} className="tag" style={{background:STATUS.success.bgStrong,color:STATUS.success.text,borderColor:'transparent'}}>
                                              {row.name}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {showRosterQueryDetails && kbQueryResult.type === 'roster-status' && (
                          <div style={{fontSize:'0.85rem'}}>
                            <div>Period: <strong>{kbQueryResult.data.period}</strong></div>
                            <div>Submitted entries: {kbQueryResult.data.submittedEntries?.length || 0}</div>
                            <div>Sources: {kbQueryResult.data.submittedSources?.length || 0}</div>
                            {(kbQueryResult.data.rosterRows || []).length > 0 && (
                              <div style={{marginTop:'0.5rem'}}>
                                <strong>Roster:</strong>
                                <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginTop:'0.3rem'}}>
                                  {kbQueryResult.data.rosterRows.map(r => (
                                    <span key={r.name} className="tag" style={{background:r.submitted?STATUS.success.bgStrong:STATUS.danger.bg,color:r.submitted?STATUS.success.text:STATUS.danger.text,borderColor:'transparent'}}>
                                      {r.submitted ? '✓' : '✗'} {r.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {kbQueryResult.type === 'missing' && (
                          <div style={{fontSize:'0.85rem'}}>
                            <div>Period: <strong>{kbQueryResult.data.period}</strong></div>
                            <div>Roster size: <strong>{kbQueryResult.data.rosterSize}</strong></div>
                            <div>
                              Missing:{' '}
                              <strong style={{color: kbQueryResult.data.canDetermineMissing === false ? STATUS.warning.text : STATUS.danger.solid}}>
                                {kbQueryResult.data.canDetermineMissing === false ? 'Not determinable' : kbQueryResult.data.missingCount}
                              </strong>
                            </div>
                            {kbQueryResult.data.note && (
                              <div style={{marginTop:'0.5rem',fontSize:'0.8rem',color:STATUS.warning.text}}>
                                {kbQueryResult.data.note}
                              </div>
                            )}
                            {kbQueryResult.data.canDetermineMissing !== false && (kbQueryResult.data.missing || []).length > 0 && (
                              <ul style={{marginTop:'0.5rem',color:STATUS.danger.text}}>
                                {kbQueryResult.data.missing.map(n => <li key={n}>{n}</li>)}
                              </ul>
                            )}
                          </div>
                        )}
                        {kbQueryResult.type === 'repeat-content' && (
                          <div style={{fontSize:'0.85rem'}}>
                            <div>Period: <strong>{kbQueryResult.data.period}</strong></div>
                            <div>Repeat count: <strong style={{color:STATUS.danger.solid}}>{kbQueryResult.data.count}</strong></div>
                            {(kbQueryResult.data.repeats || []).length > 0 && (
                              <table style={{width:'100%',marginTop:'0.5rem',borderCollapse:'collapse',fontSize:'0.78rem'}}>
                                <thead><tr style={{background:STATUS.neutral.surfaceAlt}}>
                                  <th style={{textAlign:'left',padding:'0.3rem 0.5rem'}}>Entry</th>
                                  <th style={{textAlign:'left',padding:'0.3rem 0.5rem'}}>Compared To</th>
                                  <th style={{textAlign:'right',padding:'0.3rem 0.5rem'}}>Similarity</th>
                                </tr></thead>
                                <tbody>
                                  {kbQueryResult.data.repeats.map((r,i) => (
                                    <tr key={i}><td style={{padding:'0.3rem 0.5rem',fontFamily:'monospace'}}>{r.pid}</td><td style={{padding:'0.3rem 0.5rem'}}>{r.comparedTo}</td><td style={{padding:'0.3rem 0.5rem',textAlign:'right',color:STATUS.danger.solid}}>{r.similarity}%</td></tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                        {kbQueryResult.type === 'compare-period-similarity' && (
                          <div style={{fontSize:'0.85rem'}}>
                            <div style={{marginBottom:'0.8rem',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'0.75rem',flexWrap:'wrap'}}>
                              <div style={{color:STATUS.neutral.textStrong}}>
                                Comparing <strong>{kbQueryResult.data.selectedPeriods?.length || 0}</strong> saved period(s):
                                <span style={{marginLeft:'0.35rem'}}>
                                  {(kbQueryResult.data.selectedPeriods || []).map(period => kbPeriodLabelByValue[period] || period).join(', ')}
                                </span>
                              </div>
                              <label style={{display:'flex',alignItems:'center',gap:'0.4rem',fontSize:'0.78rem',color:STATUS.neutral.text,cursor:'pointer',whiteSpace:'nowrap'}}>
                                <input type="checkbox" checked={kbHideTrivialOverlaps} onChange={(e) => setKbHideTrivialOverlaps(e.target.checked)} />
                                Hide rows where the same file is reused across both periods
                              </label>
                            </div>
                            {(kbQueryResult.data.comparisons || []).length > 0 ? (
                              <div style={{display:'grid',gap:'0.85rem'}}>
                                {kbQueryResult.data.comparisons.map(comparison => {
                                  const allOverlaps = comparison.overlaps || [];
                                  const visibleOverlaps = kbHideTrivialOverlaps
                                    ? allOverlaps.filter(item => !(item.similarity >= 100 && item.sourceA && item.sourceB && item.sourceA === item.sourceB))
                                    : allOverlaps;
                                  const hiddenCount = allOverlaps.length - visibleOverlaps.length;
                                  return (
                                  <div key={`${comparison.periodA}-${comparison.periodB}`} className="table-frame table-frame-body">
                                    <div style={{display:'flex',justifyContent:'space-between',gap:'0.75rem',alignItems:'center',flexWrap:'wrap',marginBottom:'0.65rem'}}>
                                      <div style={{minWidth:0,flex:'1 1 auto'}}>
                                        <div style={{fontWeight:700,color:STATUS.neutral.textStrong}}>{comparison.periodALabel} vs {comparison.periodBLabel}</div>
                                        <div className="sub-note">
                                          Shared entries: <strong>{comparison.sharedPidCount}</strong> · Shared people: <strong>{comparison.sharedPersonCount}</strong> · Average similarity: <strong>{comparison.averageSimilarity}%</strong>
                                          {hiddenCount > 0 && <span style={{marginLeft:'0.4rem',color:STATUS.neutral.textMuted}}>· {hiddenCount} same-file row(s) hidden</span>}
                                        </div>
                                      </div>
                                      <span className="tag" style={{background:comparison.highSimilarityCount > 0 ? STATUS.danger.bg : STATUS.success.bgStrong,color:comparison.highSimilarityCount > 0 ? STATUS.danger.text : STATUS.success.text,borderColor:'transparent',fontWeight:700,flexShrink:0}}>
                                        {comparison.highSimilarityCount > 0 ? `${comparison.highSimilarityCount} high-similarity matches` : 'No high-similarity matches'}
                                      </span>
                                    </div>
                                    {visibleOverlaps.length > 0 ? (
                                      <div style={{overflowX:'auto'}}>
                                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem',tableLayout:'fixed'}}>
                                          <colgroup>
                                            <col style={{width:'72px'}} />
                                            <col style={{width:'160px'}} />
                                            <col style={{width:'90px'}} />
                                            <col />
                                            <col />
                                          </colgroup>
                                          <thead>
                                            <tr style={{background:STATUS.neutral.surface}}>
                                              <th style={{textAlign:'left',padding:'0.35rem 0.45rem'}}>Entry</th>
                                              <th style={{textAlign:'left',padding:'0.35rem 0.45rem'}}>Person</th>
                                              <th style={{textAlign:'right',padding:'0.35rem 0.45rem'}}>Similarity</th>
                                              <th style={{textAlign:'left',padding:'0.35rem 0.45rem'}}>{comparison.periodALabel}</th>
                                              <th style={{textAlign:'left',padding:'0.35rem 0.45rem'}}>{comparison.periodBLabel}</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {visibleOverlaps.map(item => (
                                              <tr key={`${comparison.periodA}-${comparison.periodB}-${item.pid}`} style={{borderTop:`1px solid ${STATUS.neutral.surfaceAlt}`}}>
                                                <td style={{padding:'0.4rem 0.45rem',fontFamily:'monospace',wordBreak:'break-all'}}>{item.pid}</td>
                                                <td style={{padding:'0.4rem 0.45rem',wordBreak:'break-word'}}>{item.personName || 'Unknown'}</td>
                                                <td style={{padding:'0.4rem 0.45rem',textAlign:'right',fontWeight:700,color:item.similarity >= 80 ? STATUS.danger.text : STATUS.warning.text}}>{item.similarity}%</td>
                                                <td style={{padding:'0.4rem 0.45rem',color:STATUS.neutral.text,wordBreak:'break-all'}}>{item.sourceA || 'Saved manifest'}</td>
                                                <td style={{padding:'0.4rem 0.45rem',color:STATUS.neutral.text,wordBreak:'break-all'}}>{item.sourceB || 'Saved manifest'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div style={{fontSize:'0.8rem',color:STATUS.neutral.text}}>
                                        {allOverlaps.length > 0
                                          ? `All ${allOverlaps.length} overlap(s) are same-file reuse across both periods (hidden).`
                                          : 'No shared-content overlaps were detected between these periods.'}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{fontSize:'0.82rem',color:STATUS.neutral.text}}>No pairwise comparisons were available for the selected periods.</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* One-line hint, not a callout — it is standing advice rather
                        than a condition to act on, so it gets a rule and a line of
                        text instead of a filled block. */}
                    <div className="inline-hint">
                      <Info size={13} aria-hidden="true" />
                      <span>Set the roster on the Process tab first — it is reused for these queries via local storage.</span>
                    </div>

                  </div>
                </div>
              </div>
            ) : (
              <div className="welcome-card">
                <div className="welcome-icon" aria-hidden="true"><Lock size={52} /></div>
                <h2>Select a Project</h2>
              </div>
            )
          ) : (
            projectConfirmed ? (
              <Chatbot
                apiBaseUrl={API_ENDPOINT}
                chatUrl={`${API_ENDPOINT}/chat`}
                resyncUrl={resyncUrl}
                syncStatusUrl={syncStatusUrl}
                authenticatedFetch={authenticatedFetch}
                projectInfo={{
                  projectName,
                  year,
                  months: selectedMonths,
                  availableMonths: availableMonths,  // Pass actual available months
                  projectOptions: realProjectOptions,
                }}
              />
            ) : (
              <div className="welcome-card">
                <div className="welcome-icon" aria-hidden="true"><Lock size={52} /></div>
                <h2>Select a Project</h2>

              </div>
            )
          )}
        </div>
      </main>

      <footer className="App-footer">
        <div className="content-wrapper footer-content">
          <p className="footer-version">Version: V0.1.2</p>
        </div>
      </footer>
    </div>
  );
}

function OAuthApp({ config }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = getActiveAccount(instance, accounts);
  const user = buildUserProfile(activeAccount);

  useEffect(() => {
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  const authenticatedFetch = async (url, options = {}) => {
    const account = instance.getActiveAccount() || activeAccount;
    return fetchWithMicrosoftAuth(instance, account, url, options);
  };

  return (
    <AppShell
      config={config}
      auth={{
        authenticatedFetch,
        isAuthenticated,
        loginView: <Login authMode="oauth" />,
        onLogout: async () => {
          await instance.logoutRedirect({
            account: activeAccount || undefined,
            postLogoutRedirectUri: window.location.origin,
          });
        },
        user,
      }}
    />
  );
}

function PlaceholderApp({ config }) {
  const [user, setUser] = useState(() => readStoredPlaceholderAuth());

  return (
    <AppShell
      config={config}
      auth={{
        authenticatedFetch: (url, options = {}) => fetch(url, options),
        isAuthenticated: Boolean(user),
        loginView: (
          <Login
            authMode="placeholder"
            adminPassword={config.adminPassword}
            adminUsername={config.adminUsername}
            onLogin={setUser}
          />
        ),
        onLogout: async () => {
          setUser(null);
        },
        user,
      }}
    />
  );
}

function App({ config }) {
  return config.authMode === 'placeholder'
    ? <PlaceholderApp config={config} />
    : <OAuthApp config={config} />;
}

export default App;
